<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import AiCloserAPI from 'dashboard/api/aiCloser';
import getWootAxios from 'dashboard/helper/getWootAxios';
import CreateArticleSheet from './CreateArticleSheet.vue';
import ProductInformationTab from '../../../tabs/product/InformationTab.vue';
import ProductMediaTab from '../../../tabs/product/MediaTab.vue';
import ProductLinksTab from '../../../tabs/product/LinksTab.vue';
import ProductAnswerTemplateTab from '../../../tabs/product/AnswerTemplateTab.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['openLegacy', 'openPreview']);
const store = useStore();
const route = useRoute();
const router = useRouter();
const alert = useAlert();
const { accountId, accountScopedRoute } = useAccount();
const CONTENT_SEED_QUERY_KEYS = [
  'content_seed',
  'content_seed_source',
  'content_seed_focus',
  'content_seed_query',
  'content_seed_reason',
  'content_seed_scope_warning',
];
const TEST_COMPARE_QUERY_KEYS = [
  'test_compare_bundle',
  'test_compare_title',
  'test_compare_focus_label',
  'test_compare_strategy_group_title',
  'test_compare_recommendation_code',
  'test_compare_owner_workspace',
  'test_compare_owner_action_title',
  'test_compare_guidance_editor',
  'test_compare_escalation_focus',
  'test_compare_hotspot_summary',
  'test_compare_resolved_summary',
  'test_compare_escalated_summary',
  'test_compare_validation_summary',
  'test_compare_stage',
];

const searchQuery = ref('');
const isLoadingContentSources = ref(false);
const isArticleSheetOpen = ref(false);
const activeArticleType = ref('public');
const isSavingArticle = ref(false);
const savingArticleAction = ref('');
const isSavingContentOwnership = ref(false);
const savingRecommendationThresholdProfileKey = ref('');
const isLoadingRecommendations = ref(false);
const isCapturingRecommendationSnapshot = ref(false);
const recommendationsSectionRef = ref(null);
const retrievalStrategySectionRef = ref(null);
const gapRecommendations = ref([]);
const gapRecommendationsMeta = ref({});
const hasAttemptedRecommendationSnapshot = ref(false);
const activeRecommendationAlertKey = ref('');
const activeRecommendationWorkflowAuditKey = ref('');
const recommendationAlertWorkflowActivityHighlightFilter = ref('all');
const recommendationWorkflowAuditHighlightFilter = ref('all');
const creatingGapDraftTagId = ref(null);
const recommendationPriorityFilter = ref('all');
const recommendationWorkTypeFilter = ref('all');
const recommendationDraftStateFilter = ref('all');
const recommendationPresetFilter = ref('all');
const recommendationFamilyFilter = ref('all');
const recommendationFamilyConfidenceFilter = ref('all');
const recommendationReadinessFilter = ref('all');
const recommendationTrendFilter = ref('all');
const recommendationFreshnessFilter = ref('all');
const recommendationRoutingFilter = ref('all');
const recommendationSort = ref('priority');
const draftArticleScopeMode = ref('inbox_portal');
const draftArticlePortalId = ref('');
const draftMaxSources = ref('4');
const draftContextBudget = ref('standard');
const draftMinConfidenceScore = ref('0.35');
const draftSearchBalance = ref('balanced');
const draftCitationMode = ref('auto');
const draftSourcePriority = ref([]);
const draftNoEvidenceBehavior = ref('general_answer');
const draftInboxBindings = ref([]);
const draftSegmentBindings = ref([]);
const draftAttributeSegmentBindings = ref([]);
const productInformationRef = ref(null);
const productMediaRef = ref(null);
const productLinksRef = ref(null);
const productAnswerTemplatesRef = ref(null);
const recommendationThresholdOverrideDrafts = ref({});
const recommendationAlertWorkflowDrafts = ref({});
const recommendationAlertWorkflowBatchMetadataDraft = ref({
  applyOwnerLabel: false,
  ownerLabel: '',
  applyDueDate: false,
  dueDate: '',
  applyResolutionOutcome: false,
  resolutionOutcome: '',
  applyHandoffReason: false,
  handoffReason: '',
  applyNextCheckpoint: false,
  nextCheckpoint: '',
  applyNote: false,
  note: '',
});
const activeRecommendationAlertWorkflowBatchPresetId = ref('');
const activeRecommendationAlertWorkflowBatchComparisonPresetId = ref('');
const hasAcknowledgedRecommendationAlertWorkflowBatchRisk = ref(false);
const savingRecommendationAlertWorkflowKey = ref('');
const isApplyingRecommendationAlertWorkflowBatch = ref(false);
const selectedRecommendationAlertWorkflowKeys = ref([]);
const recommendationAlertWorkflowStatusFilter = ref('all');
const recommendationAlertWorkflowDueFilter = ref('all');
const recommendationAlertWorkflowFollowupFilter = ref('all');
const recommendationAlertWorkflowResultFilter = ref('all');
const recommendationAlertWorkflowSort = ref('');

const assistantId = computed(() => props.assistant?.id);
const isAIGamer = computed(
  () => props.assistant?.config?.wlab_sidebar_key === 'ai_gamer'
);
const aiCloserConfig = computed(() => store.getters['aiCloser/getConfig'] || {});

const KNOWLEDGE_SOURCE_LIMIT_OPTIONS = [
  { value: '2', label: 'Up to 2 sources' },
  { value: '4', label: 'Up to 4 sources' },
  { value: '6', label: 'Up to 6 sources' },
  { value: '8', label: 'Up to 8 sources' },
];

const CONTEXT_BUDGET_OPTIONS = [
  {
    value: 'light',
    label: 'Light',
    note: 'Keep context lean for faster, tighter answers.',
  },
  {
    value: 'standard',
    label: 'Standard',
    note: 'Balance answer quality and retrieval depth for most support questions.',
  },
  {
    value: 'deep',
    label: 'Deep',
    note: 'Allow more source context when answers need richer evidence.',
  },
];

const RETRIEVAL_CONFIDENCE_OPTIONS = [
  {
    value: '0.2',
    label: 'Open',
    note: 'Allow weaker but possibly useful article matches to stay in play.',
  },
  {
    value: '0.35',
    label: 'Balanced',
    note: 'Keep a moderate floor so obviously weak article matches are filtered out.',
  },
  {
    value: '0.55',
    label: 'Strict',
    note: 'Require stronger evidence before an article can be used in runtime context.',
  },
];

const SEARCH_BALANCE_OPTIONS = [
  {
    value: 'keyword_first',
    label: 'Keyword first',
    note: 'Prefer exact and lexical matches before semantic recall.',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    note: 'Blend keyword and semantic retrieval in a safer middle ground.',
  },
  {
    value: 'semantic_first',
    label: 'Semantic first',
    note: 'Pull semantically similar articles ahead of looser keyword matches.',
  },
];

const RETRIEVAL_CITATION_MODE_OPTIONS = [
  {
    value: 'auto',
    label: 'Cite when sources are used',
    note: 'Add citations when the answer uses synced links or uploaded references.',
  },
  {
    value: 'always',
    label: 'Always require citations',
    note: 'Force citation behavior whenever supported sources are used in the answer.',
  },
  {
    value: 'never',
    label: 'Do not cite',
    note: 'Keep answers citation-free even when linked sources are available.',
  },
];

const RETRIEVAL_SOURCE_OPTIONS = [
  {
    key: 'articles',
    label: 'Help Center articles',
    note: 'Published articles in the current portal scope.',
  },
  {
    key: 'snippets',
    label: 'Legacy snippets',
    note: 'Compatibility templates that still act as fallback knowledge.',
  },
  {
    key: 'links',
    label: 'Website sync',
    note: 'Official synced links and website-derived support references.',
  },
  {
    key: 'media',
    label: 'Uploaded documents',
    note: 'Media and uploaded reference assets that can still support answers.',
  },
];
const MAX_DETECTED_ATTRIBUTE_SEGMENT_BINDINGS = 4;
const DETECTED_ATTRIBUTE_MATCH_TYPE_OPTIONS = [
  {
    value: 'equals',
    label: 'Equals exact value',
    note: 'Only match when the detected attribute equals the configured value.',
  },
  {
    value: 'contains',
    label: 'Contains text',
    note: 'Match when the detected attribute contains the configured text.',
  },
  {
    value: 'starts_with',
    label: 'Starts with',
    note: 'Match when the detected attribute starts with the configured text.',
  },
  {
    value: 'ends_with',
    label: 'Ends with',
    note: 'Match when the detected attribute ends with the configured text.',
  },
  {
    value: 'present',
    label: 'Is present',
    note: 'Match any non-empty detected value for this attribute key.',
  },
];

const RETRIEVAL_SEGMENT_BINDING_OPTIONS = [
  {
    key: 'reply_language:en',
    segment_type: 'reply_language',
    match_value: 'en',
    label: 'English replies',
    note: 'Applies when runtime reply language resolves to English.',
  },
  {
    key: 'reply_language:ms',
    segment_type: 'reply_language',
    match_value: 'ms',
    label: 'Malay replies',
    note: 'Applies when runtime reply language resolves to Malay.',
  },
  {
    key: 'reply_language:th',
    segment_type: 'reply_language',
    match_value: 'th',
    label: 'Thai replies',
    note: 'Applies when runtime reply language resolves to Thai.',
  },
  {
    key: 'conversation_language:en',
    segment_type: 'conversation_language',
    match_value: 'en',
    label: 'English conversations',
    note: 'Applies when the live conversation language resolves to English before reply strategy runs.',
  },
  {
    key: 'conversation_language:ms',
    segment_type: 'conversation_language',
    match_value: 'ms',
    label: 'Malay conversations',
    note: 'Applies when the live conversation language resolves to Malay before reply strategy runs.',
  },
  {
    key: 'conversation_language:th',
    segment_type: 'conversation_language',
    match_value: 'th',
    label: 'Thai conversations',
    note: 'Applies when the live conversation language resolves to Thai before reply strategy runs.',
  },
  {
    key: 'vip_conversation:true',
    segment_type: 'vip_conversation',
    match_value: 'true',
    label: 'VIP conversations',
    note: 'Applies when the live conversation is marked or labeled as VIP.',
  },
];

const NO_EVIDENCE_BEHAVIOR_OPTIONS = [
  {
    value: 'general_answer',
    label: 'Attempt a general answer',
    note: 'Keep the AI running, but let it work without approved retrieval evidence.',
  },
  {
    value: 'say_unknown',
    label: `Say "I don't know" safely`,
    note: 'Return a safe no-evidence response instead of guessing.',
  },
  {
    value: 'escalate',
    label: 'Escalate to a human',
    note: 'Handoff when no configured evidence is available.',
  },
];

const cloneJson = value => JSON.parse(JSON.stringify(value || {}));

const articles = computed(() => store.getters['articles/allArticles'] || []);
const articlesMeta = computed(() => store.getters['articles/getMeta'] || {});
const answerTemplates = computed(
  () => store.getters['aiCloser/getAnswerTemplates'] || []
);
const answerTemplatesMeta = computed(
  () => store.getters['aiCloser/getAnswerTemplatesMeta'] || {}
);
const media = computed(() => store.getters['aiCloser/getMedia'] || []);
const mediaMeta = computed(() => store.getters['aiCloser/getMediaMeta'] || {});
const links = computed(() => store.getters['aiCloser/getLinks'] || []);
const linksMeta = computed(() => store.getters['aiCloser/getLinksMeta'] || {});
const products = computed(() => store.getters['aiCloser/getProducts'] || []);
const portals = computed(() => store.getters['portals/allPortals'] || []);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const conversationAttributeDefinitions = computed(
  () => store.getters['attributes/getConversationAttributes'] || []
);
const retrievalDetectedAttributePresetOptions = computed(() =>
  [...conversationAttributeDefinitions.value]
    .map(attribute => ({
      key: String(attribute?.attributeKey || '').trim(),
      label: String(
        attribute?.attributeDisplayName || attribute?.attributeKey || 'Attribute'
      ).trim(),
      description: String(attribute?.attributeDescription || '').trim(),
      values: Array.isArray(attribute?.attributeValues) ? attribute.attributeValues : [],
      displayType: String(attribute?.attributeDisplayType || '').trim(),
      regexCue: String(attribute?.regexCue || '').trim(),
    }))
    .filter(option => option.key)
    .sort((left, right) => left.label.localeCompare(right.label))
);
const retrievalDetectedAttributePresetByKey = computed(() =>
  retrievalDetectedAttributePresetOptions.value.reduce((acc, option) => {
    acc[option.key] = option;
    return acc;
  }, {})
);
const detectedAttributeDefinitionForKey = key =>
  retrievalDetectedAttributePresetByKey.value[
    normalizeDetectedAttributeKey(key)
  ] || null;
const currentUserId = computed(() => store.getters.getCurrentUserID);
const connectedInboxIds = computed(() =>
  (props.assistant?.inboxes || []).map(i => i.id || i)
);
const connectedInboxes = computed(() =>
  inboxes.value.filter(inbox => connectedInboxIds.value.includes(inbox.id))
);

const previewInbox = computed(() => {
  const connectedWebWidget = inboxes.value.find(
    i =>
      connectedInboxIds.value.includes(i.id) &&
      i.channel_type === 'Channel::WebWidget'
  );
  if (connectedWebWidget) return connectedWebWidget;
  return inboxes.value.find(i => i.channel_type === 'Channel::WebWidget');
});

const connectedPortalIds = computed(() => {
  return [...new Set(connectedInboxes.value.map(inbox => inbox.portal_id).filter(Boolean))];
});
const connectedPortals = computed(() =>
  portals.value.filter(portal => connectedPortalIds.value.includes(portal.id))
);
const savedKnowledgeRetrieval = computed(() => {
  const raw = aiCloserConfig.value?.prompt_sections?.knowledge_retrieval;
  return raw && typeof raw === 'object' ? raw : {};
});
const savedArticleScopeMode = computed(() => {
  const raw =
    props.assistant?.config?.wlab_article_scope_mode ||
    savedKnowledgeRetrieval.value?.article_scope_mode;
  return raw === 'selected_portal' ? 'selected_portal' : 'inbox_portal';
});
const configuredArticlePortalId = computed(() => {
  const raw =
    props.assistant?.config?.wlab_article_portal_id ||
    savedKnowledgeRetrieval.value?.article_portal_id;
  return raw ? Number(raw) : null;
});
const normalizeMaxSources = value => {
  const next = Number(value);
  if (!Number.isFinite(next) || next < 1) return 4;
  return Math.min(Math.max(Math.round(next), 1), 8);
};
const normalizeContextBudget = value =>
  ['light', 'standard', 'deep'].includes(String(value)) ? String(value) : 'standard';
const normalizeMinConfidenceScore = value => {
  const next = Number(value);
  if (!Number.isFinite(next)) return 0.35;
  return Math.min(Math.max(Number(next.toFixed(2)), 0), 1);
};
const normalizeSearchBalance = value =>
  ['keyword_first', 'balanced', 'semantic_first'].includes(String(value))
    ? String(value)
    : 'balanced';
const normalizeSourcePriority = value => {
  const allowed = RETRIEVAL_SOURCE_OPTIONS.map(option => option.key);
  const requested = Array.isArray(value) ? value : [];
  return [...new Set(requested.map(item => String(item || '').trim()).filter(Boolean))]
    .filter(item => allowed.includes(item))
    .concat(allowed)
    .filter((item, index, array) => array.indexOf(item) === index)
    .slice(0, allowed.length);
};
const normalizeRetrievalCitationMode = value =>
  ['auto', 'always', 'never'].includes(String(value)) ? String(value) : 'auto';
const normalizeNoEvidenceBehavior = value =>
  ['general_answer', 'say_unknown', 'escalate'].includes(String(value))
    ? String(value)
    : 'general_answer';
const normalizeInboxBindingScopeMode = value =>
  ['inherit', 'inbox_portal', 'selected_portal'].includes(String(value))
    ? String(value)
    : 'inherit';
const normalizeSegmentBindingScopeMode = value =>
  ['inherit', 'inbox_portal', 'selected_portal'].includes(String(value))
    ? String(value)
    : 'inherit';
const normalizeDetectedAttributeKey = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_:-]/g, '');
const normalizeDetectedAttributeMatchValue = value =>
  String(value || '').trim().toLowerCase();
const normalizeDetectedAttributeMatchType = value =>
  DETECTED_ATTRIBUTE_MATCH_TYPE_OPTIONS.find(
    option => option.value === String(value || '').trim()
  )?.value || 'equals';
const normalizeKnowledgeRetrievalInboxBindings = value =>
  (Array.isArray(value) ? value : [])
    .map(binding => {
      const inboxId = Number(binding?.inbox_id || binding?.inboxId || 0);
      const scopeMode = normalizeInboxBindingScopeMode(
        binding?.article_scope_mode || binding?.articleScopeMode
      );
      const portalId = Number(
        binding?.article_portal_id || binding?.articlePortalId || 0
      );
      if (!Number.isFinite(inboxId) || inboxId <= 0) return null;
      if (scopeMode === 'inherit') {
        return {
          inbox_id: inboxId,
          article_scope_mode: 'inherit',
          article_portal_id: null,
        };
      }
      return {
        inbox_id: inboxId,
        article_scope_mode: scopeMode,
        article_portal_id:
          scopeMode === 'selected_portal' && Number.isFinite(portalId) && portalId > 0
            ? portalId
            : null,
      };
    })
    .filter(Boolean)
    .filter(
      (binding, index, rows) =>
        rows.findIndex(row => row.inbox_id === binding.inbox_id) === index
    )
    .sort((a, b) => a.inbox_id - b.inbox_id);
const normalizeKnowledgeRetrievalSegmentBindings = value =>
  (Array.isArray(value) ? value : [])
    .map(binding => {
      const segmentType = String(binding?.segment_type || binding?.segmentType || '').trim();
      const matchValue = String(binding?.match_value || binding?.matchValue || '')
        .trim()
        .toLowerCase();
      const attributeKey = normalizeDetectedAttributeKey(
        binding?.attribute_key || binding?.attributeKey
      );
      const matchType = normalizeDetectedAttributeMatchType(
        binding?.match_type || binding?.matchType
      );
      const scopeMode = normalizeSegmentBindingScopeMode(
        binding?.article_scope_mode || binding?.articleScopeMode
      );
      const portalId = Number(
        binding?.article_portal_id || binding?.articlePortalId || 0
      );
      const allowed =
        segmentType === 'vip_conversation'
          ? RETRIEVAL_SEGMENT_BINDING_OPTIONS.find(
              option => option.segment_type === 'vip_conversation'
            )
          : RETRIEVAL_SEGMENT_BINDING_OPTIONS.find(
              option =>
                option.segment_type === segmentType &&
                option.match_value === matchValue
            );
      if (segmentType === 'detected_attribute') {
        if (!attributeKey || (matchType !== 'present' && !matchValue)) return null;
        if (scopeMode === 'inherit') {
          return {
            segment_type: 'detected_attribute',
            attribute_key: attributeKey,
            match_type: matchType,
            match_value: matchType === 'present' ? null : matchValue,
            article_scope_mode: 'inherit',
            article_portal_id: null,
          };
        }
        return {
          segment_type: 'detected_attribute',
          attribute_key: attributeKey,
          match_type: matchType,
          match_value: matchType === 'present' ? null : matchValue,
          article_scope_mode: scopeMode,
          article_portal_id:
            scopeMode === 'selected_portal' && Number.isFinite(portalId) && portalId > 0
              ? portalId
              : null,
        };
      }
      if (!allowed) return null;
      if (scopeMode === 'inherit') {
        return {
          segment_type: allowed.segment_type,
          attribute_key: null,
          match_type: null,
          match_value: allowed.match_value,
          article_scope_mode: 'inherit',
          article_portal_id: null,
        };
      }
      return {
        segment_type: allowed.segment_type,
        attribute_key: null,
        match_type: null,
        match_value: allowed.match_value,
        article_scope_mode: scopeMode,
        article_portal_id:
          scopeMode === 'selected_portal' && Number.isFinite(portalId) && portalId > 0
            ? portalId
            : null,
      };
    })
    .filter(Boolean)
    .filter(
      (binding, index, rows) =>
        rows.findIndex(
          row =>
            row.segment_type === binding.segment_type &&
            String(row.attribute_key || '') === String(binding.attribute_key || '') &&
            String(row.match_type || '') === String(binding.match_type || '') &&
            row.match_value === binding.match_value
        ) === index
    );
const contextBudgetLabel = value =>
  CONTEXT_BUDGET_OPTIONS.find(option => option.value === normalizeContextBudget(value))
    ?.label || 'Standard';
const minConfidenceLabel = value =>
  RETRIEVAL_CONFIDENCE_OPTIONS.find(
    option => Number(option.value) === normalizeMinConfidenceScore(value)
  )?.label || 'Balanced';
const searchBalanceLabel = value =>
  SEARCH_BALANCE_OPTIONS.find(option => option.value === normalizeSearchBalance(value))
    ?.label || 'Balanced';
const sourcePriorityLabel = value =>
  RETRIEVAL_SOURCE_OPTIONS.find(option => option.key === String(value))?.label || String(value);
const retrievalCitationModeLabel = value =>
  RETRIEVAL_CITATION_MODE_OPTIONS.find(
    option => option.value === normalizeRetrievalCitationMode(value)
  )?.label || 'Cite when sources are used';
const noEvidenceBehaviorLabel = value =>
  NO_EVIDENCE_BEHAVIOR_OPTIONS.find(option => option.value === normalizeNoEvidenceBehavior(value))
    ?.label || 'Attempt a general answer';
const savedMaxSources = computed(() =>
  normalizeMaxSources(savedKnowledgeRetrieval.value?.max_sources)
);
const savedContextBudget = computed(() =>
  normalizeContextBudget(savedKnowledgeRetrieval.value?.context_budget)
);
const savedMinConfidenceScore = computed(() =>
  normalizeMinConfidenceScore(savedKnowledgeRetrieval.value?.min_confidence_score)
);
const savedSearchBalance = computed(() =>
  normalizeSearchBalance(savedKnowledgeRetrieval.value?.search_balance)
);
const savedCitationMode = computed(() =>
  normalizeRetrievalCitationMode(savedKnowledgeRetrieval.value?.citation_mode)
);
const savedSourcePriority = computed(() =>
  normalizeSourcePriority(savedKnowledgeRetrieval.value?.source_priority)
);
const savedNoEvidenceBehavior = computed(() =>
  normalizeNoEvidenceBehavior(savedKnowledgeRetrieval.value?.no_evidence_behavior)
);
const savedConnectedInboxBindings = computed(() =>
  normalizeKnowledgeRetrievalInboxBindings(
    savedKnowledgeRetrieval.value?.inbox_bindings
  ).filter(binding => connectedInboxIds.value.includes(binding.inbox_id))
);
const savedDisconnectedInboxBindings = computed(() =>
  normalizeKnowledgeRetrievalInboxBindings(
    savedKnowledgeRetrieval.value?.inbox_bindings
  ).filter(binding => !connectedInboxIds.value.includes(binding.inbox_id))
);
const savedSegmentBindings = computed(() =>
  normalizeKnowledgeRetrievalSegmentBindings(
    savedKnowledgeRetrieval.value?.segment_bindings
  )
);
const savedFixedSegmentBindings = computed(() =>
  savedSegmentBindings.value.filter(
    binding => binding.segment_type !== 'detected_attribute'
  )
);
const savedDetectedAttributeBindings = computed(() =>
  savedSegmentBindings.value.filter(
    binding => binding.segment_type === 'detected_attribute'
  )
);
const configuredArticlePortal = computed(() => {
  if (!configuredArticlePortalId.value) return null;
  return (
    connectedPortals.value.find(
      portal => portal.id === configuredArticlePortalId.value
    ) || null
  );
});
const draftConfiguredArticlePortalId = computed(() => {
  const raw = draftArticlePortalId.value;
  return raw ? Number(raw) : null;
});
const articleScopePortalOptions = computed(() => connectedPortals.value);
const buildDraftInboxBindingRows = bindings =>
  connectedInboxes.value.map(inbox => {
    const savedBinding = normalizeKnowledgeRetrievalInboxBindings(bindings).find(
      binding => binding.inbox_id === Number(inbox.id)
    );
    return {
      inbox_id: Number(inbox.id),
      article_scope_mode: savedBinding?.article_scope_mode || 'inherit',
      article_portal_id: savedBinding?.article_portal_id
        ? String(savedBinding.article_portal_id)
        : '',
    };
  });
const buildDraftSegmentBindingRows = bindings =>
  RETRIEVAL_SEGMENT_BINDING_OPTIONS.map(option => {
    const savedBinding = normalizeKnowledgeRetrievalSegmentBindings(bindings).find(
      binding =>
        binding.segment_type === option.segment_type &&
        binding.match_value === option.match_value
    );
    return {
      segment_type: option.segment_type,
      match_value: option.match_value,
      article_scope_mode: savedBinding?.article_scope_mode || 'inherit',
      article_portal_id: savedBinding?.article_portal_id
        ? String(savedBinding.article_portal_id)
        : '',
      label: option.label,
      note: option.note,
      key: option.key,
    };
  });
const buildDraftDetectedAttributeBindingRows = bindings =>
  (Array.isArray(bindings) ? bindings : [])
    .filter(binding => {
      const segmentType = String(
        binding?.segment_type || binding?.segmentType || 'detected_attribute'
      ).trim();
      return !segmentType || segmentType === 'detected_attribute';
    })
    .slice(0, MAX_DETECTED_ATTRIBUTE_SEGMENT_BINDINGS)
    .map((binding, index) => ({
      key:
        binding?.key ||
        `detected_attribute:${binding?.attribute_key || binding?.attributeKey || ''}:${binding?.match_type || binding?.matchType || 'equals'}:${binding?.match_value || binding?.matchValue || ''}:${index}`,
      segment_type: 'detected_attribute',
      attribute_key: binding?.attribute_key || binding?.attributeKey || '',
      match_type: normalizeDetectedAttributeMatchType(
        binding?.match_type || binding?.matchType
      ),
      match_value: binding?.match_value || binding?.matchValue || '',
      article_scope_mode:
        binding?.article_scope_mode || binding?.articleScopeMode || 'inherit',
      article_portal_id:
        binding?.article_portal_id || binding?.articlePortalId
          ? String(binding?.article_portal_id || binding?.articlePortalId)
        : '',
      label: (binding?.attribute_key || binding?.attributeKey)
        ? `Detected attribute: ${binding?.attribute_key || binding?.attributeKey}`
        : 'Detected attribute override',
      note: 'Applies when a detected conversation attribute matches this rule before retrieval runs.',
    }));
const normalizedDraftInboxBindings = computed(() =>
  normalizeKnowledgeRetrievalInboxBindings(draftInboxBindings.value)
);
const persistedDraftInboxBindings = computed(() =>
  normalizedDraftInboxBindings.value
    .filter(binding => binding.article_scope_mode !== 'inherit')
    .map(binding => ({
      inbox_id: binding.inbox_id,
      article_scope_mode: binding.article_scope_mode,
      article_portal_id:
        binding.article_scope_mode === 'selected_portal'
          ? binding.article_portal_id
          : null,
    }))
);
const inboxBindingRows = computed(() =>
  buildDraftInboxBindingRows(draftInboxBindings.value).map(row => {
    const inbox = connectedInboxes.value.find(item => Number(item.id) === row.inbox_id) || {};
    const portal = articleScopePortalOptions.value.find(
      item => Number(item.id) === Number(row.article_portal_id || 0)
    );
    return {
      ...row,
      name: inbox.name || `Inbox ${row.inbox_id}`,
      channelType: (inbox.channel_type || 'channel').replace('Channel::', ''),
      inboxPortalName:
        portals.value.find(item => item.id === Number(inbox.portal_id || 0))?.name || '',
      selectedPortalName: portal?.name || '',
    };
  })
);
const channelBindingSelectionValid = computed(() =>
  inboxBindingRows.value.every(row => {
    if (row.article_scope_mode !== 'selected_portal') return true;
    return Boolean(Number(row.article_portal_id || 0));
  })
);
const normalizedDraftSegmentBindings = computed(() =>
  normalizeKnowledgeRetrievalSegmentBindings(draftSegmentBindings.value)
);
const normalizedDraftDetectedAttributeBindings = computed(() =>
  normalizeKnowledgeRetrievalSegmentBindings(draftAttributeSegmentBindings.value)
);
const persistedDraftSegmentBindings = computed(() =>
  [
    ...normalizedDraftSegmentBindings.value,
    ...normalizedDraftDetectedAttributeBindings.value,
  ].filter(binding => binding.article_scope_mode !== 'inherit')
);
const segmentBindingRows = computed(() =>
  buildDraftSegmentBindingRows(draftSegmentBindings.value).map(row => {
    const portal = articleScopePortalOptions.value.find(
      item => Number(item.id) === Number(row.article_portal_id || 0)
    );
    return {
      ...row,
      selectedPortalName: portal?.name || '',
    };
  })
);
const detectedAttributeBindingRows = computed(() =>
  buildDraftDetectedAttributeBindingRows(draftAttributeSegmentBindings.value).map(row => {
    const portal = articleScopePortalOptions.value.find(
      item => Number(item.id) === Number(row.article_portal_id || 0)
    );
    const detectedAttributeDefinition = detectedAttributeDefinitionForKey(row.attribute_key);
    return {
      ...row,
      selectedPortalName: portal?.name || '',
      detectedAttributeDefinition,
      usesPreset: Boolean(detectedAttributeDefinition),
      presetSelectValue: detectedAttributeDefinition
        ? detectedAttributeDefinition.key
        : row.attribute_key
          ? '__custom__'
          : '',
    };
  })
);
const segmentBindingSelectionValid = computed(() =>
  segmentBindingRows.value.every(row => {
    if (row.article_scope_mode !== 'selected_portal') return true;
    return Boolean(Number(row.article_portal_id || 0));
  }) &&
  detectedAttributeBindingRows.value.every(row => {
    const hasAnyValue =
      Boolean(String(row.attribute_key || '').trim()) ||
      normalizeDetectedAttributeMatchType(row.match_type) !== 'equals' ||
      Boolean(String(row.match_value || '').trim()) ||
      row.article_scope_mode !== 'inherit' ||
      Boolean(Number(row.article_portal_id || 0));
    if (!hasAnyValue) return true;
    const hasMatch =
      Boolean(normalizeDetectedAttributeKey(row.attribute_key)) &&
      (normalizeDetectedAttributeMatchType(row.match_type) === 'present' ||
        Boolean(normalizeDetectedAttributeMatchValue(row.match_value)));
    if (row.article_scope_mode === 'inherit') return hasMatch;
    return hasMatch && (
      row.article_scope_mode !== 'selected_portal' ||
      Boolean(Number(row.article_portal_id || 0))
    );
  })
);
const updateInboxBindingRow = (inboxId, patch) => {
  draftInboxBindings.value = buildDraftInboxBindingRows(draftInboxBindings.value).map(row =>
    row.inbox_id === Number(inboxId)
      ? {
          ...row,
          ...patch,
        }
      : row
  );
};
const updateSegmentBindingRow = (key, patch) => {
  draftSegmentBindings.value = buildDraftSegmentBindingRows(draftSegmentBindings.value).map(
    row =>
      row.key === key
        ? {
            ...row,
            ...patch,
          }
        : row
  );
};
const addDetectedAttributeBindingRow = () => {
  if (
    buildDraftDetectedAttributeBindingRows(draftAttributeSegmentBindings.value)
      .length >= MAX_DETECTED_ATTRIBUTE_SEGMENT_BINDINGS
  ) {
    return;
  }
  draftAttributeSegmentBindings.value = [
    ...buildDraftDetectedAttributeBindingRows(draftAttributeSegmentBindings.value),
    {
      key: `detected_attribute:new:${Date.now()}`,
      segment_type: 'detected_attribute',
      attribute_key: '',
      match_type: 'equals',
      match_value: '',
      article_scope_mode: 'inherit',
      article_portal_id: '',
      label: 'Detected attribute override',
      note: 'Applies when a detected conversation attribute matches this rule before retrieval runs.',
    },
  ];
};
const updateDetectedAttributeBindingRow = (key, patch) => {
  draftAttributeSegmentBindings.value =
    buildDraftDetectedAttributeBindingRows(draftAttributeSegmentBindings.value).map(row =>
      row.key === key
        ? {
            ...row,
            ...patch,
          }
        : row
    );
};
const updateDetectedAttributePresetSelection = (row, nextValue) => {
  if (!nextValue) {
    updateDetectedAttributeBindingRow(row.key, {
      attribute_key: '',
      match_value: '',
    });
    return;
  }
  if (nextValue === '__custom__') {
    updateDetectedAttributeBindingRow(row.key, {
      attribute_key: row.usesPreset ? '' : row.attribute_key,
    });
    return;
  }
  updateDetectedAttributeBindingRow(row.key, {
    attribute_key: nextValue,
    match_value:
      row.detectedAttributeDefinition?.key === nextValue ? row.match_value : '',
  });
};
const removeDetectedAttributeBindingRow = key => {
  draftAttributeSegmentBindings.value =
    buildDraftDetectedAttributeBindingRows(draftAttributeSegmentBindings.value).filter(
      row => row.key !== key
    );
};
const previewPortal = computed(() => {
  if (!previewInbox.value?.portal_id) return null;
  return portals.value.find(portal => portal.id === previewInbox.value.portal_id) || null;
});
const defaultPortal = computed(() => {
  if (
    savedArticleScopeMode.value === 'selected_portal' &&
    configuredArticlePortal.value
  ) {
    return configuredArticlePortal.value;
  }
  if (previewPortal.value) return previewPortal.value;
  if (connectedPortalIds.value.length === 1) {
    return (
      portals.value.find(portal => portal.id === connectedPortalIds.value[0]) ||
      null
    );
  }
  if (connectedPortalIds.value.length > 1) return null;
  return portals.value[0] || null;
});
const defaultPortalSlug = computed(() => defaultPortal.value?.slug || '');
const defaultPortalLocale = computed(
  () => defaultPortal.value?.meta?.default_locale || 'en'
);

const articleCount = computed(
  () =>
    defaultPortal.value
      ? Number(
          articlesMeta.value?.publishedArticlesCount ||
            articlesMeta.value?.articlesCount ||
            articles.value.length ||
            0
        )
      : 0
);
const snippetCount = computed(
  () =>
    Number(
      answerTemplatesMeta.value?.total || answerTemplates.value.length || 0
    )
);
const documentCount = computed(
  () => Number(mediaMeta.value?.total || media.value.length || 0)
);
const websiteSyncCount = computed(
  () => Number(linksMeta.value?.total || links.value.length || 0)
);
const productCount = computed(() => Number(products.value.length || 0));
const articleRuntimeScopeMode = computed(() => {
  if (
    savedArticleScopeMode.value === 'selected_portal' &&
    configuredArticlePortal.value
  ) {
    return 'selected_portal';
  }
  if (previewPortal.value) return 'preview_portal';
  if (connectedPortalIds.value.length === 1) return 'single_connected_portal';
  if (connectedPortalIds.value.length > 1) return 'multi_connected_portals';
  return 'fallback_account_portal';
});
const articleRuntimeEnabled = computed(() =>
  ['selected_portal', 'preview_portal', 'single_connected_portal'].includes(
    articleRuntimeScopeMode.value
  )
    ? articleCount.value > 0
    : articleRuntimeScopeMode.value === 'multi_connected_portals'
);
const articleStatusText = computed(() => {
  if (articleRuntimeScopeMode.value === 'multi_connected_portals') {
    return 'Live by inbox';
  }
  if (articleRuntimeEnabled.value) return `${articleCount.value} Live`;
  if (
    savedArticleScopeMode.value === 'selected_portal' &&
    !configuredArticlePortal.value
  ) {
    return 'Select a portal';
  }
  return `${articleCount.value} Stored`;
});
const selectedArticlePortalName = computed(() => {
  const portal = connectedPortals.value.find(
    item => String(item.id) === String(configuredArticlePortalId.value)
  );
  return portal?.name || '';
});

const runtimeOwnershipNote = computed(() => {
  if (articleRuntimeScopeMode.value === 'selected_portal') {
    return 'AI now uses published Help Center articles from the pinned portal, plus the assistant runtime knowledge sources below.';
  }

  if (articleRuntimeScopeMode.value === 'multi_connected_portals') {
    return 'AI now uses published Help Center articles, but article scope is resolved by the current inbox portal at runtime because this assistant is connected to multiple portals.';
  }

  if (isAIGamer.value) {
    return 'AI currently retrieves published Help Center articles from the assistant-scoped portal, plus legacy snippets, Website sync, and Uploaded documents.';
  }

  return 'AI currently retrieves published Help Center articles from the assistant-scoped portal, plus legacy snippets and Uploaded documents. Website sync links are also used when configured.';
});

const previewSurfaceLabel = computed(() => {
  if (previewInbox.value?.name) return previewInbox.value.name;
  if (defaultPortal.value?.name) return defaultPortal.value.name;
  return 'Static preview';
});

const previewSurfaceNote = computed(() => {
  if (previewInbox.value) {
    return 'Customer preview follows the connected web widget surface.';
  }
  if (defaultPortal.value) {
    return 'Customer preview follows the connected Help Center portal.';
  }
  return 'Connect a widget or portal to make the preview more realistic.';
});

const contentSummaryCards = computed(() => [
  {
    label: 'Live articles',
    value: articleCount.value,
    note: articleStatusText.value,
  },
  {
    label: 'Snippets',
    value: snippetCount.value,
    note: 'Short reusable answers available to the assistant.',
  },
  {
    label: 'Uploaded docs',
    value: documentCount.value,
    note: 'Documents the assistant can draw from at runtime.',
  },
  {
    label: 'Preview surface',
    value: previewSurfaceLabel.value,
    note: previewSurfaceNote.value,
  },
]);

const syncArticleScopeDraft = () => {
  draftArticleScopeMode.value = savedArticleScopeMode.value;
  draftArticlePortalId.value = configuredArticlePortalId.value
    ? String(configuredArticlePortalId.value)
    : '';
  draftMaxSources.value = String(savedMaxSources.value);
  draftContextBudget.value = savedContextBudget.value;
  draftMinConfidenceScore.value = String(savedMinConfidenceScore.value);
  draftSearchBalance.value = savedSearchBalance.value;
  draftCitationMode.value = savedCitationMode.value;
  draftSourcePriority.value = [...savedSourcePriority.value];
  draftNoEvidenceBehavior.value = savedNoEvidenceBehavior.value;
  draftInboxBindings.value = buildDraftInboxBindingRows(savedConnectedInboxBindings.value);
  draftSegmentBindings.value = buildDraftSegmentBindingRows(savedFixedSegmentBindings.value);
  draftAttributeSegmentBindings.value = buildDraftDetectedAttributeBindingRows(
    savedDetectedAttributeBindings.value
  );
};

const moveSourcePriorityItem = (key, direction) => {
  const current = [...normalizeSourcePriority(draftSourcePriority.value)];
  const index = current.indexOf(key);
  if (index < 0) return;
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= current.length) return;
  [current[index], current[nextIndex]] = [current[nextIndex], current[index]];
  draftSourcePriority.value = current;
};

const draftPersistedArticlePortalId = computed(() =>
  draftArticleScopeMode.value === 'selected_portal'
    ? draftConfiguredArticlePortalId.value
    : null
);
const draftConfiguredArticlePortal = computed(() => {
  if (!draftConfiguredArticlePortalId.value) return null;
  return (
    articleScopePortalOptions.value.find(
      portal => portal.id === draftConfiguredArticlePortalId.value
    ) || null
  );
});
const savedPersistedArticlePortalId = computed(() =>
  savedArticleScopeMode.value === 'selected_portal'
    ? configuredArticlePortalId.value
    : null
);

const articleScopeSelectionValid = computed(
  () =>
    (draftArticleScopeMode.value !== 'selected_portal' ||
      Boolean(draftPersistedArticlePortalId.value)) &&
    channelBindingSelectionValid.value &&
    segmentBindingSelectionValid.value
);

const articleScopeDirty = computed(
  () =>
    draftArticleScopeMode.value !== savedArticleScopeMode.value ||
    Number(draftPersistedArticlePortalId.value || 0) !==
      Number(savedPersistedArticlePortalId.value || 0)
);
const retrievalTuningDirty = computed(
  () =>
    normalizeMaxSources(draftMaxSources.value) !== savedMaxSources.value ||
    normalizeContextBudget(draftContextBudget.value) !== savedContextBudget.value ||
    normalizeMinConfidenceScore(draftMinConfidenceScore.value) !==
      savedMinConfidenceScore.value ||
    normalizeSearchBalance(draftSearchBalance.value) !== savedSearchBalance.value ||
    normalizeRetrievalCitationMode(draftCitationMode.value) !== savedCitationMode.value ||
    JSON.stringify(normalizeSourcePriority(draftSourcePriority.value)) !==
      JSON.stringify(savedSourcePriority.value) ||
    normalizeNoEvidenceBehavior(draftNoEvidenceBehavior.value) !==
      savedNoEvidenceBehavior.value ||
    JSON.stringify(persistedDraftInboxBindings.value) !==
      JSON.stringify(savedConnectedInboxBindings.value) ||
    JSON.stringify(persistedDraftSegmentBindings.value) !==
      JSON.stringify(savedSegmentBindings.value)
);
const knowledgeRetrievalDirty = computed(
  () => articleScopeDirty.value || retrievalTuningDirty.value
);

const articleScopeStatusNote = computed(() => {
  if (
    draftArticleScopeMode.value === 'selected_portal' &&
    !draftPersistedArticlePortalId.value
  ) {
    return 'Choose one connected Help Center portal before saving.';
  }

  if (!channelBindingSelectionValid.value) {
    return 'Every channel override that pins a portal needs one connected Help Center portal selected.';
  }

  if (!segmentBindingSelectionValid.value) {
    return 'Every segment override that pins a portal needs one connected Help Center portal selected.';
  }

  if (knowledgeRetrievalDirty.value) {
    return 'Top Save will persist the retrieval strategy for this page.';
  }

  return 'Knowledge retrieval strategy is already in sync with the saved AI config.';
});

const assistantWideKnowledgeSourceLabels = computed(() => {
  const labels = [];
  if (snippetCount.value > 0) labels.push('Legacy snippets');
  if (websiteSyncCount.value > 0) labels.push('Website sync');
  if (documentCount.value > 0) labels.push('Uploaded documents');
  return labels;
});

const knowledgeRetrievalCards = computed(() => [
  {
    label: 'Help Center scope',
    value:
      draftArticleScopeMode.value === 'selected_portal'
        ? 'Pinned portal'
        : 'Inbox-aware',
    note:
      draftArticleScopeMode.value === 'selected_portal'
        ? draftConfiguredArticlePortal.value?.name ||
          'Choose one connected portal before saving.'
        : connectedInboxes.value.length > 1
          ? 'Article scope follows the inbox portal for each channel.'
          : 'Article scope follows the connected inbox portal.',
  },
  {
    label: 'Assistant-wide sources',
    value: assistantWideKnowledgeSourceLabels.value.length,
    note: assistantWideKnowledgeSourceLabels.value.length
      ? assistantWideKnowledgeSourceLabels.value.join(', ')
      : 'No snippets, website sync, or uploaded documents are live yet.',
  },
  {
    label: 'Connected channels',
    value: connectedInboxes.value.length,
    note:
      connectedInboxes.value.length > 1
        ? 'Multiple inboxes are connected, so runtime article scope can differ by channel.'
        : connectedInboxes.value.length === 1
          ? 'One inbox currently defines the article portal context.'
          : 'Connect an inbox to make runtime scope follow channel context.',
  },
  {
    label: 'Sources per answer',
    value: normalizeMaxSources(draftMaxSources.value),
    note: 'Limit how many Help Center sources can be packed into each runtime answer.',
  },
  {
    label: 'Confidence floor',
    value: minConfidenceLabel(draftMinConfidenceScore.value),
    note: 'Filter out weaker article matches before they enter runtime context.',
  },
  {
    label: 'Context budget',
    value: contextBudgetLabel(draftContextBudget.value),
    note: 'Control how much article context the runtime is allowed to inject.',
  },
  {
    label: 'Top priority source',
    value: sourcePriorityLabel(normalizeSourcePriority(draftSourcePriority.value)[0]),
    note: 'This source type appears first in the runtime prompt when multiple knowledge sources are present.',
  },
  {
    label: 'Search balance',
    value: searchBalanceLabel(draftSearchBalance.value),
    note: 'Choose whether retrieval should lean keyword-first or semantic-first.',
  },
  {
    label: 'Citation mode',
    value: retrievalCitationModeLabel(draftCitationMode.value),
    note: 'Controls whether retrieval-backed answers should carry citations.',
  },
  {
    label: 'Channel overrides',
    value: persistedDraftInboxBindings.value.length,
    note: persistedDraftInboxBindings.value.length
      ? 'One or more inboxes now override the assistant-wide Help Center scope.'
      : 'All connected channels currently inherit the assistant-wide Help Center scope.',
  },
  {
    label: 'Segment overrides',
    value: persistedDraftSegmentBindings.value.length,
    note: persistedDraftSegmentBindings.value.length
      ? 'Reply-language, conversation-language, VIP, or detected-attribute segments now override the assistant-wide Help Center scope.'
      : 'All reply-language, conversation-language, VIP, and detected-attribute segments currently inherit the assistant-wide scope.',
  },
  {
    label: 'No-evidence fallback',
    value: noEvidenceBehaviorLabel(draftNoEvidenceBehavior.value),
    note: 'What the runtime should do when no configured evidence is available.',
  },
]);

const retrievalWarnings = computed(() => {
  const warnings = [];
  if (!connectedInboxes.value.length) {
    warnings.push(
      'Connect at least one inbox if you want runtime article scope to follow a live channel context.'
    );
  }
  if (!articleCount.value) {
    warnings.push(
      'No published Help Center articles are live for the current runtime scope yet.'
    );
  }
  if (
    normalizeRetrievalCitationMode(draftCitationMode.value) === 'never' &&
    (websiteSyncCount.value > 0 || documentCount.value > 0)
  ) {
    warnings.push(
      'Links and uploaded documents are live, but reply citations are currently turned off.'
    );
  }
  if (
    articleRuntimeScopeMode.value === 'multi_connected_portals' &&
    draftArticleScopeMode.value !== 'selected_portal'
  ) {
    warnings.push(
      'Multiple portals are connected, so article knowledge can change by inbox unless you pin one Help Center portal.'
    );
  }
  if (persistedDraftInboxBindings.value.length) {
    warnings.push(
      'Some channels now override the assistant-wide Help Center scope. Use Test with a specific inbox to verify the final runtime behavior.'
    );
  }
  if (persistedDraftSegmentBindings.value.length) {
    warnings.push(
      'Some reply-language, conversation-language, VIP, or detected-attribute segments now override the assistant-wide Help Center scope. Use a realistic conversation test to confirm which segment wins.'
    );
  }
  if (
    !articleCount.value &&
    !snippetCount.value &&
    !websiteSyncCount.value &&
    !documentCount.value
  ) {
    warnings.push(
      `No retrieval-backed sources are live right now. Current fallback: ${noEvidenceBehaviorLabel(draftNoEvidenceBehavior.value)}.`
    );
  }
  return warnings;
});

const knowledgeSourceStrategyRows = computed(() => [
  {
    key: 'articles',
    title: 'Help Center articles',
    owner:
      draftArticleScopeMode.value === 'selected_portal'
        ? draftConfiguredArticlePortal.value?.name || 'Pinned portal'
        : 'Inbox portal',
    scope:
      draftArticleScopeMode.value === 'selected_portal'
        ? 'Pinned portal scope'
        : 'Per-inbox runtime scope',
    status: articleStatusText.value,
    note:
      articleRuntimeScopeMode.value === 'multi_connected_portals'
        ? 'Runtime resolves the portal from the current inbox unless you pin one.'
        : runtimeOwnershipNote.value,
  },
  {
    key: 'snippets',
    title: 'Legacy snippets',
    owner: 'Assistant config',
    scope: 'Assistant-wide',
    status: `${snippetCount.value} live`,
    note: 'Compatibility-only runtime knowledge that still applies across connected channels.',
  },
  {
    key: 'links',
    title: 'Website sync',
    owner: 'Assistant config',
    scope: 'Assistant-wide',
    status: `${websiteSyncCount.value} live`,
    note: normalizeRetrievalCitationMode(draftCitationMode.value) !== 'never'
      ? 'Citations can be attached when synced links are used in a reply.'
      : 'Replies can still use synced links, but source citations are turned off right now.',
  },
  {
    key: 'docs',
    title: 'Uploaded documents',
    owner: 'Assistant config',
    scope: 'Assistant-wide',
    status: `${documentCount.value} live`,
    note: normalizeRetrievalCitationMode(draftCitationMode.value) !== 'never'
      ? 'Document-backed answers can include source citations when the runtime attaches them.'
      : 'Documents are available to the runtime, but source citations are currently disabled.',
  },
]);
const orderedSourcePriorityItems = computed(() =>
  normalizeSourcePriority(draftSourcePriority.value).map((key, index) => {
    const source = RETRIEVAL_SOURCE_OPTIONS.find(option => option.key === key);
    return {
      key,
      rank: index + 1,
      label: source?.label || key,
      note: source?.note || '',
      canMoveUp: index > 0,
      canMoveDown: index < RETRIEVAL_SOURCE_OPTIONS.length - 1,
    };
  })
);

/* ── Content-sources table rows ─────────────────────────────────────────── */
const contentSources = computed(() => [
  {
    key: 'help_center_articles',
    icon: 'i-lucide-megaphone',
    title: 'Help Center articles',
    subtitle: '· Published public and internal articles used when the assistant resolves to this portal',
    statusDot: articleRuntimeEnabled.value || articleCount.value > 0,
    statusText: articleStatusText.value,
    aiCount: articleRuntimeEnabled.value ? 1 : 0,
    aiEnabled: articleRuntimeEnabled.value,
    manageTarget: null,
  },
  {
    key: 'snippets',
    icon: 'i-lucide-file-text',
    title: 'Legacy snippets',
    subtitle: '· Legacy answer templates still active in AI runtime',
    statusDot: snippetCount.value > 0,
    statusText: `${snippetCount.value} Live`,
    aiCount: snippetCount.value > 0 ? 1 : 0,
    aiEnabled: snippetCount.value > 0,
    manageTarget: 'answer_templates',
  },
  {
    key: 'website_sync',
    icon: 'i-lucide-globe',
    title: 'Website sync',
    subtitle: '· External public URLs and synced links used by AI',
    statusDot: websiteSyncCount.value > 0,
    statusText: `${websiteSyncCount.value} Live`,
    aiCount: websiteSyncCount.value > 0 ? 1 : 0,
    aiEnabled: websiteSyncCount.value > 0,
    manageTarget: 'links',
  },
  {
    key: 'external_apps',
    icon: 'i-lucide-database',
    title: 'External apps',
    subtitle: '· Zendesk, Notion, Confluence, Freshdesk',
    statusDot: false,
    statusText: '0 Live',
    aiCount: 0,
    aiEnabled: false,
    manageTarget: null,
  },
  {
    key: 'documents',
    icon: 'i-lucide-file-up',
    title: 'Uploaded documents',
    subtitle: '· Media library assets and uploaded files used by AI',
    statusDot: documentCount.value > 0,
    statusText: `${documentCount.value} Live`,
    aiCount: documentCount.value > 0 ? 1 : 0,
    aiEnabled: documentCount.value > 0,
    manageTarget: 'media',
  },
]);

const filteredContentSources = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();
  if (!query) return contentSources.value;

  return contentSources.value.filter(source => {
    return `${source.title} ${source.subtitle}`.toLowerCase().includes(query);
  });
});

const humanizeContentGapReason = reason => {
  switch (String(reason || '').trim()) {
    case 'article_scope_blocked':
      return 'Article scope blocked';
    case 'no_runtime_knowledge':
      return 'No runtime knowledge';
    case 'low_article_recall':
      return 'Low article recall';
    default:
      return String(reason || '').trim().replaceAll('_', ' ') || 'Unknown';
  }
};

const contentGapTrendLabel = row => {
  const recent = Number(row?.recent_7d_count || 0);
  const previous = Number(row?.previous_7d_count || 0);
  if (recent && !previous) return `+${recent} this week`;
  if (!recent && previous) return `${previous} last week`;
  if (recent === previous) return `${recent} this week`;

  const delta = recent - previous;
  return `${recent} this week (${delta > 0 ? '+' : ''}${delta} vs last week)`;
};

const CONTENT_GAP_RECOMMENDATION_WEEKS = 8;

const recommendationWeeklyBuckets = row =>
  (Array.isArray(row?.weekly_buckets) ? row.weekly_buckets : []).map(item => ({
    label: String(item?.label || '').trim(),
    count: Number(item?.count) || 0,
  }));

const recommendationTrendTitle = row => {
  const count = recommendationWeeklyBuckets(row).length || CONTENT_GAP_RECOMMENDATION_WEEKS;
  return `${count}-week trend`;
};

const formatRecommendationDate = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

const recommendationPersistenceLabel = row => {
  const ageDays = Number(row?.total_age_days || 0);
  const activeWeeks = Number(row?.active_week_count || 0);
  const streakWeeks = Number(row?.trailing_active_weeks || 0);
  const windowWeeks =
    recommendationWeeklyBuckets(row).length || CONTENT_GAP_RECOMMENDATION_WEEKS;
  const seenSince = formatRecommendationDate(row?.first_seen_at);

  if (ageDays <= 7) return 'New this week';
  if (streakWeeks >= 3) return `Active ${streakWeeks} weeks straight`;
  if (activeWeeks >= 2) {
    return `Seen in ${activeWeeks} of ${windowWeeks} weeks${
      seenSince ? ` since ${seenSince}` : ''
    }`;
  }
  return seenSince ? `Seen since ${seenSince}` : '';
};

const recommendationPriorityLabel = row => {
  const band = String(row?.priority_band || '').trim();
  const score = Number(row?.priority_score || 0);
  if (band === 'high') return `High priority${score ? ` (${score})` : ''}`;
  if (band === 'medium') return `Medium priority${score ? ` (${score})` : ''}`;
  if (band === 'low') return `Low priority${score ? ` (${score})` : ''}`;
  return '';
};

const recommendationPriorityClass = row => {
  switch (String(row?.priority_band || '').trim()) {
    case 'high':
      return 'border-rose-200 bg-rose-50 text-rose-700';
    case 'medium':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-600';
  }
};

const recommendationNextAction = row =>
  String(row?.recommended_action || '').trim();

const humanizeRecommendationWorkType = row => {
  switch (String(row?.work_type || '').trim()) {
    case 'scope_fix':
      return 'Scope fix';
    case 'recall_expansion':
      return 'Recall expansion';
    case 'draft_followup':
      return 'Draft follow-up';
    case 'net_new_article':
      return 'Net-new article';
    default:
      return 'Content review';
  }
};

const recommendationOwnerHint = row =>
  String(row?.owner_hint || '').trim();

const recommendationPriorityRationale = row =>
  String(row?.priority_rationale || '').trim();

const recommendationUrgencyLabel = row => {
  switch (String(row?.urgency_signal || '').trim()) {
    case 'act_now':
      return 'Act now';
    case 'review_draft_now':
      return 'Review draft now';
    case 'persistent_gap':
      return 'Persistent gap';
    case 'plan_next':
      return 'Plan next';
    default:
      return 'Monitor';
  }
};

const humanizeRecommendationFamily = row => {
  switch (String(row?.semantic_family || '').trim()) {
    case 'verification_documents':
      return 'Verification docs';
    case 'document_requirements':
      return 'Document requirements';
    case 'status_tracking':
      return 'Status tracking';
    case 'timing_and_sla':
      return 'Timing / SLA';
    case 'eligibility_requirements':
      return 'Eligibility';
    case 'fees_and_limits':
      return 'Fees / limits';
    case 'withdrawal_operations':
      return 'Withdrawal ops';
    case 'refund_operations':
      return 'Refund ops';
    default:
      return 'General content';
  }
};

const humanizeRecommendationDeliveryStage = row => {
  switch (String(row?.delivery_stage || '').trim()) {
    case 'review_published_coverage':
      return 'Review published coverage';
    case 'review_linked_draft':
      return 'Review linked draft';
    case 'fix_scope':
      return 'Fix scope';
    case 'expand_existing_coverage':
      return 'Expand existing coverage';
    case 'write_net_new_article':
      return 'Write net-new article';
    default:
      return 'Review cluster';
  }
};

const recommendationDeliveryEffortLabel = row => {
  switch (String(row?.delivery_effort || '').trim()) {
    case 'large':
      return 'Large effort';
    case 'medium':
      return 'Medium effort';
    default:
      return 'Small effort';
  }
};

const recommendationReadinessLabel = row => {
  switch (String(row?.readiness_status || '').trim()) {
    case 'ready_for_content_work':
      return 'Ready for content work';
    case 'review_existing_draft':
      return 'Review existing draft';
    case 'needs_scope_decision':
      return 'Needs scope decision';
    case 'coverage_already_published':
      return 'Coverage already published';
    default:
      return 'Monitoring';
  }
};

const recommendationTrendDirectionLabel = value => {
  switch (String(value || '').trim()) {
    case 'new':
      return 'New';
    case 'rising':
      return 'Rising';
    case 'cooling':
      return 'Cooling';
    case 'steady':
      return 'Steady';
    default:
      return 'Quiet';
  }
};

const recommendationFreshnessLabel = row => {
  switch (String(row?.freshness_band || '').trim()) {
    case 'active_now':
      return 'Active now';
    case 'recent':
      return 'Recent';
    case 'aging':
      return 'Aging';
    default:
      return 'Stale';
  }
};

const recommendationBacklogStatusLabel = row => {
  switch (String(row?.backlog_status || '').trim()) {
    case 'draft_in_progress':
      return 'Draft in progress';
    case 'published_followup':
      return 'Published follow-up';
    case 'aging_backlog':
      return 'Aging backlog';
    case 'active_backlog':
      return 'Active backlog';
    default:
      return 'Monitored backlog';
  }
};

const recommendationFamilyConfidenceLabel = row => {
  switch (String(row?.family_confidence || '').trim()) {
    case 'strong':
      return 'Strong family match';
    case 'medium':
      return 'Medium family match';
    default:
      return 'Light family match';
  }
};

const recommendationRoutingTargetLabel = row => {
  switch (String(row?.routing_target || '').trim()) {
    case 'verification_content_owner':
      return 'Verification content owner';
    case 'transaction_ops_content':
      return 'Transaction ops content';
    case 'portal_scope_owner':
      return 'Portal scope owner';
    case 'content_editor_followup':
      return 'Content editor follow-up';
    default:
      return 'Knowledge owner';
  }
};

const recommendationSnapshotSummary = computed(
  () => gapRecommendationsMeta.value?.snapshot_summary || {}
);

const recommendationSnapshotModeLabel = value => {
  switch (String(value || '').trim()) {
    case 'scheduled':
      return 'Scheduled snapshot';
    default:
      return 'Manual snapshot';
  }
};

const recommendationSnapshotLabel = computed(() => {
  const latest = recommendationSnapshotSummary.value?.latest_snapshot_at;
  if (!latest) return 'No durable snapshot yet';

  const date = new Date(latest);
  const modeLabel = recommendationSnapshotModeLabel(
    recommendationSnapshotSummary.value?.latest_capture_mode
  );
  if (Number.isNaN(date.getTime())) return `${modeLabel} captured`;
  return `${modeLabel} ${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
});

const formatRecommendationSnapshotDelta = value => {
  const number = Number(value || 0);
  if (!number) return '0';
  return `${number > 0 ? '+' : ''}${number}`;
};

const recommendationSnapshotCards = computed(() => [
  {
    key: 'total_clusters',
    label: 'Clusters vs prev',
    value: `${Number(recommendationSnapshotSummary.value?.total_clusters || 0)} (${formatRecommendationSnapshotDelta(
      recommendationSnapshotSummary.value?.total_clusters_delta
    )})`,
  },
  {
    key: 'write_now',
    label: 'Write now delta',
    value: formatRecommendationSnapshotDelta(recommendationSnapshotSummary.value?.write_now_delta),
  },
  {
    key: 'active_now',
    label: 'Active now delta',
    value: formatRecommendationSnapshotDelta(recommendationSnapshotSummary.value?.active_now_delta),
  },
  {
    key: 'top_route',
    label: 'Top route',
    value: recommendationSnapshotSummary.value?.top_routing_label || 'Uncaptured',
  },
]);

const recommendationSnapshotHistoryPoints = computed(() =>
  (Array.isArray(recommendationSnapshotSummary.value?.history_points)
    ? recommendationSnapshotSummary.value.history_points
    : []
  ).map(point => ({
    capturedAt: String(point?.captured_at || '').trim(),
    captureMode: String(point?.capture_mode || '').trim(),
    totalClusters: Number(point?.total_clusters || 0),
    writeNowCount: Number(point?.write_now_count || 0),
    activeNowCount: Number(point?.active_now_count || 0),
    strongFamilyCount: Number(point?.strong_family_count || 0),
  }))
);

const recommendationBacklogTrajectory = computed(
  () => recommendationSnapshotSummary.value?.backlog_trajectory || {}
);

const recommendationTrajectoryLabel = computed(() => {
  switch (String(recommendationBacklogTrajectory.value?.direction || '').trim()) {
    case 'burn_up':
      return 'Backlog rising';
    case 'burn_down':
      return 'Backlog burning down';
    default:
      return 'Backlog steady';
  }
});

const recommendationTrajectoryCards = computed(() => [
  {
    key: 'trajectory',
    label: 'Trajectory',
    value: recommendationTrajectoryLabel.value,
    detail: `${Number(recommendationBacklogTrajectory.value?.snapshots_considered || 0)} snapshots`,
  },
  {
    key: 'clusters',
    label: 'Cluster change',
    value: formatRecommendationSnapshotDelta(
      recommendationBacklogTrajectory.value?.total_clusters_change
    ),
    detail: `Peak ${Number(recommendationBacklogTrajectory.value?.peak_total_clusters || 0)}`,
  },
  {
    key: 'write_now',
    label: 'Write now change',
    value: formatRecommendationSnapshotDelta(
      recommendationBacklogTrajectory.value?.write_now_change
    ),
    detail: `Peak ${Number(recommendationBacklogTrajectory.value?.peak_write_now_count || 0)}`,
  },
  {
    key: 'active_now',
    label: 'Active now change',
    value: formatRecommendationSnapshotDelta(
      recommendationBacklogTrajectory.value?.active_now_change
    ),
    detail: recommendationBacklogTrajectory.value?.first_snapshot_at
      ? `Since ${formatRecommendationDate(
          recommendationBacklogTrajectory.value?.first_snapshot_at
        )}`
      : 'History window',
  },
]);

const contentOptimizationSnapshotCards = computed(() => [
  {
    key: 'snapshot',
    label: 'Latest snapshot',
    value: recommendationSnapshotLabel.value,
    detail: `${Number(recommendationSnapshotSummary.value?.snapshot_count || 0)} captured`,
  },
  {
    key: 'trajectory',
    label: 'Backlog trend',
    value: recommendationTrajectoryLabel.value,
    detail: `${Number(recommendationBacklogTrajectory.value?.snapshots_considered || 0)} snapshots`,
  },
  {
    key: 'write_now',
    label: 'Write now',
    value: Number(recommendationSnapshotSummary.value?.write_now_count || 0),
    detail: formatRecommendationSnapshotDelta(
      recommendationSnapshotSummary.value?.write_now_delta
    ),
  },
  {
    key: 'active_now',
    label: 'Active now',
    value: Number(recommendationSnapshotSummary.value?.active_now_count || 0),
    detail: formatRecommendationSnapshotDelta(
      recommendationSnapshotSummary.value?.active_now_delta
    ),
  },
]);

const topContentOptimizationAlerts = computed(() =>
  [
    ...recommendationVisibleLanePressureAlerts.value,
    ...recommendationVisibleOwnerCapacityAlerts.value,
  ].slice(0, 3)
);

const normalizeRecommendationSnapshotAlert = alert => ({
  key: String(alert?.key || '').trim(),
  category: String(alert?.category || '').trim(),
  id: String(alert?.id || '').trim(),
  label: String(alert?.label || '').trim(),
  severity: String(alert?.severity || '').trim(),
  count: Number(alert?.count || 0),
  delta: Number(alert?.delta || 0),
  trendDirection: String(alert?.trend_direction || '').trim(),
  thresholdValue: Number(alert?.threshold_value || 0),
  thresholdStatus: String(alert?.threshold_status || '').trim(),
  capacityBand: String(alert?.capacity_band || '').trim(),
  thresholdProfile: alert?.threshold_profile || {},
  title: String(alert?.title || '').trim(),
  message: String(alert?.message || '').trim(),
  recommendedActionLabel: String(alert?.recommended_action_label || '').trim(),
  recommendedAction: alert?.recommended_action || {},
  drilldownContext: alert?.drilldown_context || {},
  recommendedNextSteps: Array.isArray(alert?.recommended_next_steps)
    ? alert.recommended_next_steps.map(item => String(item || '').trim()).filter(Boolean)
    : [],
  resolutionChecklist: Array.isArray(alert?.resolution_checklist)
    ? alert.resolution_checklist.map(item => String(item || '').trim()).filter(Boolean)
    : [],
  workflowStatus: String(alert?.workflow_status || '').trim(),
  workflowSessionState: String(alert?.workflow_session_state || '').trim(),
  workflowSummaryLabel: String(alert?.workflow_summary_label || '').trim(),
  workflowReopenReason: String(alert?.workflow_reopen_reason || '').trim(),
  workflowOwnerLabel: String(alert?.workflow_owner_label || '').trim(),
  workflowDueAt: String(alert?.workflow_due_at || '').trim(),
  workflowDueStatus: String(alert?.workflow_due_status || '').trim(),
  workflowResolutionOutcome: String(alert?.workflow_resolution_outcome || '').trim(),
  workflowHandoffReason: String(alert?.workflow_handoff_reason || '').trim(),
  workflowNextCheckpoint: String(alert?.workflow_next_checkpoint || '').trim(),
  workflowNote: String(alert?.workflow_note || '').trim(),
  workflowNeedsFollowup: Boolean(alert?.workflow_needs_followup),
  workflowResultKey: String(alert?.workflow_result_key || '').trim(),
  workflowResultLabel: String(alert?.workflow_result_label || '').trim(),
  workflowLastEventAt: String(alert?.workflow_last_event_at || '').trim(),
  workflowLastEventLabel: String(alert?.workflow_last_event_label || '').trim(),
  workflowLastEventType: String(alert?.workflow_last_event_type || '').trim(),
  workflowLastActorName: String(alert?.workflow_last_actor_name || '').trim(),
  updatedAt: String(alert?.updated_at || alert?.workflow_state?.updated_at || '').trim(),
  targetLabel: String(alert?.target_label || alert?.label || '').trim(),
  isSuppressed: Boolean(alert?.is_suppressed),
  workflowState: alert?.workflow_state || {},
});

const normalizeRecommendationWorkflowAuditEvent = event => ({
  workflowKey: String(event?.workflow_key || '').trim(),
  workflowLabel: String(event?.workflow_label || '').trim(),
  changeType: String(event?.change_type || '').trim(),
  changeSummaryLabel: String(event?.change_summary_label || '').trim(),
  changedAt: String(event?.changed_at || '').trim(),
  actorName: String(event?.actor_name || '').trim(),
  previousStatus: String(event?.previous_status || '').trim(),
  nextStatus: String(event?.next_status || '').trim(),
  changedFields: Array.isArray(event?.changed_fields)
    ? event.changed_fields.map(item => String(item || '').trim()).filter(Boolean)
    : [],
  fieldChanges: Array.isArray(event?.field_changes)
    ? event.field_changes.map(change => ({
        field: String(change?.field || '').trim(),
        label: String(change?.label || '').trim(),
        previousValue: change?.previous_value ?? '',
        nextValue: change?.next_value ?? '',
        previousDisplay: String(
          change?.previous_display ?? change?.previous_value ?? ''
        ).trim(),
        nextDisplay: String(
          change?.next_display ?? change?.next_value ?? ''
        ).trim(),
      }))
    : [],
  changeHighlights: Array.isArray(event?.change_highlights)
    ? event.change_highlights.map(item => ({
        id: String(item?.id || '').trim(),
        label: String(item?.label || '').trim(),
      }))
    : [],
});

const normalizeRecommendationWorkflowHighlightCard = item => ({
  id: String(item?.id || '').trim(),
  label: String(item?.label || '').trim(),
  count: Number(item?.count || 0),
  sessionCount: Number(item?.session_count || 0),
  latestChangedAt: String(item?.latest_changed_at || '').trim(),
  latestWorkflowKey: String(item?.latest_workflow_key || '').trim(),
  latestWorkflowLabel: String(item?.latest_workflow_label || '').trim(),
  recommendedFilterId: String(
    item?.recommended_filter_id || item?.id || ''
  ).trim(),
  recommendedFilterLabel: String(
    item?.recommended_filter_label || item?.label || ''
  ).trim(),
  drilldownContext: item?.drilldown_context || {},
});

const normalizeRecommendationWorkflowHighlightWindowCard = item => ({
  id: String(item?.id || '').trim(),
  label: String(item?.label || '').trim(),
  recentCount: Number(item?.recent_count || 0),
  previousCount: Number(item?.previous_count || 0),
  delta: Number(item?.delta || 0),
  trendDirection: String(item?.trend_direction || '').trim(),
  recentSessionCount: Number(item?.recent_session_count || 0),
  previousSessionCount: Number(item?.previous_session_count || 0),
  sessionDelta: Number(item?.session_delta || 0),
  latestChangedAt: String(item?.latest_changed_at || '').trim(),
  latestWorkflowKey: String(item?.latest_workflow_key || '').trim(),
  latestWorkflowLabel: String(item?.latest_workflow_label || '').trim(),
  recommendedFilterId: String(
    item?.recommended_filter_id || item?.id || ''
  ).trim(),
  recommendedFilterLabel: String(
    item?.recommended_filter_label || item?.label || ''
  ).trim(),
  drilldownContext: item?.drilldown_context || {},
});

const recommendationLanePressureAlerts = computed(() =>
  (Array.isArray(recommendationSnapshotSummary.value?.lane_pressure_alerts)
    ? recommendationSnapshotSummary.value.lane_pressure_alerts
    : []
  ).map(normalizeRecommendationSnapshotAlert)
);

const recommendationOwnerCapacityAlerts = computed(() =>
  (Array.isArray(recommendationSnapshotSummary.value?.owner_capacity_alerts)
    ? recommendationSnapshotSummary.value.owner_capacity_alerts
    : []
  ).map(normalizeRecommendationSnapshotAlert)
);

const recommendationVisibleLanePressureAlerts = computed(() =>
  recommendationLanePressureAlerts.value.filter(alert => !alert.isSuppressed)
);

const recommendationVisibleOwnerCapacityAlerts = computed(() =>
  recommendationOwnerCapacityAlerts.value.filter(alert => !alert.isSuppressed)
);

const recommendationAllAlerts = computed(() => [
  ...recommendationLanePressureAlerts.value,
  ...recommendationOwnerCapacityAlerts.value,
]);

const recommendationAlertWorkflowLedger = computed(() =>
  (Array.isArray(recommendationSnapshotSummary.value?.alert_workflow_ledger)
    ? recommendationSnapshotSummary.value.alert_workflow_ledger
    : []
  ).map(normalizeRecommendationSnapshotAlert)
);

const recommendationAlertWorkflowSessionAudits = computed(() =>
  (Array.isArray(recommendationSnapshotSummary.value?.alert_workflow_session_audits)
    ? recommendationSnapshotSummary.value.alert_workflow_session_audits
    : []
  ).map(audit => ({
    workflowKey: String(audit?.workflow_key || '').trim(),
    workflowLabel: String(audit?.workflow_label || '').trim(),
    totalEvents: Number(audit?.total_events || 0),
    latestChangedAt: String(audit?.latest_changed_at || '').trim(),
    latestChangeType: String(audit?.latest_change_type || '').trim(),
    latestChangeSummaryLabel: String(audit?.latest_change_summary_label || '').trim(),
    latestChangeHighlights: Array.isArray(audit?.latest_change_highlights)
      ? audit.latest_change_highlights.map(item => ({
          id: String(item?.id || '').trim(),
          label: String(item?.label || '').trim(),
        }))
      : [],
    highlightCards: Array.isArray(audit?.highlight_cards)
      ? audit.highlight_cards.map(normalizeRecommendationWorkflowHighlightCard)
      : [],
    latestEvent: normalizeRecommendationWorkflowAuditEvent(audit?.latest_event || {}),
    events: Array.isArray(audit?.events)
      ? audit.events.map(normalizeRecommendationWorkflowAuditEvent)
      : [],
  }))
);

const activeRecommendationAlert = computed(() => {
  if (!activeRecommendationAlertKey.value) return null;
  return (
    recommendationAllAlerts.value.find(
      alert => alert.key === activeRecommendationAlertKey.value
    ) || null
  );
});

const activeRecommendationWorkflowAudit = computed(() => {
  const workflowKey = String(activeRecommendationWorkflowAuditKey.value || '').trim();
  if (!workflowKey) return null;

  const storedAudit =
    recommendationAlertWorkflowSessionAudits.value.find(
      audit => audit.workflowKey === workflowKey
    ) || null;
  if (storedAudit) return storedAudit;

  const fallbackAlert =
    recommendationAlertWorkflowLedger.value.find(alert => alert.key === workflowKey) ||
    recommendationAllAlerts.value.find(alert => alert.key === workflowKey) ||
    null;

  if (!fallbackAlert) return null;

  return {
    workflowKey,
    workflowLabel: fallbackAlert.targetLabel || fallbackAlert.label || workflowKey,
    totalEvents: 0,
    latestChangedAt: fallbackAlert.workflowLastEventAt || '',
    latestChangeType: fallbackAlert.workflowLastEventType || '',
    latestChangeSummaryLabel: fallbackAlert.workflowLastEventLabel || '',
    latestChangeHighlights: [],
    highlightCards: [],
    latestEvent: normalizeRecommendationWorkflowAuditEvent({}),
    events: [],
  };
});

const recommendationAlertClass = alert => {
  switch (String(alert?.severity || '').trim()) {
    case 'high':
      return 'border-rose-200 bg-rose-50 text-rose-700';
    case 'medium':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    default:
      return 'border-slate-200 bg-slate-50 text-slate-600';
  }
};

const recommendationAlertThresholdLabel = alert => {
  switch (String(alert?.thresholdStatus || '').trim()) {
    case 'above_threshold':
      return `Above threshold (${alert?.thresholdValue || 0})`;
    case 'trend_watch':
      return 'Trend watch';
    default:
      return 'Within threshold';
  }
};

const recommendationAlertCapacityBandLabel = alert => {
  switch (String(alert?.capacityBand || '').trim()) {
    case 'overflow':
      return 'Overflow';
    case 'at_capacity':
      return 'At capacity';
    case 'watch':
      return 'Watch';
    default:
      return 'Stable';
  }
};

const recommendationAlertWorkflowStateLabel = alert => {
  switch (String(alert?.workflowSessionState || '').trim()) {
    case 'snoozed':
      return 'Snoozed';
    case 'resolved':
      return 'Resolved';
    case 'handoff':
      return 'Handed off';
    case 'reopened':
      return 'Reopened';
    default:
      return 'No workflow';
  }
};

const recommendationAlertDueStatusLabel = alert => {
  switch (String(alert?.workflowDueStatus || '').trim()) {
    case 'overdue':
      return 'Overdue';
    case 'due_soon':
      return 'Due soon';
    case 'scheduled':
      return 'Scheduled';
    default:
      return 'No due date';
  }
};

const recommendationAlertWorkflowSummary = computed(
  () => recommendationSnapshotSummary.value?.alert_workflow_summary || {}
);

const recommendationAlertWorkflowFilterCards = computed(
  () => recommendationSnapshotSummary.value?.alert_workflow_filter_cards || {}
);

const recommendationAlertWorkflowStatusCards = computed(() =>
  Array.isArray(recommendationAlertWorkflowFilterCards.value?.status_cards)
    ? recommendationAlertWorkflowFilterCards.value.status_cards
    : []
);

const recommendationAlertWorkflowDueCards = computed(() =>
  Array.isArray(recommendationAlertWorkflowFilterCards.value?.due_cards)
    ? recommendationAlertWorkflowFilterCards.value.due_cards
    : []
);

const recommendationAlertWorkflowFollowupCards = computed(() =>
  Array.isArray(recommendationAlertWorkflowFilterCards.value?.followup_cards)
    ? recommendationAlertWorkflowFilterCards.value.followup_cards
    : []
);

const recommendationAlertWorkflowResultCards = computed(() =>
  Array.isArray(recommendationAlertWorkflowFilterCards.value?.result_cards)
    ? recommendationAlertWorkflowFilterCards.value.result_cards
    : []
);

const recommendationAlertWorkflowSortOptions = computed(() =>
  Array.isArray(recommendationSnapshotSummary.value?.alert_workflow_sort_options)
    ? recommendationSnapshotSummary.value.alert_workflow_sort_options
    : [
        { id: 'followup_due', label: 'Follow-up first' },
        { id: 'recent_update', label: 'Recent update' },
        { id: 'title', label: 'Title' },
      ]
);

const recommendationAlertWorkflowCards = computed(() => [
  {
    key: 'visible',
    label: 'Visible',
    value: Number(recommendationAlertWorkflowSummary.value?.visible_alerts || 0),
  },
  {
    key: 'suppressed',
    label: 'Suppressed',
    value: Number(recommendationAlertWorkflowSummary.value?.suppressed_alerts || 0),
  },
  {
    key: 'reopened',
    label: 'Reopened',
    value: Number(recommendationAlertWorkflowSummary.value?.reopened_alerts || 0),
  },
  {
    key: 'followup',
    label: 'Needs follow-up',
    value: Number(recommendationAlertWorkflowSummary.value?.needs_followup_alerts || 0),
  },
  {
    key: 'overdue',
    label: 'Overdue',
    value: Number(recommendationAlertWorkflowSummary.value?.overdue_alerts || 0),
  },
]);

const recommendationAlertWorkflowOutcomeRollups = computed(
  () => recommendationSnapshotSummary.value?.alert_workflow_outcome_rollups || {}
);

const recommendationAlertWorkflowActivitySummary = computed(
  () => recommendationSnapshotSummary.value?.alert_workflow_activity_summary || {}
);

const recommendationAlertWorkflowActivityFilterCards = computed(
  () => recommendationSnapshotSummary.value?.alert_workflow_activity_filter_cards || {}
);

const recommendationAlertWorkflowActivityHighlightRollups = computed(
  () => recommendationSnapshotSummary.value?.alert_workflow_activity_highlight_rollups || {}
);

const recommendationAlertWorkflowActivityHighlightWindow = computed(
  () => recommendationSnapshotSummary.value?.alert_workflow_activity_highlight_window || {}
);

const recommendationAlertWorkflowActivityCards = computed(() => [
  {
    key: 'created',
    label: 'Created',
    value: Number(recommendationAlertWorkflowActivitySummary.value?.created_events || 0),
  },
  {
    key: 'updated',
    label: 'Updated',
    value: Number(recommendationAlertWorkflowActivitySummary.value?.updated_events || 0),
  },
  {
    key: 'cleared',
    label: 'Cleared',
    value: Number(recommendationAlertWorkflowActivitySummary.value?.cleared_events || 0),
  },
]);

const recommendationAlertWorkflowActivityTimeline = computed(() =>
  (Array.isArray(recommendationSnapshotSummary.value?.alert_workflow_activity_timeline)
    ? recommendationSnapshotSummary.value.alert_workflow_activity_timeline
    : []
  ).map(normalizeRecommendationWorkflowAuditEvent)
);

const recommendationAlertWorkflowActivityHighlightCards = computed(() =>
  Array.isArray(recommendationAlertWorkflowActivityFilterCards.value?.highlight_cards)
    ? recommendationAlertWorkflowActivityFilterCards.value.highlight_cards.map(
        normalizeRecommendationWorkflowHighlightCard
      )
    : []
);

const recommendationAlertWorkflowActivityTrendCards = computed(() =>
  Array.isArray(recommendationAlertWorkflowActivityHighlightRollups.value?.highlight_cards)
    ? recommendationAlertWorkflowActivityHighlightRollups.value.highlight_cards.map(
        normalizeRecommendationWorkflowHighlightCard
      )
    : []
);

const recommendationAlertWorkflowActivityWindowTrendCards = computed(() =>
  Array.isArray(recommendationAlertWorkflowActivityHighlightWindow.value?.highlight_cards)
    ? recommendationAlertWorkflowActivityHighlightWindow.value.highlight_cards.map(
        normalizeRecommendationWorkflowHighlightWindowCard
      )
    : []
);

const activeRecommendationAlertWorkflowActivityHighlightContext = computed(() => {
  const filterId = String(recommendationAlertWorkflowActivityHighlightFilter.value || '').trim();
  if (!filterId || filterId === 'all') return null;

  const candidates = [
    ...recommendationAlertWorkflowActivityWindowTrendCards.value,
    ...recommendationAlertWorkflowActivityTrendCards.value,
    ...recommendationAlertWorkflowActivityHighlightCards.value,
  ];
  const match =
    candidates.find(item => item.recommendedFilterId === filterId) || null;

  if (match) return match;

  return {
    recommendedFilterId: filterId,
    recommendedFilterLabel: filterId.replaceAll('_', ' '),
    drilldownContext: {
      title: 'Filtered workflow activity',
      summary: 'Recent activity is filtered to the currently selected change intent.',
      source_kind: 'activity_filter',
      filter_id: filterId,
    },
  };
});

const recommendationFilteredAlertWorkflowActivityTimeline = computed(() =>
  recommendationAlertWorkflowActivityTimeline.value.filter(item => {
    if (recommendationAlertWorkflowActivityHighlightFilter.value === 'all') return true;

    return item.changeHighlights.some(
      highlight => highlight.id === recommendationAlertWorkflowActivityHighlightFilter.value
    );
  })
);

const activeRecommendationWorkflowAuditHighlightCards = computed(() =>
  Array.isArray(activeRecommendationWorkflowAudit.value?.highlightCards)
    ? activeRecommendationWorkflowAudit.value.highlightCards
    : []
);

const recommendationFilteredActiveWorkflowAuditEvents = computed(() => {
  const events = Array.isArray(activeRecommendationWorkflowAudit.value?.events)
    ? activeRecommendationWorkflowAudit.value.events
    : [];
  if (recommendationWorkflowAuditHighlightFilter.value === 'all') return events;

  return events.filter(item =>
    item.changeHighlights.some(
      highlight => highlight.id === recommendationWorkflowAuditHighlightFilter.value
    )
  );
});

const recommendationAlertWorkflowResolutionOutcomeCards = computed(() =>
  Array.isArray(
    recommendationAlertWorkflowOutcomeRollups.value?.resolution_outcomes
  )
    ? recommendationAlertWorkflowOutcomeRollups.value.resolution_outcomes
    : []
);

const recommendationAlertWorkflowHandoffReasonCards = computed(() =>
  Array.isArray(
    recommendationAlertWorkflowOutcomeRollups.value?.handoff_reasons
  )
    ? recommendationAlertWorkflowOutcomeRollups.value.handoff_reasons
    : []
);

const recommendationFilteredAlertWorkflowLedger = computed(() => {
  const filtered = recommendationAlertWorkflowLedger.value.filter(item => {
    if (
      recommendationAlertWorkflowStatusFilter.value !== 'all' &&
      (recommendationAlertWorkflowStatusFilter.value === 'suppressed'
        ? !item.isSuppressed
        : item.isSuppressed)
    ) {
      return false;
    }

    if (
      recommendationAlertWorkflowDueFilter.value !== 'all' &&
      item.workflowDueStatus !== recommendationAlertWorkflowDueFilter.value
    ) {
      return false;
    }

    if (recommendationAlertWorkflowFollowupFilter.value !== 'all') {
      const expectsFollowup =
        recommendationAlertWorkflowFollowupFilter.value === 'needs_followup';
      if (Boolean(item.workflowNeedsFollowup) !== expectsFollowup) {
        return false;
      }
    }

    if (
      recommendationAlertWorkflowResultFilter.value !== 'all' &&
      item.workflowResultKey !== recommendationAlertWorkflowResultFilter.value
    ) {
      return false;
    }

    return true;
  });

  return [...filtered].sort((left, right) => {
    switch (recommendationAlertWorkflowSort.value) {
      case 'recent_update':
        return String(right.updatedAt || '').localeCompare(String(left.updatedAt || ''));
      case 'title':
        return String(left.title || '').localeCompare(String(right.title || ''));
      default: {
        const leftRank =
          (left.isSuppressed ? 10 : 0) +
          (left.workflowNeedsFollowup ? 0 : 2) +
          (left.workflowDueStatus === 'overdue'
            ? 0
            : left.workflowDueStatus === 'due_soon'
              ? 1
              : left.workflowDueStatus === 'scheduled'
                ? 2
                : 3);
        const rightRank =
          (right.isSuppressed ? 10 : 0) +
          (right.workflowNeedsFollowup ? 0 : 2) +
          (right.workflowDueStatus === 'overdue'
            ? 0
            : right.workflowDueStatus === 'due_soon'
              ? 1
              : right.workflowDueStatus === 'scheduled'
                ? 2
                : 3);
        if (leftRank !== rightRank) return leftRank - rightRank;
        return String(right.updatedAt || '').localeCompare(String(left.updatedAt || ''));
      }
    }
  });
});

const selectedRecommendationAlertWorkflowSessions = computed(() =>
  recommendationAlertWorkflowLedger.value.filter(item =>
    selectedRecommendationAlertWorkflowKeys.value.includes(item.key)
  )
);

const recommendationAlertWorkflowAllFilteredSelected = computed(() =>
  recommendationFilteredAlertWorkflowLedger.value.length > 0 &&
  recommendationFilteredAlertWorkflowLedger.value.every(item =>
    selectedRecommendationAlertWorkflowKeys.value.includes(item.key)
  )
);

const resetRecommendationAlertWorkflowFilters = () => {
  recommendationAlertWorkflowStatusFilter.value = 'all';
  recommendationAlertWorkflowDueFilter.value = 'all';
  recommendationAlertWorkflowFollowupFilter.value = 'all';
  recommendationAlertWorkflowResultFilter.value = 'all';
  recommendationAlertWorkflowSort.value =
    recommendationSnapshotSummary.value?.alert_workflow_default_sort ||
    'followup_due';
};

const toggleRecommendationAlertWorkflowSelection = workflowKey => {
  const key = String(workflowKey || '').trim();
  if (!key) return;

  if (selectedRecommendationAlertWorkflowKeys.value.includes(key)) {
    selectedRecommendationAlertWorkflowKeys.value =
      selectedRecommendationAlertWorkflowKeys.value.filter(item => item !== key);
    return;
  }

  selectedRecommendationAlertWorkflowKeys.value = [
    ...selectedRecommendationAlertWorkflowKeys.value,
    key,
  ];
};

const selectAllFilteredRecommendationAlertWorkflows = () => {
  selectedRecommendationAlertWorkflowKeys.value = [
    ...new Set(
      recommendationFilteredAlertWorkflowLedger.value.map(item => item.key)
    ),
  ];
};

const clearSelectedRecommendationAlertWorkflows = () => {
  selectedRecommendationAlertWorkflowKeys.value = [];
};

const normalizeRecommendationThresholdProfile = profile => ({
  key: String(profile?.key || '').trim(),
  label: String(profile?.label || '').trim(),
  rationale: String(profile?.rationale || '').trim(),
  basis: String(profile?.basis || '').trim(),
  thresholdValue: Number(profile?.threshold_value || 0),
  watchDelta: Number(profile?.watch_delta || 0),
  thresholdStatus: String(profile?.threshold_status || '').trim(),
  capacityBand: String(profile?.capacity_band || '').trim(),
  profileSource: String(profile?.profile_source || '').trim(),
  overrideFields: Array.isArray(profile?.override_fields)
    ? profile.override_fields.map(item => String(item || '').trim()).filter(Boolean)
    : [],
  category: String(profile?.category || '').trim(),
  id: String(profile?.id || '').trim(),
  currentCount: Number(profile?.current_count || 0),
  currentDelta: Number(profile?.current_delta || 0),
  currentTrendDirection: String(profile?.current_trend_direction || '').trim(),
  targetLabel: String(profile?.target_label || '').trim(),
});

const recommendationThresholdProfiles = computed(() =>
  (Array.isArray(recommendationSnapshotSummary.value?.threshold_profiles)
    ? recommendationSnapshotSummary.value.threshold_profiles
    : []
  ).map(normalizeRecommendationThresholdProfile)
);

const recommendationThresholdProfileSourceLabel = profile => {
  switch (String(profile?.profileSource || profile?.profile_source || '').trim()) {
    case 'assistant_config_override':
      return 'Assistant override';
    default:
      return 'Default profile';
  }
};

const normalizeRecommendationThresholdOverrideEntry = entry => {
  const normalized = {};
  const label = String(entry?.label || '').trim();
  const rationale = String(entry?.rationale || '').trim();
  const thresholdValue = Number.parseInt(String(entry?.thresholdValue || '').trim(), 10);
  const watchDelta = Number.parseInt(String(entry?.watchDelta || '').trim(), 10);

  if (label) normalized.label = label;
  if (rationale) normalized.rationale = rationale;
  if (Number.isInteger(thresholdValue) && thresholdValue > 0) {
    normalized.threshold_value = thresholdValue;
  }
  if (Number.isInteger(watchDelta) && watchDelta > 0) {
    normalized.watch_delta = watchDelta;
  }

  return normalized;
};

const currentRecommendationThresholdOverride = profile => {
  const root = props.assistant?.config?.content_gap_threshold_profiles || {};
  const category = root?.[profile?.category] || {};
  const entry = category?.[profile?.id] || {};

  return normalizeRecommendationThresholdOverrideEntry({
    label: entry?.label,
    rationale: entry?.rationale,
    thresholdValue: entry?.threshold_value,
    watchDelta: entry?.watch_delta,
  });
};

const recommendationThresholdOverrideDraft = profile =>
  recommendationThresholdOverrideDrafts.value?.[profile?.key] || {
    label: '',
    rationale: '',
    thresholdValue: '',
    watchDelta: '',
  };

const recommendationThresholdOverrideDirty = profile => {
  const current = JSON.stringify(currentRecommendationThresholdOverride(profile));
  const draft = JSON.stringify(
    normalizeRecommendationThresholdOverrideEntry(
      recommendationThresholdOverrideDraft(profile)
    )
  );

  return current !== draft;
};

const hasPersistedRecommendationThresholdOverride = profile =>
  Object.keys(currentRecommendationThresholdOverride(profile)).length > 0;

const recommendationThresholdResetLabel = profile =>
  hasPersistedRecommendationThresholdOverride(profile)
    ? 'Reset default'
    : 'Clear draft';

const normalizeRecommendationSnapshotTrendCard = card => ({
  id: String(card?.id || '').trim(),
  label: String(card?.label || '').trim(),
  count: Number(card?.count || 0),
  delta: Number(card?.delta || 0),
  trendDirection: String(card?.trend_direction || '').trim(),
  peakCount: Number(card?.peak_count || 0),
  history: (Array.isArray(card?.history) ? card.history : []).map(point => ({
    capturedAt: String(point?.captured_at || '').trim(),
    count: Number(point?.count || 0),
  })),
});

const recommendationPresetTrendCards = computed(() =>
  (Array.isArray(recommendationSnapshotSummary.value?.preset_trend_cards)
    ? recommendationSnapshotSummary.value.preset_trend_cards
    : []
  ).map(normalizeRecommendationSnapshotTrendCard)
);

const recommendationReadinessTrendCards = computed(() =>
  (Array.isArray(recommendationSnapshotSummary.value?.readiness_trend_cards)
    ? recommendationSnapshotSummary.value.readiness_trend_cards
    : []
  ).map(normalizeRecommendationSnapshotTrendCard)
);

const recommendationOwnerTrendCards = computed(() =>
  (Array.isArray(recommendationSnapshotSummary.value?.owner_trend_cards)
    ? recommendationSnapshotSummary.value.owner_trend_cards
    : []
  ).map(normalizeRecommendationSnapshotTrendCard)
);

const recommendationSnapshotTrendBucketWidth = (card, count) => {
  const history = Array.isArray(card?.history) ? card.history : [];
  const max = Math.max(...history.map(point => point.count), 0);
  if (!max) return '16%';
  return `${Math.max(16, Math.round((Number(count || 0) / max) * 100))}%`;
};

const recommendationSnapshotTrendBucketHeight = (card, count) => {
  const history = Array.isArray(card?.history) ? card.history : [];
  const max = Math.max(...history.map(point => point.count), 0);
  if (!max) return '12px';
  return `${Math.max(12, Math.round((Number(count || 0) / max) * 48))}px`;
};

const recommendationSnapshotTrendSummary = card => {
  const count = Number(card?.count || 0);
  const delta = Number(card?.delta || 0);
  const direction = String(card?.trendDirection || '').trim();

  if (direction === 'new') return `${count} now, new in latest snapshot`;
  if (direction === 'rising') return `${count} now (${delta > 0 ? '+' : ''}${delta})`;
  if (direction === 'cooling') return `${count} now (${delta})`;
  if (direction === 'steady') return `${count} steady`;
  return `${count} tracked`;
};

const normalizeRecommendationMetaCard = card => ({
  id: String(card?.id || '').trim(),
  label: String(card?.label || '').trim(),
  count: Number(card?.count || 0),
  totalHits: Number(card?.total_hits || 0),
  currentWeekHits: Number(card?.current_week_hits || 0),
  previousWeekHits: Number(card?.previous_week_hits || 0),
  weeklyDelta: Number(card?.weekly_delta || 0),
  trendDirection: String(card?.trend_direction || '').trim(),
  defaultSort: String(card?.default_sort || 'priority').trim() || 'priority',
  weeklyBuckets: (Array.isArray(card?.weekly_buckets) ? card.weekly_buckets : []).map(item => ({
    label: String(item?.label || '').trim(),
    count: Number(item?.count || 0),
  })),
});

const recommendationMetaCardTrendLabel = card => {
  const current = Number(card?.currentWeekHits || 0);
  const previous = Number(card?.previousWeekHits || 0);
  const delta = Number(card?.weeklyDelta || 0);
  if (current && !previous) return `+${current} this week`;
  if (!current && previous) return `${previous} last week`;
  if (!current && !previous) return 'No hits this week';
  if (!delta) return `${current} this week`;
  return `${current} this week (${delta > 0 ? '+' : ''}${delta})`;
};

const recommendationMetaCardBucketWidth = (card, count) => {
  const buckets = Array.isArray(card?.weeklyBuckets) ? card.weeklyBuckets : [];
  const max = Math.max(...buckets.map(item => item.count), 0);
  if (!max) return '12%';
  return `${Math.max(12, Math.round((Number(count || 0) / max) * 100))}%`;
};

const recommendationPriorityCounts = computed(() => ({
  high: Number(recommendationQueueMeta.value?.high_priority_count || 0),
  medium: Number(recommendationQueueMeta.value?.medium_priority_count || 0),
  low: Number(recommendationQueueMeta.value?.low_priority_count || 0),
}));

const recommendationQueueSummary = computed(() => {
  const { high, medium, low } = recommendationPriorityCounts.value;
  const parts = [];
  if (high) parts.push(`${high} high`);
  if (medium) parts.push(`${medium} medium`);
  if (low) parts.push(`${low} low`);
  return parts.join(' • ');
});

const recommendationPriorityFilters = [
  { id: 'all', label: 'All priorities' },
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
];

const recommendationWorkTypeFilters = [
  { id: 'all', label: 'All work' },
  { id: 'net_new_article', label: 'Net-new' },
  { id: 'recall_expansion', label: 'Recall' },
  { id: 'scope_fix', label: 'Scope' },
  { id: 'draft_followup', label: 'Draft follow-up' },
];

const recommendationDraftStateFilters = [
  { id: 'all', label: 'All draft states' },
  { id: 'not_started', label: 'No draft yet' },
  { id: 'draft_linked', label: 'Draft linked' },
  { id: 'published_linked', label: 'Published linked' },
];

const recommendationSortOptions = [
  { id: 'priority', label: 'Priority' },
  { id: 'variants', label: 'Variants' },
  { id: 'recent', label: 'Newest' },
  { id: 'persistence', label: 'Persistence' },
];

const recommendationQueueMeta = computed(
  () => gapRecommendationsMeta.value?.queue_summary || {}
);

const recommendationPresetOptions = computed(() => {
  const cards = Array.isArray(gapRecommendationsMeta.value?.preset_cards)
    ? gapRecommendationsMeta.value.preset_cards
    : [];
  const base = cards.map(normalizeRecommendationMetaCard);

  return [
    {
      id: 'all',
      label: 'All queue',
      count: Number(gapRecommendationsMeta.value?.total || gapRecommendations.value.length || 0),
      totalHits: Number(gapRecommendations.value.reduce((sum, row) => sum + Number(row?.total_count || 0), 0)),
      currentWeekHits: 0,
      previousWeekHits: 0,
      weeklyDelta: 0,
      trendDirection: 'steady',
      weeklyBuckets: [],
      defaultSort: 'priority',
    },
    ...base,
  ];
});

const recommendationFamilyCards = computed(() =>
  (Array.isArray(gapRecommendationsMeta.value?.family_cards) ? gapRecommendationsMeta.value.family_cards : [])
    .map(normalizeRecommendationMetaCard)
);

const recommendationReadinessCards = computed(() =>
  (Array.isArray(gapRecommendationsMeta.value?.readiness_cards)
    ? gapRecommendationsMeta.value.readiness_cards
    : []
  ).map(normalizeRecommendationMetaCard)
);

const recommendationFamilyConfidenceCards = computed(() =>
  (Array.isArray(gapRecommendationsMeta.value?.family_confidence_cards)
    ? gapRecommendationsMeta.value.family_confidence_cards
    : []
  ).map(normalizeRecommendationMetaCard)
);

const recommendationTrendCards = computed(() =>
  (Array.isArray(gapRecommendationsMeta.value?.trend_cards) ? gapRecommendationsMeta.value.trend_cards : [])
    .map(normalizeRecommendationMetaCard)
);

const recommendationFreshnessCards = computed(() =>
  (Array.isArray(gapRecommendationsMeta.value?.freshness_cards)
    ? gapRecommendationsMeta.value.freshness_cards
    : []
  ).map(normalizeRecommendationMetaCard)
);

const recommendationRoutingCards = computed(() =>
  (Array.isArray(gapRecommendationsMeta.value?.routing_cards) ? gapRecommendationsMeta.value.routing_cards : [])
    .map(normalizeRecommendationMetaCard)
);

const recommendationFamilyFilters = computed(() => [
  { id: 'all', label: 'All families', count: recommendationsCount.value },
  ...recommendationFamilyCards.value.map(card => ({
    id: card.id,
    label: card.label,
    count: card.count,
  })),
]);

const recommendationReadinessFilters = computed(() => [
  { id: 'all', label: 'All readiness', count: recommendationsCount.value },
  ...recommendationReadinessCards.value.map(card => ({
    id: card.id,
    label: card.label,
    count: card.count,
  })),
]);

const recommendationFamilyConfidenceFilters = computed(() => [
  { id: 'all', label: 'All family confidence', count: recommendationsCount.value },
  ...recommendationFamilyConfidenceCards.value.map(card => ({
    id: card.id,
    label: card.label,
    count: card.count,
  })),
]);

const recommendationTrendFilters = computed(() => [
  { id: 'all', label: 'All trends', count: recommendationsCount.value },
  ...recommendationTrendCards.value.map(card => ({
    id: card.id,
    label: card.label,
    count: card.count,
  })),
]);

const recommendationFreshnessFilters = computed(() => [
  { id: 'all', label: 'All freshness', count: recommendationsCount.value },
  ...recommendationFreshnessCards.value.map(card => ({
    id: card.id,
    label: card.label,
    count: card.count,
  })),
]);

const recommendationRoutingFilters = computed(() => [
  { id: 'all', label: 'All routes', count: recommendationsCount.value },
  ...recommendationRoutingCards.value.map(card => ({
    id: card.id,
    label: card.label,
    count: card.count,
  })),
]);

const recommendationSummaryCards = computed(() =>
  recommendationPresetOptions.value
    .filter(option => option.id !== 'all' && option.id !== 'monitor')
    .slice(0, 4)
    .map(option => ({
      key: option.id,
      label: option.label,
      value: option.count,
      presetId: option.id,
      defaultSort: option.defaultSort,
    }))
);

const activeRecommendationPresetLabel = computed(() => {
  const active = recommendationPresetOptions.value.find(
    option => option.id === recommendationPresetFilter.value
  );
  return active?.label || 'All queue';
});

const recommendationLaneSections = computed(() => {
  const activePreset = recommendationPresetFilter.value;
  const laneOptions = recommendationPresetOptions.value.filter(option => option.id !== 'all');
  const sections = laneOptions
    .filter(option => {
      if (activePreset !== 'all') return option.id === activePreset;
      if (option.id === 'monitor') return false;
      return option.count > 0 || gapRecommendations.value.some(row => row.queue_preset === option.id);
    })
    .map(option => ({
      ...option,
      rows: gapRecommendations.value.filter(row => row.queue_preset === option.id),
    }));

  if (sections.length || activePreset !== 'all') return sections;

  return laneOptions
    .filter(option => option.id === 'monitor')
    .map(option => ({
      ...option,
      rows: gapRecommendations.value.filter(row => row.queue_preset === option.id),
    }))
    .filter(section => section.count > 0 || section.rows.length);
});

const applyRecommendationPreset = presetId => {
  const nextPreset = String(presetId || 'all').trim() || 'all';
  recommendationPresetFilter.value = nextPreset;
  const presetConfig = recommendationPresetOptions.value.find(
    option => option.id === nextPreset
  );
  if (presetConfig?.defaultSort) {
    recommendationSort.value = presetConfig.defaultSort;
  }
};

const resetRecommendationFilters = () => {
  recommendationPriorityFilter.value = 'all';
  recommendationWorkTypeFilter.value = 'all';
  recommendationDraftStateFilter.value = 'all';
  recommendationPresetFilter.value = 'all';
  recommendationFamilyFilter.value = 'all';
  recommendationFamilyConfidenceFilter.value = 'all';
  recommendationReadinessFilter.value = 'all';
  recommendationTrendFilter.value = 'all';
  recommendationFreshnessFilter.value = 'all';
  recommendationRoutingFilter.value = 'all';
  recommendationSort.value = 'priority';
};

const applyRecommendationAlert = alert => {
  const action = alert?.recommendedAction || {};

  resetRecommendationFilters();
  activeRecommendationAlertKey.value = String(alert?.key || '').trim();
  activeRecommendationWorkflowAuditKey.value = String(alert?.key || '').trim();

  if (action.preset) {
    applyRecommendationPreset(action.preset);
  }
  if (action.readiness) {
    recommendationReadinessFilter.value = String(action.readiness).trim() || 'all';
  }
  if (action.routing) {
    recommendationRoutingFilter.value = String(action.routing).trim() || 'all';
  }
  if (action.sort) {
    recommendationSort.value = String(action.sort).trim() || 'priority';
  }

  scrollRecommendationsIntoView();
};

const clearRecommendationAlert = () => {
  activeRecommendationAlertKey.value = '';
};

const openRecommendationWorkflowAudit = workflow => {
  activeRecommendationWorkflowAuditKey.value = String(
    workflow?.workflowKey || workflow?.key || ''
  ).trim();
};

const closeRecommendationWorkflowAudit = () => {
  activeRecommendationWorkflowAuditKey.value = '';
};

const applyRecommendationAlertWorkflowActivityHighlightFilter = filterId => {
  recommendationAlertWorkflowActivityHighlightFilter.value =
    String(filterId || '').trim() || 'all';
};

const contentGapWeeklyBucketWidth = (row, count) => {
  const buckets = recommendationWeeklyBuckets(row);
  const max = Math.max(...buckets.map(item => item.count), 0);
  if (!max) return '8%';
  return `${Math.max(8, Math.round((Number(count || 0) / max) * 100))}%`;
};

const recommendationsCount = computed(() => {
  const total = Number(gapRecommendationsMeta.value?.total);
  return Number.isFinite(total) && total >= 0 ? total : gapRecommendations.value.length;
});

const activePreviewTab = ref('customer');
const previewEvents = ref([]);
let previewEventCounter = 0;
const isProductDialogOpen = ref(false);
const isCreatingProduct = ref(false);
const selectedProduct = ref(null);
const isProductSheetOpen = ref(false);
const isMediaSheetOpen = ref(false);
const isLinksSheetOpen = ref(false);
const isAnswerTemplatesSheetOpen = ref(false);
const newProductDraft = ref({
  name: '',
  series: '',
  description: '',
});

const previewAssistantName = computed(() => {
  const raw = props.assistant?.name?.toString().trim();
  return raw || 'Preview assistant';
});

const previewAssistantInitial = computed(() =>
  previewAssistantName.value.charAt(0).toUpperCase() || 'A'
);

const previewHeroMessage = computed(() => {
  if (articleRuntimeEnabled.value) {
    return 'Hi, let us know if you have any questions.';
  }

  if (connectedPortals.value.length > 1) {
    return 'Pin one Help Center portal to preview article-backed answers clearly.';
  }

  return 'Connect a Help Center portal to preview how article knowledge will appear.';
});

const previewAnnouncement = computed(() => {
  if (
    savedArticleScopeMode.value === 'selected_portal' &&
    configuredArticlePortal.value
  ) {
    return `Using Help Center: ${configuredArticlePortal.value.name}`;
  }

  if (previewPortal.value) {
    return `Following inbox portal: ${previewPortal.value.name}`;
  }

  if (connectedPortals.value.length > 1) {
    return 'Preview is static until one portal is pinned.';
  }

  return 'No article portal selected yet.';
});

const previewCustomerQuestion = computed(() => {
  if (articleRuntimeEnabled.value) {
    return 'What can you help me with right now?';
  }
  if (connectedPortals.value.length > 1) {
    return 'Which Help Center is this preview using?';
  }
  return 'Why are article-backed answers not showing here yet?';
});

const previewAssistantFollowup = computed(() => {
  if (articleRuntimeEnabled.value) {
    return previewAnnouncement.value;
  }
  if (connectedPortals.value.length > 1) {
    return 'Pin a single portal to make article-backed preview behavior deterministic.';
  }
  return 'Connect a Help Center portal and publish articles to unlock article-backed preview replies.';
});

const previewStatusLine = computed(() => {
  if (articleRuntimeEnabled.value) return 'Typically replies instantly';
  if (connectedPortals.value.length > 1) return 'Needs one pinned Help Center portal';
  return 'Static preview until article knowledge is connected';
});

const eventTimeLabel = () =>
  new Intl.DateTimeFormat('en', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(new Date());

const pushPreviewEvent = (title, detail, type = 'info') => {
  previewEventCounter += 1;
  previewEvents.value = [
    {
      id: `preview-event-${previewEventCounter}`,
      time: eventTimeLabel(),
      title,
      detail,
      type,
    },
    ...previewEvents.value,
  ].slice(0, 20);
};

const selectPreviewTab = tab => {
  activePreviewTab.value = tab;
  pushPreviewEvent(
    tab === 'customer' ? 'Customer view selected' : 'Event log selected',
    tab === 'customer'
      ? 'Switched back to the static AI preview.'
      : 'Opened preview event log timeline.'
  );
};

const getProductName = product => {
  return (
    product?.name || product?.product_name || product?.title || `#${product?.id || ''}`.trim()
  );
};

const getProductSeries = product => {
  return product?.series || product?.product_series || product?.product_line || '';
};

const getProductSummary = product => {
  return (
    product?.overview ||
    product?.description ||
    product?.product_description ||
    'Open to edit the product basics used by AI.'
  );
};

const contentSeedContext = computed(() => {
  const source = String(route.query?.content_seed_source || '').trim();
  const focus = String(route.query?.content_seed_focus || '').trim();
  const query = String(route.query?.content_seed_query || '').trim();
  const reason = String(route.query?.content_seed_reason || '').trim();
  const scopeWarning = String(route.query?.content_seed_scope_warning || '').trim();
  const enabled =
    String(route.query?.content_seed || '').trim() === '1' ||
    [source, focus, query, reason, scopeWarning].some(Boolean);

  return {
    enabled,
    source,
    focus,
    query,
    reason,
    scopeWarning,
  };
});
const contentSeedSummary = computed(() => {
  if (!contentSeedContext.value.enabled) return '';

  const parts = [];
  if (contentSeedContext.value.source) parts.push(`from ${contentSeedContext.value.source}`);
  if (contentSeedContext.value.focus === 'retrieval') parts.push('retrieval strategy');
  if (contentSeedContext.value.focus === 'recommendations') parts.push('internal improvement queue');
  if (contentSeedContext.value.reason) parts.push(contentSeedContext.value.reason);
  if (contentSeedContext.value.scopeWarning) parts.push('scope warning');
  return parts.join(' • ');
});
const contentSeedPrimaryActionLabel = computed(() =>
  contentSeedContext.value.focus === 'retrieval'
    ? 'Open retrieval strategy'
    : 'Open internal queue'
);

const setContentPanelQuery = panel => {
  const nextQuery = { ...route.query, tab: 'train_content' };
  if (panel) {
    nextQuery.contentPanel = panel;
  } else {
    delete nextQuery.contentPanel;
  }
  router.replace({ query: nextQuery });
};

const onOpenLegacy = (source, target = 'product') => {
  const targetLabel =
    target === 'answer_templates'
      ? 'legacy answer template editor'
      : target === 'links'
        ? 'legacy website sync editor'
        : target === 'media'
          ? 'legacy media library'
        : 'legacy product and sync editor';
  pushPreviewEvent(
    'Compatibility editor opened',
    `${source} redirected to the ${targetLabel}.`
  );
  emit('openLegacy', target);
};

const openCreateProductDialog = () => {
  newProductDraft.value = {
    name: '',
    series: '',
    description: '',
  };
  isProductDialogOpen.value = true;
};

const closeCreateProductDialog = () => {
  if (isCreatingProduct.value) return;
  isProductDialogOpen.value = false;
};

const openProductSheet = product => {
  selectedProduct.value = product;
  isProductSheetOpen.value = true;
  pushPreviewEvent(
    'Product basics opened',
    `${getProductName(product)} opened inside Train > Content.`
  );
};

const closeProductSheet = () => {
  if (!isProductSheetOpen.value) return;
  isProductSheetOpen.value = false;
  selectedProduct.value = null;
  pushPreviewEvent('Product basics closed', 'Dismissed product basics editor.');
};

const openMediaSheet = () => {
  isMediaSheetOpen.value = true;
  setContentPanelQuery('media');
  pushPreviewEvent('Media library opened', 'Opened uploaded documents inside Train > Content.');
};

const closeMediaSheet = () => {
  if (!isMediaSheetOpen.value) return;
  isMediaSheetOpen.value = false;
  if (route.query.contentPanel === 'media') setContentPanelQuery(null);
  pushPreviewEvent('Media library closed', 'Dismissed uploaded documents editor.');
};

const openLinksSheet = () => {
  isLinksSheetOpen.value = true;
  setContentPanelQuery('links');
  pushPreviewEvent('Website sync opened', 'Opened synced links inside Train > Content.');
};

const closeLinksSheet = () => {
  if (!isLinksSheetOpen.value) return;
  isLinksSheetOpen.value = false;
  if (route.query.contentPanel === 'links') setContentPanelQuery(null);
  pushPreviewEvent('Website sync closed', 'Dismissed synced links editor.');
};

const openAnswerTemplatesSheet = () => {
  isAnswerTemplatesSheetOpen.value = true;
  setContentPanelQuery('answer_templates');
  pushPreviewEvent(
    'Answer templates opened',
    'Opened legacy snippets inside Train > Content.'
  );
};

const closeAnswerTemplatesSheet = () => {
  if (!isAnswerTemplatesSheetOpen.value) return;
  isAnswerTemplatesSheetOpen.value = false;
  if (route.query.contentPanel === 'answer_templates') setContentPanelQuery(null);
  pushPreviewEvent(
    'Answer templates closed',
    'Dismissed legacy snippets editor.'
  );
};

const createProduct = async () => {
  if (!assistantId.value) return;
  if (!newProductDraft.value.name?.trim()) {
    alert('Product name is required.');
    return;
  }

  isCreatingProduct.value = true;
  try {
    const created = await store.dispatch('aiCloser/createProduct', {
      assistantId: assistantId.value,
      data: {
        name: newProductDraft.value.name.trim(),
        series: newProductDraft.value.series?.trim() || '',
        description: newProductDraft.value.description?.trim() || '',
      },
    });
    await store.dispatch('aiCloser/fetchProducts', {
      assistantId: assistantId.value,
    });
    isProductDialogOpen.value = false;
    alert('Product created.');
    pushPreviewEvent(
      'Product created',
      `${getProductName(created)} is now available in Train > Content.`,
      'success'
    );
    openProductSheet(created);
  } catch (error) {
    const message = error?.message || error?.response?.data?.error || 'Failed to create product.';
    alert(message);
    pushPreviewEvent('Product create failed', String(message), 'error');
  } finally {
    isCreatingProduct.value = false;
  }
};

const handleManageContentSource = source => {
  if (source.manageTarget === 'media') {
    openMediaSheet();
    return;
  }
  if (source.manageTarget === 'links') {
    openLinksSheet();
    return;
  }
  if (source.manageTarget === 'answer_templates') {
    openAnswerTemplatesSheet();
    return;
  }
  onOpenLegacy(source.title, source.manageTarget);
};

const onOpenArticleSheet = type => {
  activeArticleType.value = type;
  isArticleSheetOpen.value = true;
  pushPreviewEvent(
    `${type === 'internal' ? 'Internal' : 'Public'} article opened`,
    'Opened article side sheet editor.'
  );
};

const onCloseArticleSheet = () => {
  isArticleSheetOpen.value = false;
  pushPreviewEvent('Article editor closed', 'Dismissed article side sheet.');
};

const onSaveArticleDraft = payload => {
  saveArticle(payload, 'draft');
};

const onPublishArticle = payload => {
  saveArticle(payload, 'published');
};

const onOpenQuickPreview = source => {
  pushPreviewEvent(
    'Quick preview opened',
    `${source} redirected to the full preview experience.`
  );
  emit('openPreview');
};

const onPreviewSettingsClick = () => {
  pushPreviewEvent(
    'Preview settings opened',
    'Static preview settings are mirrored in the full Quick Preview workspace.'
  );
  onOpenQuickPreview('Preview settings');
};

const onPreviewCloseClick = () => {
  pushPreviewEvent(
    'Preview panel closed',
    'Redirected to the full Quick Preview tab.'
  );
  emit('openPreview');
};

onMounted(() => {
  pushPreviewEvent(
    'Preview panel initialized',
    'Static Fin-style AI preview is ready for testing as Preview user.'
  );
});

watch(
  () => route.query.contentPanel,
  panel => {
    isMediaSheetOpen.value = panel === 'media';
    isLinksSheetOpen.value = panel === 'links';
    isAnswerTemplatesSheetOpen.value = panel === 'answer_templates';
  },
  { immediate: true }
);

watch(
  () => [
    route.query?.content_seed,
    route.query?.content_seed_source,
    route.query?.content_seed_focus,
    route.query?.content_seed_query,
    route.query?.content_seed_reason,
    route.query?.content_seed_scope_warning,
  ],
  () => {
    focusSeededContentTarget();
  },
  { immediate: true }
);

const fetchContentSources = async () => {
  if (!assistantId.value) return;

  isLoadingContentSources.value = true;
  try {
    const requests = [
      store.dispatch('aiCloser/fetchProducts', {
        assistantId: assistantId.value,
      }),
      store.dispatch('aiCloser/fetchAnswerTemplates', {
        assistantId: assistantId.value,
        params: { limit: 1, offset: 0, enabled: true },
      }),
      store.dispatch('aiCloser/fetchMedia', {
        assistantId: assistantId.value,
        params: { limit: 1, offset: 0 },
      }),
      store.dispatch('aiCloser/fetchLinks', {
        assistantId: assistantId.value,
        params: { limit: 1, offset: 0 },
      }),
    ];

    if (defaultPortalSlug.value) {
      requests.push(
        store.dispatch('articles/index', {
          portalSlug: defaultPortalSlug.value,
          locale: defaultPortalLocale.value,
        })
      );
    }

    await Promise.allSettled(requests);
  } finally {
    isLoadingContentSources.value = false;
  }
};

const fetchGapRecommendations = async () => {
  if (!assistantId.value || !accountId.value) {
    gapRecommendations.value = [];
    gapRecommendationsMeta.value = {};
    return;
  }

  isLoadingRecommendations.value = true;
  try {
    const res = await getWootAxios().get(
      `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags`,
      {
        params: {
          assistant_id: assistantId.value,
          status: 'open',
          aggregate: 'content_gap_topics',
          limit: 10,
          weeks: CONTENT_GAP_RECOMMENDATION_WEEKS,
          ...(recommendationPriorityFilter.value !== 'all'
            ? { priority_band: recommendationPriorityFilter.value }
            : {}),
          ...(recommendationWorkTypeFilter.value !== 'all'
            ? { work_type: recommendationWorkTypeFilter.value }
            : {}),
          ...(recommendationDraftStateFilter.value !== 'all'
            ? { draft_state: recommendationDraftStateFilter.value }
            : {}),
          ...(recommendationPresetFilter.value !== 'all'
            ? { preset: recommendationPresetFilter.value }
            : {}),
          ...(recommendationFamilyFilter.value !== 'all'
            ? { family: recommendationFamilyFilter.value }
            : {}),
          ...(recommendationFamilyConfidenceFilter.value !== 'all'
            ? { family_confidence: recommendationFamilyConfidenceFilter.value }
            : {}),
          ...(recommendationReadinessFilter.value !== 'all'
            ? { readiness: recommendationReadinessFilter.value }
            : {}),
          ...(recommendationTrendFilter.value !== 'all'
            ? { trend: recommendationTrendFilter.value }
            : {}),
          ...(recommendationFreshnessFilter.value !== 'all'
            ? { freshness: recommendationFreshnessFilter.value }
            : {}),
          ...(recommendationRoutingFilter.value !== 'all'
            ? { routing: recommendationRoutingFilter.value }
            : {}),
          sort: recommendationSort.value,
        },
      }
    );
    gapRecommendations.value = Array.isArray(res?.data?.data) ? res.data.data : [];
    gapRecommendationsMeta.value = res?.data?.meta || {};
    if (
      !recommendationSnapshotSummary.value?.latest_snapshot_at &&
      !hasAttemptedRecommendationSnapshot.value
    ) {
      hasAttemptedRecommendationSnapshot.value = true;
      await captureRecommendationSnapshot();
    }
  } catch {
    gapRecommendations.value = [];
    gapRecommendationsMeta.value = {};
  } finally {
    isLoadingRecommendations.value = false;
  }
};

const captureRecommendationSnapshot = async () => {
  if (!assistantId.value || !accountId.value || isCapturingRecommendationSnapshot.value) return;

  isCapturingRecommendationSnapshot.value = true;
  try {
    const res = await getWootAxios().post(
      `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags/capture_content_gap_snapshot`,
      {
        assistant_id: assistantId.value,
        status: 'open',
        weeks: CONTENT_GAP_RECOMMENDATION_WEEKS,
      }
    );
    const snapshotSummary = res?.data?.snapshot_summary || {};
    gapRecommendationsMeta.value = {
      ...gapRecommendationsMeta.value,
      snapshot_summary: snapshotSummary,
    };
  } catch {
    // Ignore snapshot capture failures so queue browsing still works.
  } finally {
    isCapturingRecommendationSnapshot.value = false;
  }
};

const scrollRecommendationsIntoView = () => {
  recommendationsSectionRef.value?.scrollIntoView?.({
    behavior: 'smooth',
    block: 'start',
  });
};

const scrollRetrievalStrategyIntoView = () => {
  retrievalStrategySectionRef.value?.scrollIntoView?.({
    behavior: 'smooth',
    block: 'start',
  });
};

const openTestWorkspaceWithSeed = ({ queryText = '', reason = '', source = '' } = {}) => {
  const nextQuery = { ...(route.query || {}), tab: 'test_preview' };
  CONTENT_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  delete nextQuery.guidance_seed;
  delete nextQuery.guidance_seed_source;
  delete nextQuery.guidance_seed_editor;
  delete nextQuery.guidance_seed_reason;
  delete nextQuery.guidance_seed_query;
  delete nextQuery.deploy_seed;
  delete nextQuery.deploy_seed_source;
  delete nextQuery.deploy_seed_focus;
  delete nextQuery.deploy_seed_reason;
  delete nextQuery.deploy_seed_inbox_id;
  delete nextQuery.escalation_seed;
  delete nextQuery.escalation_seed_source;
  delete nextQuery.escalation_seed_focus;
  delete nextQuery.escalation_seed_reason;
  delete nextQuery.escalation_seed_query;
  delete nextQuery.escalation_seed_label;
  delete nextQuery.analyze_seed;
  delete nextQuery.analyze_source;
  delete nextQuery.analyze_time_window;
  delete nextQuery.analyze_inbox_id;
  delete nextQuery.analyze_content_gap_reason;
  delete nextQuery.analyze_article_scope_warning;
  delete nextQuery.analyze_delivery_mode;
  TEST_COMPARE_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });

  const normalizedQuery = String(queryText || '').trim();
  const normalizedReason = String(reason || '').trim();
  const normalizedSource = String(source || '').trim();

  if (normalizedQuery) nextQuery.test_query = normalizedQuery;
  else delete nextQuery.test_query;

  if (normalizedReason) nextQuery.test_reason = normalizedReason;
  else delete nextQuery.test_reason;

  if (normalizedSource) nextQuery.test_source = normalizedSource;
  else delete nextQuery.test_source;

  router.replace({ query: nextQuery });
};

async function focusSeededContentTarget() {
  if (!contentSeedContext.value.enabled) return;
  await nextTick();

  if (
    contentSeedContext.value.focus === 'retrieval' ||
    contentSeedContext.value.scopeWarning
  ) {
    scrollRetrievalStrategyIntoView();
    return;
  }

  if (recommendationsSectionRef.value) {
    recommendationsSectionRef.value.open = true;
  }
  scrollRecommendationsIntoView();
}

const clearContentSeed = async () => {
  const nextQuery = { ...(route.query || {}) };
  CONTENT_SEED_QUERY_KEYS.forEach(key => {
    delete nextQuery[key];
  });
  await router.replace({ query: nextQuery });
};

const retestSeededContentContext = () => {
  if (!contentSeedContext.value.query) return;
  openTestWorkspaceWithSeed({
    queryText: contentSeedContext.value.query,
    reason: contentSeedContext.value.reason || contentSeedContext.value.scopeWarning || 'content_seed',
    source: 'Content',
  });
};

const recommendationSampleQuery = recommendation =>
  String(
    recommendation?.query_preview ||
      (Array.isArray(recommendation?.sample_queries) ? recommendation.sample_queries[0] : '') ||
      recommendation?.topic ||
      ''
  ).trim();

const openRecommendationInTest = recommendation => {
  const sampleQuery = recommendationSampleQuery(recommendation);
  if (!sampleQuery) return;

  openTestWorkspaceWithSeed({
    queryText: sampleQuery,
    reason: String(recommendation?.reason || 'content_gap').trim(),
    source: 'Content',
  });
};

const createGapArticleDraft = async recommendation => {
  const tagId = Number(recommendation?.sample_tag_id || 0);
  if (!tagId || !accountId.value) return;

  creatingGapDraftTagId.value = tagId;
  try {
    const res = await getWootAxios().post(
      `/api/v1/accounts/${accountId.value}/ailab/ai_failure_tags/${tagId}/create_article_draft`
    );
    const route = res?.data?.article_route || {};
    const articleId = res?.data?.created_article_id;

    alert(
      articleId
        ? `Draft article created (#${articleId}).`
        : 'Draft article created.'
    );
    await Promise.allSettled([fetchGapRecommendations(), fetchContentSources()]);

    if (route.portal_slug && route.locale && route.article_slug) {
      router.push(
        accountScopedRoute('portals_articles_edit', {
          portalSlug: route.portal_slug,
          locale: route.locale,
          articleSlug: route.article_slug,
        })
      );
    }
  } catch (error) {
    alert(
      error?.response?.data?.error ||
        error?.message ||
        'Failed to create draft article.'
    );
  } finally {
    creatingGapDraftTagId.value = null;
  }
};

const ensurePortalReady = async () => {
  if (defaultPortalSlug.value) return true;

  if (connectedPortalIds.value.length > 1) {
    alert(
      'This assistant is connected to multiple portals. Open a preview inbox for the target portal or pin article scope to one portal first.'
    );
    return false;
  }

  await store.dispatch('portals/index');
  return Boolean(defaultPortalSlug.value);
};

const saveArticle = async (payload, status) => {
  isSavingArticle.value = true;
  savingArticleAction.value = status === 'published' ? 'publish' : 'draft';
  try {
    const hasPortal = await ensurePortalReady();
    if (!hasPortal) {
      alert('No help center portal is available for article creation.');
      pushPreviewEvent(
        'Article save blocked',
        'No help center portal is available for this account.',
        'error'
      );
      return;
    }

    const articleId = await store.dispatch('articles/create', {
      portalSlug: defaultPortalSlug.value,
      content: payload.content || ' ',
      title:
        payload.title ||
        (payload.type === 'internal'
          ? 'Untitled internal article'
          : 'Untitled public article'),
      locale: defaultPortalLocale.value,
      authorId: currentUserId.value,
      categoryId: null,
      description: payload.summary || '',
      status,
      visibility: payload.type === 'internal' ? 'internal_article' : 'public_article',
    });

    if (!articleId) {
      throw new Error('Article create request did not return an id');
    }

    const actionLabel = status === 'published' ? 'published' : 'saved as draft';
    alert(
      `${payload.type === 'internal' ? 'Internal' : 'Public'} article ${actionLabel} to Help Center. Published articles are available to AI when this assistant resolves to the same portal at runtime.`
    );
    pushPreviewEvent(
      status === 'published' ? 'Article published' : 'Article draft saved',
      `${payload.type} article ${actionLabel}${payload.title ? `: ${payload.title}` : '.'}`,
      'success'
    );
    pushPreviewEvent(
      'Article runtime boundary',
      'Published Help Center articles are injected into AI only when the assistant resolves to the same portal scope at runtime.'
    );
    isArticleSheetOpen.value = false;
    router.push(
      accountScopedRoute('portals_articles_edit', {
        portalSlug: defaultPortalSlug.value,
        locale: defaultPortalLocale.value,
        articleSlug: articleId,
      })
    );
  } catch (error) {
    const message =
      error?.message ||
      error?.response?.data?.error ||
      'Failed to save article.';
    alert(message);
    pushPreviewEvent('Article save failed', String(message), 'error');
  } finally {
    isSavingArticle.value = false;
    savingArticleAction.value = '';
  }
};

const persistAssistantConfig = async nextConfig => {
  await store.dispatch('ailabAssistants/update', {
    id: assistantId.value,
    assistant: { config: nextConfig },
  });
  await store.dispatch('ailabAssistants/show', assistantId.value);
  return true;
};

const buildNextThresholdOverrideConfig = (profile, entry) => {
  const nextConfig = cloneJson(props.assistant?.config || {});
  const nextRoot = cloneJson(nextConfig.content_gap_threshold_profiles || {});
  const nextCategory = cloneJson(nextRoot?.[profile?.category] || {});
  const normalizedEntry = normalizeRecommendationThresholdOverrideEntry(entry);

  if (Object.keys(normalizedEntry).length) {
    nextCategory[profile.id] = normalizedEntry;
  } else {
    delete nextCategory[profile.id];
  }

  if (Object.keys(nextCategory).length) {
    nextRoot[profile.category] = nextCategory;
  } else {
    delete nextRoot[profile.category];
  }

  if (Object.keys(nextRoot).length) {
    nextConfig.content_gap_threshold_profiles = nextRoot;
  } else {
    delete nextConfig.content_gap_threshold_profiles;
  }

  return nextConfig;
};

const syncRecommendationThresholdOverrideDrafts = () => {
  const nextDrafts = {};

  recommendationThresholdProfiles.value.forEach(profile => {
    const current = currentRecommendationThresholdOverride(profile);
    nextDrafts[profile.key] = {
      label: String(current.label || ''),
      rationale: String(current.rationale || ''),
      thresholdValue:
        current.threshold_value != null ? String(current.threshold_value) : '',
      watchDelta: current.watch_delta != null ? String(current.watch_delta) : '',
    };
  });

  recommendationThresholdOverrideDrafts.value = nextDrafts;
};

const saveRecommendationThresholdOverride = async profile => {
  if (!assistantId.value || !profile?.key) return false;
  if (!recommendationThresholdOverrideDirty(profile)) return false;

  savingRecommendationThresholdProfileKey.value = profile.key;
  try {
    const nextConfig = buildNextThresholdOverrideConfig(
      profile,
      recommendationThresholdOverrideDraft(profile)
    );
    await persistAssistantConfig(nextConfig);
    await fetchGapRecommendations();
    alert('Threshold override saved.');
    return true;
  } catch (error) {
    alert(error?.message || 'Failed to save threshold override.');
    return false;
  } finally {
    savingRecommendationThresholdProfileKey.value = '';
  }
};

const resetRecommendationThresholdOverride = async profile => {
  if (!profile?.key) return false;

  if (!hasPersistedRecommendationThresholdOverride(profile)) {
    recommendationThresholdOverrideDrafts.value = {
      ...recommendationThresholdOverrideDrafts.value,
      [profile.key]: {
        label: '',
        rationale: '',
        thresholdValue: '',
        watchDelta: '',
      },
    };
    return true;
  }

  savingRecommendationThresholdProfileKey.value = profile.key;
  try {
    const nextConfig = buildNextThresholdOverrideConfig(profile, {});
    await persistAssistantConfig(nextConfig);
    await fetchGapRecommendations();
    alert('Threshold override reset to defaults.');
    return true;
  } catch (error) {
    alert(error?.message || 'Failed to reset threshold override.');
    return false;
  } finally {
    savingRecommendationThresholdProfileKey.value = '';
  }
};

const recommendationAlertWorkflowDraft = alertItem =>
  recommendationAlertWorkflowDrafts.value?.[alertItem?.key] || {
    ownerLabel: '',
    dueDate: '',
    resolutionOutcome: '',
    handoffReason: '',
    nextCheckpoint: '',
    note: '',
  };

const emptyRecommendationAlertWorkflowBatchMetadataDraft = () => ({
  applyOwnerLabel: false,
  ownerLabel: '',
  applyDueDate: false,
  dueDate: '',
  applyResolutionOutcome: false,
  resolutionOutcome: '',
  applyHandoffReason: false,
  handoffReason: '',
  applyNextCheckpoint: false,
  nextCheckpoint: '',
  applyNote: false,
  note: '',
});

const recommendationDateOffsetLabel = days => {
  const target = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  const year = target.getUTCFullYear();
  const month = String(target.getUTCMonth() + 1).padStart(2, '0');
  const date = String(target.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${date}`;
};

const recommendationAlertWorkflowBatchMetadataPresetDefinitions = computed(() => [
  {
    id: 'kb_followup',
    label: 'KB follow-up',
    description: 'Assign KB owner and queue a short content follow-up.',
    buildDraft: () => ({
      applyOwnerLabel: true,
      ownerLabel: 'Knowledge owner',
      applyDueDate: true,
      dueDate: recommendationDateOffsetLabel(3),
      applyResolutionOutcome: false,
      resolutionOutcome: '',
      applyHandoffReason: false,
      handoffReason: '',
      applyNextCheckpoint: true,
      nextCheckpoint: 'Review article coverage and publish follow-up update.',
      applyNote: true,
      note: 'Batch preset applied for knowledge-base follow-up.',
    }),
  },
  {
    id: 'ops_handoff',
    label: 'Ops handoff',
    description: 'Prepare an ops-facing handoff with policy review context.',
    buildDraft: () => ({
      applyOwnerLabel: true,
      ownerLabel: 'Ops editor',
      applyDueDate: true,
      dueDate: recommendationDateOffsetLabel(2),
      applyResolutionOutcome: false,
      resolutionOutcome: '',
      applyHandoffReason: true,
      handoffReason: 'Policy review required',
      applyNextCheckpoint: true,
      nextCheckpoint: 'Ops owner to confirm policy update and reply copy.',
      applyNote: true,
      note: 'Batch preset applied for ops policy handoff.',
    }),
  },
  {
    id: 'publish_validation',
    label: 'Publish validation',
    description: 'Log publish outcome and schedule recall verification.',
    buildDraft: () => ({
      applyOwnerLabel: false,
      ownerLabel: '',
      applyDueDate: true,
      dueDate: recommendationDateOffsetLabel(5),
      applyResolutionOutcome: true,
      resolutionOutcome: 'published_article',
      applyHandoffReason: false,
      handoffReason: '',
      applyNextCheckpoint: true,
      nextCheckpoint: 'Verify article recall after the next snapshot capture.',
      applyNote: true,
      note: 'Batch preset applied for publish validation.',
    }),
  },
]);

const recommendationAlertWorkflowBatchPresetSelectionSignals = computed(() => {
  const sessions = selectedRecommendationAlertWorkflowSessions.value;
  const total = sessions.length;
  if (!total) {
    return {
      total: 0,
      majorityThreshold: 0,
      handoffCount: 0,
      resolvedCount: 0,
      followupCount: 0,
    };
  }

  const majorityThreshold = Math.max(1, Math.ceil(total / 2));
  const handoffCount = sessions.filter(
    item =>
      item?.workflowStatus === 'handoff' ||
      item?.workflowSessionState === 'handoff' ||
      String(item?.workflowHandoffReason || '').trim()
  ).length;
  const resolvedCount = sessions.filter(
    item =>
      item?.workflowStatus === 'resolved' ||
      item?.workflowSessionState === 'resolved' ||
      String(item?.workflowResolutionOutcome || '').trim() === 'published_article'
  ).length;
  const followupCount = sessions.filter(
    item =>
      item?.workflowNeedsFollowup ||
      item?.workflowSessionState === 'snoozed' ||
      item?.workflowSessionState === 'reopened'
  ).length;

  return {
    total,
    majorityThreshold,
    handoffCount,
    resolvedCount,
    followupCount,
  };
});

const recommendationAlertWorkflowBatchPresetConfidence = (matchingCount, totalCount) => {
  if (!totalCount) return 'low';
  const ratio = matchingCount / totalCount;
  if (ratio >= 0.75) return 'high';
  if (ratio >= 0.5) return 'medium';
  return 'low';
};

const recommendationAlertWorkflowBatchPresetRecommendationOptions = computed(() => {
  const signals = recommendationAlertWorkflowBatchPresetSelectionSignals.value;
  const { total, majorityThreshold, handoffCount, resolvedCount, followupCount } = signals;
  if (!total) return [];

  let recommendedPresetId = 'kb_followup';
  if (handoffCount >= majorityThreshold && handoffCount >= resolvedCount) {
    recommendedPresetId = 'ops_handoff';
  } else if (resolvedCount >= majorityThreshold) {
    recommendedPresetId = 'publish_validation';
  }

  const presetIds = ['ops_handoff', 'publish_validation', 'kb_followup'];
  return presetIds
    .map(presetId => {
      const preset =
        recommendationAlertWorkflowBatchMetadataPresetDefinitions.value.find(
          item => item.id === presetId
        ) || {};

      let matchingCount = followupCount || total;
      let supportingLabel = 'Follow-up signals';
      let reason = `${followupCount || total} of ${total} selected sessions still need active content follow-up.`;
      let whyNotReason = `Only ${followupCount || total} of ${total} selected sessions still look follow-up heavy, so a different template may fit better.`;

      if (presetId === 'ops_handoff') {
        matchingCount = handoffCount;
        supportingLabel = 'Handoff signals';
        reason = `${handoffCount} of ${total} selected sessions are already handed off or carry handoff signals.`;
        whyNotReason = `Only ${handoffCount} of ${total} selected sessions show strong handoff signals, so this template did not outrank the current recommendation.`;
      } else if (presetId === 'publish_validation') {
        matchingCount = resolvedCount;
        supportingLabel = 'Publish signals';
        reason = `${resolvedCount} of ${total} selected sessions are already resolved or publish-oriented.`;
        whyNotReason = `Only ${resolvedCount} of ${total} selected sessions look publish-oriented, so this template did not win the current selection.`;
      }

      return {
        presetId,
        presetLabel: String(preset.label || presetId).trim(),
        presetDescription: String(preset.description || '').trim(),
        presetDraft: preset.buildDraft ? preset.buildDraft() : {},
        matchingCount,
        totalCount: total,
        supportingLabel,
        confidence: recommendationAlertWorkflowBatchPresetConfidence(matchingCount, total),
        isRecommended: presetId === recommendedPresetId,
        reason,
        whyNotReason,
        signalCounts: {
          handoffCount,
          resolvedCount,
          followupCount,
        },
      };
    })
    .sort((left, right) => {
      if (left.isRecommended !== right.isRecommended) return left.isRecommended ? -1 : 1;
      if (left.matchingCount !== right.matchingCount) return right.matchingCount - left.matchingCount;
      return String(left.presetLabel || '').localeCompare(String(right.presetLabel || ''));
    });
});

const recommendationSuggestedAlertWorkflowBatchPreset = computed(() => {
  const suggestion =
    recommendationAlertWorkflowBatchPresetRecommendationOptions.value.find(
      item => item.isRecommended
    ) || null;
  if (!suggestion) return null;

  return suggestion;
});

const recommendationAlertWorkflowBatchMetadataFields = [
  {
    applyKey: 'applyOwnerLabel',
    valueKey: 'ownerLabel',
    metadataKey: 'owner_label',
    label: 'Owner',
    repairPriority: 1,
  },
  {
    applyKey: 'applyDueDate',
    valueKey: 'dueDate',
    metadataKey: 'due_date',
    label: 'Due date',
    repairPriority: 2,
  },
  {
    applyKey: 'applyResolutionOutcome',
    valueKey: 'resolutionOutcome',
    metadataKey: 'resolution_outcome',
    label: 'Resolution',
    repairPriority: 3,
  },
  {
    applyKey: 'applyHandoffReason',
    valueKey: 'handoffReason',
    metadataKey: 'handoff_reason',
    label: 'Handoff reason',
    repairPriority: 3,
  },
  {
    applyKey: 'applyNextCheckpoint',
    valueKey: 'nextCheckpoint',
    metadataKey: 'next_checkpoint',
    label: 'Next checkpoint',
    repairPriority: 4,
  },
  {
    applyKey: 'applyNote',
    valueKey: 'note',
    metadataKey: 'note',
    label: 'Note',
    repairPriority: 5,
  },
];

const currentRecommendationAlertWorkflowMetadata = alertItem => ({
  owner_label: String(
    alertItem?.workflowOwnerLabel ||
      alertItem?.workflowState?.owner_label ||
      alertItem?.workflowState?.handoff_target ||
      ''
  ).trim(),
  due_date: String(
    alertItem?.workflowDueAt || alertItem?.workflowState?.due_at || ''
  )
    .trim()
    .slice(0, 10),
  resolution_outcome: String(
    alertItem?.workflowResolutionOutcome ||
      alertItem?.workflowState?.resolution_outcome ||
      ''
  ).trim(),
  handoff_reason: String(
    alertItem?.workflowHandoffReason ||
      alertItem?.workflowState?.handoff_reason ||
      ''
  ).trim(),
  next_checkpoint: String(
    alertItem?.workflowNextCheckpoint ||
      alertItem?.workflowState?.next_checkpoint ||
      ''
  ).trim(),
  note: String(
    alertItem?.workflowNote || alertItem?.workflowState?.note || ''
  ).trim(),
});

const normalizeRecommendationAlertWorkflowMetadataDraft = draft => {
  const normalized = {};
  const ownerLabel = String(draft?.ownerLabel || '').trim();
  const dueDate = String(draft?.dueDate || '').trim();
  const resolutionOutcome = String(draft?.resolutionOutcome || '').trim();
  const handoffReason = String(draft?.handoffReason || '').trim();
  const nextCheckpoint = String(draft?.nextCheckpoint || '').trim();
  const note = String(draft?.note || '').trim();

  if (ownerLabel) normalized.owner_label = ownerLabel;
  if (dueDate) normalized.due_date = dueDate;
  if (resolutionOutcome) normalized.resolution_outcome = resolutionOutcome;
  if (handoffReason) normalized.handoff_reason = handoffReason;
  if (nextCheckpoint) normalized.next_checkpoint = nextCheckpoint;
  if (note) normalized.note = note;

  return normalized;
};

const recommendationAlertWorkflowBatchMetadataInstructions = computed(() =>
  recommendationAlertWorkflowBatchMetadataFields
    .filter(field => recommendationAlertWorkflowBatchMetadataDraft.value?.[field.applyKey])
    .map(field => ({
      metadataKey: field.metadataKey,
      value: String(
        recommendationAlertWorkflowBatchMetadataDraft.value?.[field.valueKey] || ''
      ).trim(),
    }))
);

const recommendationAlertWorkflowBatchMetadataDirty = computed(() => {
  if (!selectedRecommendationAlertWorkflowSessions.value.length) return false;
  if (!recommendationAlertWorkflowBatchMetadataInstructions.value.length) return false;

  return selectedRecommendationAlertWorkflowSessions.value.some(alertItem => {
    const currentMetadata = currentRecommendationAlertWorkflowMetadata(alertItem);

    return recommendationAlertWorkflowBatchMetadataInstructions.value.some(
      ({ metadataKey, value }) => String(currentMetadata?.[metadataKey] || '').trim() !== value
    );
  });
});

const recommendationAlertWorkflowMetadataDirty = alertItem => {
  const current = JSON.stringify(currentRecommendationAlertWorkflowMetadata(alertItem));
  const draft = JSON.stringify(
    normalizeRecommendationAlertWorkflowMetadataDraft(
      recommendationAlertWorkflowDraft(alertItem)
    )
  );

  return current !== draft;
};

const recommendationAlertWorkflowBatchMetadataPresetById = presetId =>
  recommendationAlertWorkflowBatchMetadataPresetDefinitions.value.find(
    item => item.id === String(presetId || '').trim()
  ) || null;

const recommendationAlertWorkflowBatchMetadataFieldByValueKey = valueKey =>
  recommendationAlertWorkflowBatchMetadataFields.find(
    item => item.valueKey === String(valueKey || '').trim()
  ) || null;

const activeRecommendationSuggestedAlertWorkflowBatchPreset = computed(() =>
  recommendationAlertWorkflowBatchMetadataPresetById(
    recommendationSuggestedAlertWorkflowBatchPreset.value?.presetId
  )
);

const recommendationAlternativeAlertWorkflowBatchPresets = computed(() => {
  const suggestion = recommendationSuggestedAlertWorkflowBatchPreset.value;
  if (!suggestion) return [];

  return recommendationAlertWorkflowBatchPresetRecommendationOptions.value
    .filter(item => !item.isRecommended)
    .map(item => ({
      ...item,
      ...buildRecommendationAlertWorkflowBatchPresetComparison(suggestion, item),
      isPreviewing:
        activeRecommendationAlertWorkflowBatchComparisonPresetId.value === item.presetId,
    }));
});

const activeRecommendationAlternativeAlertWorkflowBatchPreset = computed(() => {
  const alternatives = recommendationAlternativeAlertWorkflowBatchPresets.value;
  if (!alternatives.length) return null;

  return (
    alternatives.find(
      item => item.presetId === activeRecommendationAlertWorkflowBatchComparisonPresetId.value
    ) || alternatives[0]
  );
});

const recommendationAlertWorkflowBatchPresetDriftCandidates = computed(() => {
  if (!selectedRecommendationAlertWorkflowSessions.value.length) return [];
  if (
    !activeRecommendationAlertWorkflowBatchPresetId.value &&
    !recommendationAlertWorkflowBatchMetadataInstructions.value.length
  ) {
    return [];
  }

  return recommendationAlertWorkflowBatchMetadataPresetDefinitions.value
    .map(preset => ({
      presetId: preset.id,
      presetLabel: String(preset.label || preset.id).trim(),
      isSuggestedPreset:
        recommendationSuggestedAlertWorkflowBatchPreset.value?.presetId === preset.id,
      isLoadedPreset: activeRecommendationAlertWorkflowBatchPresetId.value === preset.id,
      ...buildRecommendationAlertWorkflowBatchPresetDraftDrift(preset),
    }))
    .sort((left, right) => {
      if (left.deltaCount !== right.deltaCount) return left.deltaCount - right.deltaCount;
      if (left.isSuggestedPreset !== right.isSuggestedPreset) {
        return left.isSuggestedPreset ? -1 : 1;
      }
      return String(left.presetLabel || '').localeCompare(String(right.presetLabel || ''));
    });
});

const closestRecommendationAlertWorkflowBatchPresetDrift = computed(
  () => recommendationAlertWorkflowBatchPresetDriftCandidates.value[0] || null
);

const recommendationAlertWorkflowBatchPresetDriftCards = computed(() => {
  const drift = closestRecommendationAlertWorkflowBatchPresetDrift.value;
  if (!drift) return [];

  return [
    {
      key: 'closest',
      label: 'Closest preset',
      value: drift.presetLabel,
      detail: drift.isSuggestedPreset ? 'Current recommendation' : 'Nearest template',
    },
    {
      key: 'matching_fields',
      label: 'Matching fields',
      value: `${drift.matchingFieldCount}/${drift.totalFieldCount}`,
      detail: 'Fields already aligned',
    },
    {
      key: 'drifted_fields',
      label: 'Drifted fields',
      value: Number(drift.deltaCount || 0),
      detail: drift.isExactMatch ? 'Exact preset match' : 'Fields changed from preset',
    },
  ];
});

const recommendationAlertWorkflowBatchPresetDriftRepairs = computed(() => {
  const drift = closestRecommendationAlertWorkflowBatchPresetDrift.value;
  if (!drift?.differingFields?.length) return [];

  return [...drift.differingFields]
    .sort((left, right) => {
      if (left.repairPriority !== right.repairPriority) {
        return left.repairPriority - right.repairPriority;
      }
      return String(left.label || '').localeCompare(String(right.label || ''));
    })
    .slice(0, 3)
    .map(field => {
      let actionLabel = `Use preset ${String(field.label || 'field').toLowerCase()}`;
      if (field.driftKind === 'drops') {
        actionLabel = `Drop current ${String(field.label || 'field').toLowerCase()}`;
      } else if (field.presetDisplay === 'Clear value') {
        actionLabel = `Clear ${String(field.label || 'field').toLowerCase()}`;
      }

      return {
        ...field,
        actionLabel,
        reason:
          field.repairPriority <= 2
            ? 'High-impact routing metadata'
            : field.repairPriority === 3
              ? 'Workflow outcome alignment'
              : 'Operator note and follow-up alignment',
      };
    });
});

const recommendationAlertWorkflowBatchMetadataPreflight = computed(() => {
  if (!selectedRecommendationAlertWorkflowSessions.value.length) return null;
  if (!recommendationAlertWorkflowBatchMetadataInstructions.value.length) return null;

  const drift = closestRecommendationAlertWorkflowBatchPresetDrift.value;
  if (!drift) return null;

  const highImpactFields = drift.differingFields.filter(
    field => Number(field.repairPriority || 99) <= 2
  );
  const workflowAlignmentFields = drift.differingFields.filter(
    field => Number(field.repairPriority || 99) === 3
  );

  let readiness = 'ready';
  let title = 'Ready to save';
  let summary = `Current batch draft stays aligned with ${drift.presetLabel}.`;
  let saveHint = 'Ready to apply metadata changes to the current selection.';

  if (!recommendationAlertWorkflowBatchMetadataDirty.value) {
    readiness = 'idle';
    title = 'No save changes yet';
    summary = `The current draft matches the live selection, so there is nothing new to save yet.`;
    saveHint = 'No batch metadata changes selected yet.';
  } else if (highImpactFields.length) {
    readiness = 'risk';
    title = 'High-impact preset drift';
    summary = `${highImpactFields.length} routing-critical fields differ from ${drift.presetLabel} before save.`;
    saveHint = 'Save is available, but high-impact preset gaps remain.';
  } else if (!drift.isExactMatch) {
    readiness = 'caution';
    title = 'Save with preset drift';
    summary = `This draft will save with ${drift.deltaCount} preset differences from ${drift.presetLabel}.`;
    saveHint = 'Save is available, but the draft no longer fully matches its closest preset.';
  }

  return {
    readiness,
    title,
    summary,
    saveHint,
    closestPresetLabel: drift.presetLabel,
    totalDriftCount: Number(drift.deltaCount || 0),
    highImpactFields,
    workflowAlignmentFields,
    recommendedRepairs: recommendationAlertWorkflowBatchPresetDriftRepairs.value.slice(0, 2),
  };
});

const recommendationAlertWorkflowBatchMetadataPreflightCards = computed(() => {
  const preflight = recommendationAlertWorkflowBatchMetadataPreflight.value;
  if (!preflight) return [];

  return [
    {
      key: 'readiness',
      label: 'Readiness',
      value: String(preflight.readiness || 'ready').toUpperCase(),
      detail: preflight.closestPresetLabel || 'No preset context',
    },
    {
      key: 'preset_gap',
      label: 'Preset gaps',
      value: Number(preflight.totalDriftCount || 0),
      detail: 'Fields drifting from closest preset',
    },
    {
      key: 'high_impact',
      label: 'High-impact',
      value: Number(preflight.highImpactFields?.length || 0),
      detail: 'Owner / due date mismatches',
    },
  ];
});

const recommendationAlertWorkflowBatchMetadataSaveGuard = computed(() => {
  const preflight = recommendationAlertWorkflowBatchMetadataPreflight.value;
  const requiresConfirmation = preflight?.readiness === 'risk';
  const isAcknowledged =
    requiresConfirmation && hasAcknowledgedRecommendationAlertWorkflowBatchRisk.value;

  return {
    requiresConfirmation,
    isAcknowledged,
    buttonLabel: !recommendationAlertWorkflowBatchMetadataDirty.value
      ? 'Save batch metadata'
      : requiresConfirmation
        ? isAcknowledged
          ? 'Confirm risky save'
          : 'Acknowledge risk to save'
        : 'Save batch metadata',
    helperText: requiresConfirmation
      ? isAcknowledged
        ? 'Risk acknowledged. Saving now will apply the drifted draft as-is.'
        : 'High-impact preset gaps require one explicit confirmation before save.'
      : preflight?.saveHint || 'Ready to apply metadata changes to the current selection.',
  };
});

const recommendationSuggestedAlertWorkflowBatchPresetCards = computed(() => {
  const suggestion = recommendationSuggestedAlertWorkflowBatchPreset.value;
  if (!suggestion) return [];

  const strongestAlternative =
    recommendationAlternativeAlertWorkflowBatchPresets.value[0] || null;

  return [
    {
      key: 'selected',
      label: 'Selected',
      value: Number(suggestion.totalCount || 0),
      detail: 'Sessions',
    },
    {
      key: 'matching',
      label: 'Matching',
      value: Number(suggestion.matchingCount || 0),
      detail: suggestion.supportingLabel || 'Supporting signals',
    },
    {
      key: 'confidence',
      label: 'Confidence',
      value: String(suggestion.confidence || 'medium').toUpperCase(),
      detail: 'Recommendation strength',
    },
    {
      key: 'runner_up',
      label: 'Runner-up',
      value: strongestAlternative ? Number(strongestAlternative.matchingCount || 0) : 0,
      detail: strongestAlternative
        ? strongestAlternative.presetLabel
        : 'No competing preset',
    },
  ];
});

const normalizeRecommendationAlertWorkflowBatchMetadataDraft = draft => ({
  ...emptyRecommendationAlertWorkflowBatchMetadataDraft(),
  ...(draft || {}),
});

const formatRecommendationAlertWorkflowBatchPresetValue = (field, draft) => {
  if (!field) return 'Not set';
  if (!draft?.[field.applyKey]) return 'Unchanged';

  const rawValue = String(draft?.[field.valueKey] || '').trim();
  if (!rawValue) return 'Clear value';
  if (field.valueKey === 'resolutionOutcome' && rawValue === 'published_article') {
    return 'Published article';
  }
  return rawValue;
};

const buildRecommendationAlertWorkflowBatchPresetComparison = (
  suggestedOption,
  alternativeOption
) => {
  const suggestedDraft = normalizeRecommendationAlertWorkflowBatchMetadataDraft(
    suggestedOption?.presetDraft
  );
  const alternativeDraft = normalizeRecommendationAlertWorkflowBatchMetadataDraft(
    alternativeOption?.presetDraft
  );

  const differingFields = recommendationAlertWorkflowBatchMetadataFields
    .map(field => {
      const suggestedEnabled = Boolean(suggestedDraft?.[field.applyKey]);
      const alternativeEnabled = Boolean(alternativeDraft?.[field.applyKey]);
      const suggestedValue = String(suggestedDraft?.[field.valueKey] || '').trim();
      const alternativeValue = String(alternativeDraft?.[field.valueKey] || '').trim();

      if (
        suggestedEnabled === alternativeEnabled &&
        suggestedValue === alternativeValue
      ) {
        return null;
      }

      let changeKind = 'changes';
      if (!suggestedEnabled && alternativeEnabled) changeKind = 'adds';
      else if (suggestedEnabled && !alternativeEnabled) changeKind = 'drops';

      return {
        key: field.valueKey,
        label: field.label || field.valueKey,
        changeKind,
        suggestedDisplay: formatRecommendationAlertWorkflowBatchPresetValue(
          field,
          suggestedDraft
        ),
        alternativeDisplay: formatRecommendationAlertWorkflowBatchPresetValue(
          field,
          alternativeDraft
        ),
      };
    })
    .filter(Boolean);

  return {
    deltaCount: differingFields.length,
    deltaSummary:
      differingFields.length === 1
        ? `Changes 1 field compared with ${suggestedOption?.presetLabel || 'the suggested preset'}.`
        : `Changes ${differingFields.length} fields compared with ${suggestedOption?.presetLabel || 'the suggested preset'}.`,
    differingFields,
  };
};

const buildRecommendationAlertWorkflowBatchPresetDraftDrift = preset => {
  const presetDraft = normalizeRecommendationAlertWorkflowBatchMetadataDraft(
    preset?.buildDraft ? preset.buildDraft() : {}
  );
  const currentDraft = normalizeRecommendationAlertWorkflowBatchMetadataDraft(
    recommendationAlertWorkflowBatchMetadataDraft.value
  );

  const differingFields = recommendationAlertWorkflowBatchMetadataFields
    .map(field => {
      const presetEnabled = Boolean(presetDraft?.[field.applyKey]);
      const currentEnabled = Boolean(currentDraft?.[field.applyKey]);
      const presetValue = String(presetDraft?.[field.valueKey] || '').trim();
      const currentValue = String(currentDraft?.[field.valueKey] || '').trim();

      if (presetEnabled === currentEnabled && presetValue === currentValue) {
        return null;
      }

      let driftKind = 'changes';
      if (!presetEnabled && currentEnabled) driftKind = 'adds';
      else if (presetEnabled && !currentEnabled) driftKind = 'drops';

      return {
        key: field.valueKey,
        label: field.label || field.valueKey,
        driftKind,
        repairPriority: Number(field.repairPriority || 99),
        presetDisplay: formatRecommendationAlertWorkflowBatchPresetValue(
          field,
          presetDraft
        ),
        currentDisplay: formatRecommendationAlertWorkflowBatchPresetValue(
          field,
          currentDraft
        ),
      };
    })
    .filter(Boolean);

  const totalFieldCount = recommendationAlertWorkflowBatchMetadataFields.length;
  const matchingFieldCount = totalFieldCount - differingFields.length;

  return {
    deltaCount: differingFields.length,
    totalFieldCount,
    matchingFieldCount,
    isExactMatch: !differingFields.length,
    driftSummary:
      differingFields.length === 0
        ? `Current draft matches ${preset?.label || 'this preset'} exactly.`
        : differingFields.length === 1
          ? `Current draft differs from ${preset?.label || 'this preset'} on 1 field.`
          : `Current draft differs from ${preset?.label || 'this preset'} on ${differingFields.length} fields.`,
    differingFields,
  };
};

const recommendationAlertWorkflowBatchMetadataMatchesPreset = presetId => {
  const preset = recommendationAlertWorkflowBatchMetadataPresetById(presetId);
  if (!preset) return false;

  return (
    JSON.stringify(normalizeRecommendationAlertWorkflowBatchMetadataDraft(preset.buildDraft())) ===
    JSON.stringify(
      normalizeRecommendationAlertWorkflowBatchMetadataDraft(
        recommendationAlertWorkflowBatchMetadataDraft.value
      )
    )
  );
};

const syncRecommendationAlertWorkflowBatchMetadataDraft = () => {
  const nextDraft = emptyRecommendationAlertWorkflowBatchMetadataDraft();
  const selectedItems = selectedRecommendationAlertWorkflowSessions.value;

  if (!selectedItems.length) {
    recommendationAlertWorkflowBatchMetadataDraft.value = nextDraft;
    activeRecommendationAlertWorkflowBatchPresetId.value = '';
    return;
  }

  const firstMetadata = currentRecommendationAlertWorkflowMetadata(selectedItems[0]);
  recommendationAlertWorkflowBatchMetadataFields.forEach(field => {
    const firstValue = String(firstMetadata?.[field.metadataKey] || '').trim();
    const hasConsensus = selectedItems.every(alertItem => {
      const metadata = currentRecommendationAlertWorkflowMetadata(alertItem);
      return String(metadata?.[field.metadataKey] || '').trim() === firstValue;
    });

    nextDraft[field.valueKey] = hasConsensus ? firstValue : '';
  });

  recommendationAlertWorkflowBatchMetadataDraft.value = nextDraft;
  activeRecommendationAlertWorkflowBatchPresetId.value = '';
};

const applyRecommendationAlertWorkflowBatchPreset = presetId => {
  const preset = recommendationAlertWorkflowBatchMetadataPresetById(presetId);
  if (!preset) return false;

  recommendationAlertWorkflowBatchMetadataDraft.value =
    normalizeRecommendationAlertWorkflowBatchMetadataDraft(preset.buildDraft());
  activeRecommendationAlertWorkflowBatchPresetId.value = preset.id;
  return true;
};

const applyRecommendationAlertWorkflowBatchPresetField = (presetId, valueKey) => {
  const preset = recommendationAlertWorkflowBatchMetadataPresetById(presetId);
  const field = recommendationAlertWorkflowBatchMetadataFieldByValueKey(valueKey);
  if (!preset || !field) return false;

  const presetDraft = normalizeRecommendationAlertWorkflowBatchMetadataDraft(
    preset.buildDraft()
  );
  const nextDraft = normalizeRecommendationAlertWorkflowBatchMetadataDraft(
    recommendationAlertWorkflowBatchMetadataDraft.value
  );

  nextDraft[field.applyKey] = Boolean(presetDraft?.[field.applyKey]);
  nextDraft[field.valueKey] = String(presetDraft?.[field.valueKey] || '');
  recommendationAlertWorkflowBatchMetadataDraft.value = nextDraft;
  return true;
};

const acknowledgeRecommendationAlertWorkflowBatchMetadataRisk = () => {
  hasAcknowledgedRecommendationAlertWorkflowBatchRisk.value = true;
};

const clearRecommendationAlertWorkflowBatchMetadataRiskAcknowledgement = () => {
  hasAcknowledgedRecommendationAlertWorkflowBatchRisk.value = false;
};

const clearRecommendationAlertWorkflowBatchPreset = () => {
  syncRecommendationAlertWorkflowBatchMetadataDraft();
};

const previewRecommendationAlertWorkflowBatchPresetComparison = presetId => {
  activeRecommendationAlertWorkflowBatchComparisonPresetId.value = String(
    presetId || ''
  ).trim();
};

const syncRecommendationAlertWorkflowDrafts = () => {
  const nextDrafts = {};

  recommendationAllAlerts.value.forEach(alertItem => {
    const metadata = currentRecommendationAlertWorkflowMetadata(alertItem);
    nextDrafts[alertItem.key] = {
      ownerLabel: String(metadata.owner_label || ''),
      dueDate: String(metadata.due_date || ''),
      resolutionOutcome: String(metadata.resolution_outcome || ''),
      handoffReason: String(metadata.handoff_reason || ''),
      nextCheckpoint: String(metadata.next_checkpoint || ''),
      note: String(metadata.note || ''),
    };
  });

  recommendationAlertWorkflowDrafts.value = nextDrafts;
};

const buildNextAlertWorkflowConfig = (alertItem, nextEntry) => {
  const nextConfig = cloneJson(props.assistant?.config || {});
  const nextRoot = cloneJson(nextConfig.content_gap_alert_workflows || {});

  if (nextEntry && Object.keys(nextEntry).length) {
    nextRoot[alertItem.key] = nextEntry;
  } else {
    delete nextRoot[alertItem.key];
  }

  if (Object.keys(nextRoot).length) {
    nextConfig.content_gap_alert_workflows = nextRoot;
  } else {
    delete nextConfig.content_gap_alert_workflows;
  }

  return nextConfig;
};

const buildNextAlertWorkflowConfigForMany = workflowItems => {
  const nextConfig = cloneJson(props.assistant?.config || {});
  const nextRoot = cloneJson(nextConfig.content_gap_alert_workflows || {});

  workflowItems.forEach(({ alertItem, nextEntry }) => {
    if (!alertItem?.key) return;

    if (nextEntry && Object.keys(nextEntry).length) {
      nextRoot[alertItem.key] = nextEntry;
    } else {
      delete nextRoot[alertItem.key];
    }
  });

  if (Object.keys(nextRoot).length) {
    nextConfig.content_gap_alert_workflows = nextRoot;
  } else {
    delete nextConfig.content_gap_alert_workflows;
  }

  return nextConfig;
};

const buildRecommendationAlertWorkflowEntry = (
  alertItem,
  status,
  { refreshBaseline = false, metadataOverride = null } = {}
) => {
  const metadata =
    metadataOverride ||
    normalizeRecommendationAlertWorkflowMetadataDraft(
      recommendationAlertWorkflowDraft(alertItem)
    );
  const existing = alertItem?.workflowState || {};
  const nextEntry = {
    status,
    updated_at: new Date().toISOString(),
    updated_by_user_id: currentUserId.value || undefined,
    baseline_count: refreshBaseline
      ? Number(alertItem?.count || 0)
      : Number((existing?.baseline_count ?? alertItem?.count) || 0),
    baseline_delta: refreshBaseline
      ? Number(alertItem?.delta || 0)
      : Number((existing?.baseline_delta ?? alertItem?.delta) || 0),
    baseline_trend_direction: refreshBaseline
      ? String(alertItem?.trendDirection || '').trim()
      : String(
          existing?.baseline_trend_direction || alertItem?.trendDirection || ''
        ).trim(),
    owner_label: metadata.owner_label,
    resolution_outcome: metadata.resolution_outcome,
    handoff_reason: metadata.handoff_reason,
    next_checkpoint: metadata.next_checkpoint,
    note: metadata.note,
  };

  if (metadata.due_date) {
    nextEntry.due_at = new Date(`${metadata.due_date}T12:00:00Z`).toISOString();
  }

  if (status === 'snoozed') {
    nextEntry.snoozed_until = refreshBaseline
      ? new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString()
      : String(existing?.snoozed_until || '')
          .trim() || new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString();
  }
  if (status === 'handoff') {
    nextEntry.handoff_target = String(
      metadata.owner_label ||
        existing?.handoff_target ||
        alertItem?.label ||
        alertItem?.title ||
        ''
    ).trim();
    nextEntry.owner_label = nextEntry.owner_label || nextEntry.handoff_target;
  }

  return nextEntry;
};

const buildRecommendationAlertWorkflowMetadataOverrideForBatch = alertItem => {
  const nextMetadata = { ...currentRecommendationAlertWorkflowMetadata(alertItem) };

  recommendationAlertWorkflowBatchMetadataInstructions.value.forEach(
    ({ metadataKey, value }) => {
      if (value) {
        nextMetadata[metadataKey] = value;
      } else {
        delete nextMetadata[metadataKey];
      }
    }
  );

  return nextMetadata;
};

const saveRecommendationAlertWorkflow = async (alertItem, status) => {
  if (
    !assistantId.value ||
    !alertItem?.key ||
    savingRecommendationAlertWorkflowKey.value
  ) {
    return false;
  }

  savingRecommendationAlertWorkflowKey.value = alertItem.key;
  try {
    const nextEntry = buildRecommendationAlertWorkflowEntry(alertItem, status, {
      refreshBaseline: true,
    });
    const nextConfig = buildNextAlertWorkflowConfig(alertItem, nextEntry);
    await persistAssistantConfig(nextConfig);
    await fetchGapRecommendations();
    alertMessageForWorkflow(status);
    return true;
  } catch (error) {
    alert(error?.message || 'Failed to update alert workflow.');
    return false;
  } finally {
    savingRecommendationAlertWorkflowKey.value = '';
  }
};

const alertMessageForWorkflow = status => {
  switch (String(status || '').trim()) {
    case 'resolved':
      alert('Alert marked resolved.');
      break;
    case 'snoozed':
      alert('Alert snoozed for 3 days.');
      break;
    case 'handoff':
      alert('Alert marked as handed off.');
      break;
    default:
      alert('Alert workflow updated.');
  }
};

const saveRecommendationAlertWorkflowDetails = async alertItem => {
  if (
    !assistantId.value ||
    !alertItem?.key ||
    savingRecommendationAlertWorkflowKey.value
  ) {
    return false;
  }
  if (!recommendationAlertWorkflowMetadataDirty(alertItem)) return false;
  if (!alertItem?.workflowStatus || alertItem.workflowStatus === 'none') {
    alert('Choose a workflow action before saving owner, due date, or note.');
    return false;
  }

  savingRecommendationAlertWorkflowKey.value = alertItem.key;
  try {
    const nextEntry = buildRecommendationAlertWorkflowEntry(
      alertItem,
      alertItem.workflowStatus,
      { refreshBaseline: false }
    );
    const nextConfig = buildNextAlertWorkflowConfig(alertItem, nextEntry);
    await persistAssistantConfig(nextConfig);
    await fetchGapRecommendations();
    alert('Alert workflow details saved.');
    return true;
  } catch (error) {
    alert(error?.message || 'Failed to save alert workflow details.');
    return false;
  } finally {
    savingRecommendationAlertWorkflowKey.value = '';
  }
};

const clearRecommendationAlertWorkflow = async alertItem => {
  if (
    !assistantId.value ||
    !alertItem?.key ||
    savingRecommendationAlertWorkflowKey.value
  ) {
    return false;
  }

  savingRecommendationAlertWorkflowKey.value = alertItem.key;
  try {
    const nextConfig = buildNextAlertWorkflowConfig(alertItem, null);
    await persistAssistantConfig(nextConfig);
    await fetchGapRecommendations();
    alert('Alert workflow cleared.');
    return true;
  } catch (error) {
    alert(error?.message || 'Failed to clear alert workflow.');
    return false;
  } finally {
    savingRecommendationAlertWorkflowKey.value = '';
  }
};

const applyRecommendationAlertWorkflowBatch = async action => {
  if (!assistantId.value || isApplyingRecommendationAlertWorkflowBatch.value) {
    return false;
  }

  const selectedItems = selectedRecommendationAlertWorkflowSessions.value;
  if (!selectedItems.length) {
    alert('Select one or more workflow sessions first.');
    return false;
  }

  isApplyingRecommendationAlertWorkflowBatch.value = true;
  try {
    const nextConfig = buildNextAlertWorkflowConfigForMany(
      selectedItems.map(alertItem => ({
        alertItem,
        nextEntry:
          action === 'clear'
            ? null
            : buildRecommendationAlertWorkflowEntry(alertItem, action, {
                refreshBaseline: true,
              }),
      }))
    );
    await persistAssistantConfig(nextConfig);
    await fetchGapRecommendations();
    clearSelectedRecommendationAlertWorkflows();

    if (action === 'clear') {
      alert(`Cleared ${selectedItems.length} workflow sessions.`);
    } else {
      alert(
        `Updated ${selectedItems.length} workflow sessions to ${String(action).replace('_', ' ')}.`
      );
    }
    return true;
  } catch (error) {
    alert(error?.message || 'Failed to apply workflow batch action.');
    return false;
  } finally {
    isApplyingRecommendationAlertWorkflowBatch.value = false;
  }
};

const applyRecommendationAlertWorkflowBatchMetadata = async () => {
  if (!assistantId.value || isApplyingRecommendationAlertWorkflowBatch.value) {
    return false;
  }

  const selectedItems = selectedRecommendationAlertWorkflowSessions.value.filter(
    item => item?.workflowStatus && item.workflowStatus !== 'none'
  );
  if (!selectedItems.length) {
    alert('Select one or more workflow sessions first.');
    return false;
  }
  if (!recommendationAlertWorkflowBatchMetadataInstructions.value.length) {
    alert('Choose at least one metadata field to apply.');
    return false;
  }
  if (!recommendationAlertWorkflowBatchMetadataDirty.value) {
    alert('No batch metadata changes to apply.');
    return false;
  }
  if (
    recommendationAlertWorkflowBatchMetadataSaveGuard.value.requiresConfirmation &&
    !recommendationAlertWorkflowBatchMetadataSaveGuard.value.isAcknowledged
  ) {
    acknowledgeRecommendationAlertWorkflowBatchMetadataRisk();
    alert(
      'High-impact preset drift detected. Review the highlighted gaps, then click save again to confirm.'
    );
    return false;
  }

  isApplyingRecommendationAlertWorkflowBatch.value = true;
  try {
    const nextConfig = buildNextAlertWorkflowConfigForMany(
      selectedItems.map(alertItem => ({
        alertItem,
        nextEntry: buildRecommendationAlertWorkflowEntry(
          alertItem,
          alertItem.workflowStatus,
          {
            refreshBaseline: false,
            metadataOverride: buildRecommendationAlertWorkflowMetadataOverrideForBatch(
              alertItem
            ),
          }
        ),
      }))
    );
    await persistAssistantConfig(nextConfig);
    await fetchGapRecommendations();
    alert(`Updated workflow metadata for ${selectedItems.length} sessions.`);
    return true;
  } catch (error) {
    alert(error?.message || 'Failed to apply workflow metadata.');
    return false;
  } finally {
    isApplyingRecommendationAlertWorkflowBatch.value = false;
    hasAcknowledgedRecommendationAlertWorkflowBatchRisk.value = false;
  }
};

const saveContentOwnership = async () => {
  if (!assistantId.value) return false;
  if (!articleScopeSelectionValid.value) {
    alert(
      'Choose one connected Help Center portal for the assistant scope or any pinned channel overrides before saving.'
    );
    return false;
  }
  if (!knowledgeRetrievalDirty.value) return false;

  isSavingContentOwnership.value = true;
  try {
    const existingPromptSections = aiCloserConfig.value?.prompt_sections || {};
    const knowledgeRetrieval = {
      ...((existingPromptSections || {}).knowledge_retrieval || {}),
      article_scope_mode: draftArticleScopeMode.value,
      article_portal_id: draftPersistedArticlePortalId.value,
      max_sources: normalizeMaxSources(draftMaxSources.value),
      context_budget: normalizeContextBudget(draftContextBudget.value),
      min_confidence_score: normalizeMinConfidenceScore(
        draftMinConfidenceScore.value
      ),
      search_balance: normalizeSearchBalance(draftSearchBalance.value),
      citation_mode: normalizeRetrievalCitationMode(draftCitationMode.value),
      source_priority: normalizeSourcePriority(draftSourcePriority.value),
      no_evidence_behavior: normalizeNoEvidenceBehavior(draftNoEvidenceBehavior.value),
      inbox_bindings: [
        ...savedDisconnectedInboxBindings.value,
        ...persistedDraftInboxBindings.value,
      ],
      segment_bindings: persistedDraftSegmentBindings.value,
    };
    const nextCfg = {
      ...(props.assistant?.config || {}),
      wlab_article_scope_mode: draftArticleScopeMode.value,
      wlab_article_portal_id: draftPersistedArticlePortalId.value,
      feature_citation: normalizeRetrievalCitationMode(draftCitationMode.value) !== 'never',
    };
    await persistAssistantConfig(nextCfg);
    await store.dispatch('aiCloser/updateConfig', {
      assistantId: assistantId.value,
      data: {
        prompt_sections: {
          ...existingPromptSections,
          knowledge_retrieval: knowledgeRetrieval,
        },
      },
    });
    await fetchContentSources();
    return true;
  } catch (error) {
    alert(error?.message || 'Failed to save knowledge retrieval strategy.');
    return false;
  } finally {
    isSavingContentOwnership.value = false;
  }
};

const productSheetCanSave = () => {
  if (!isProductSheetOpen.value || !selectedProduct.value) return false;
  if (!productInformationRef.value?.save) return false;
  if (typeof productInformationRef.value.canSave === 'function') {
    return Boolean(productInformationRef.value.canSave());
  }
  return true;
};

const linksSheetCanSave = () => {
  if (!isLinksSheetOpen.value) return false;
  if (!productLinksRef.value?.save) return false;
  if (typeof productLinksRef.value.canSave === 'function') {
    return Boolean(productLinksRef.value.canSave());
  }
  return true;
};

const answerTemplatesSheetCanSave = () => {
  if (!isAnswerTemplatesSheetOpen.value) return false;
  if (!productAnswerTemplatesRef.value?.save) return false;
  if (typeof productAnswerTemplatesRef.value.canSave === 'function') {
    return Boolean(productAnswerTemplatesRef.value.canSave());
  }
  return true;
};

const mediaSheetCanSave = () => {
  if (!isMediaSheetOpen.value) return false;
  if (!productMediaRef.value?.save) return false;
  if (typeof productMediaRef.value.canSave === 'function') {
    return Boolean(productMediaRef.value.canSave());
  }
  return true;
};

const saveContentPage = async () => {
  if (productSheetCanSave()) {
    return productInformationRef.value.save();
  }
  if (mediaSheetCanSave()) {
    return productMediaRef.value.save();
  }
  if (linksSheetCanSave()) {
    return productLinksRef.value.save();
  }
  if (answerTemplatesSheetCanSave()) {
    return productAnswerTemplatesRef.value.save();
  }
  return saveContentOwnership();
};

watch(
  [
    recommendationThresholdProfiles,
    () => props.assistant?.config?.content_gap_threshold_profiles,
  ],
  () => {
    syncRecommendationThresholdOverrideDrafts();
  },
  { immediate: true, deep: true }
);

watch(
  [recommendationAllAlerts, () => props.assistant?.config?.content_gap_alert_workflows],
  () => {
    syncRecommendationAlertWorkflowDrafts();
  },
  { immediate: true, deep: true }
);

watch(
  recommendationAlertWorkflowSortOptions,
  options => {
    const available = Array.isArray(options) ? options.map(item => item.id) : [];
    const suggested =
      recommendationSnapshotSummary.value?.alert_workflow_default_sort ||
      'followup_due';
    if (!recommendationAlertWorkflowSort.value || !available.includes(recommendationAlertWorkflowSort.value)) {
      recommendationAlertWorkflowSort.value = available.includes(suggested)
        ? suggested
        : available[0] || 'followup_due';
    }
  },
  { immediate: true, deep: true }
);

watch(
  recommendationAlertWorkflowLedger,
  ledger => {
    const availableKeys = new Set(Array.isArray(ledger) ? ledger.map(item => item.key) : []);
    selectedRecommendationAlertWorkflowKeys.value =
      selectedRecommendationAlertWorkflowKeys.value.filter(key =>
        availableKeys.has(key)
      );
  },
  { immediate: true, deep: true }
);

watch(
  selectedRecommendationAlertWorkflowSessions,
  () => {
    syncRecommendationAlertWorkflowBatchMetadataDraft();
  },
  { immediate: true, deep: true }
);

watch(
  recommendationAlertWorkflowBatchMetadataDraft,
  () => {
    hasAcknowledgedRecommendationAlertWorkflowBatchRisk.value = false;
    if (!activeRecommendationAlertWorkflowBatchPresetId.value) return;
    if (
      recommendationAlertWorkflowBatchMetadataMatchesPreset(
        activeRecommendationAlertWorkflowBatchPresetId.value
      )
    ) {
      return;
    }
    activeRecommendationAlertWorkflowBatchPresetId.value = '';
  },
  { deep: true }
);

watch(
  recommendationAlternativeAlertWorkflowBatchPresets,
  alternatives => {
    const available = Array.isArray(alternatives)
      ? alternatives.map(item => item.presetId)
      : [];
    if (!available.length) {
      activeRecommendationAlertWorkflowBatchComparisonPresetId.value = '';
      return;
    }
    if (
      available.includes(activeRecommendationAlertWorkflowBatchComparisonPresetId.value)
    ) {
      return;
    }
    activeRecommendationAlertWorkflowBatchComparisonPresetId.value = available[0];
  },
  { immediate: true, deep: true }
);

watch(
  recommendationAlertWorkflowActivityHighlightCards,
  cards => {
    const available = Array.isArray(cards) ? cards.map(item => item.id) : [];
    if (
      recommendationAlertWorkflowActivityHighlightFilter.value !== 'all' &&
      !available.includes(recommendationAlertWorkflowActivityHighlightFilter.value)
    ) {
      recommendationAlertWorkflowActivityHighlightFilter.value = 'all';
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => [
    activeRecommendationWorkflowAuditKey.value,
    recommendationAlertWorkflowSessionAudits.value.length,
    recommendationAlertWorkflowLedger.value.length,
    recommendationAllAlerts.value.length,
  ],
  ([workflowKey]) => {
    if (!workflowKey) return;
    const existsInAudits = recommendationAlertWorkflowSessionAudits.value.some(
      audit => audit.workflowKey === workflowKey
    );
    const existsInAlerts =
      recommendationAlertWorkflowLedger.value.some(item => item.key === workflowKey) ||
      recommendationAllAlerts.value.some(item => item.key === workflowKey);
    if (!existsInAudits && !existsInAlerts) {
      activeRecommendationWorkflowAuditKey.value = '';
    }
  },
  { immediate: true }
);

watch(
  activeRecommendationWorkflowAuditHighlightCards,
  cards => {
    const available = Array.isArray(cards) ? cards.map(item => item.id) : [];
    if (
      recommendationWorkflowAuditHighlightFilter.value !== 'all' &&
      !available.includes(recommendationWorkflowAuditHighlightFilter.value)
    ) {
      recommendationWorkflowAuditHighlightFilter.value = 'all';
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => [assistantId.value, defaultPortalSlug.value],
  ([nextId]) => {
    if (!nextId) return;
    activeRecommendationAlertKey.value = '';
    activeRecommendationWorkflowAuditKey.value = '';
    recommendationAlertWorkflowActivityHighlightFilter.value = 'all';
    recommendationWorkflowAuditHighlightFilter.value = 'all';
    hasAttemptedRecommendationSnapshot.value = false;
    clearSelectedRecommendationAlertWorkflows();
    resetRecommendationAlertWorkflowFilters();
    fetchContentSources();
    fetchGapRecommendations();
  },
  { immediate: true }
);

watch(
  () => [
    recommendationPriorityFilter.value,
    recommendationWorkTypeFilter.value,
    recommendationDraftStateFilter.value,
    recommendationPresetFilter.value,
    recommendationFamilyFilter.value,
    recommendationFamilyConfidenceFilter.value,
    recommendationReadinessFilter.value,
    recommendationTrendFilter.value,
    recommendationFreshnessFilter.value,
    recommendationRoutingFilter.value,
    recommendationSort.value,
  ],
  () => {
    if (!assistantId.value) return;
    fetchGapRecommendations();
  }
);


watch(
  () => previewInbox.value?.id,
  inboxId => {
    if (!assistantId.value) return;

    if (inboxId) {
      pushPreviewEvent(
        'Preview context ready',
        `Using Web Widget inbox ${inboxId} as the preview context.`,
        'success'
      );
      return;
    }

    pushPreviewEvent(
      'Static preview mode',
      'No Web Widget inbox is connected yet, so this panel stays as a static AI mock.',
      'info'
    );
  },
  { immediate: true }
);

watch(
  () => [
    savedArticleScopeMode.value,
    configuredArticlePortalId.value,
    savedMaxSources.value,
    savedContextBudget.value,
    savedMinConfidenceScore.value,
    savedSearchBalance.value,
    savedCitationMode.value,
    JSON.stringify(savedSourcePriority.value),
    savedNoEvidenceBehavior.value,
    JSON.stringify(savedConnectedInboxBindings.value),
    JSON.stringify(savedSegmentBindings.value),
  ],
  () => {
    syncArticleScopeDraft();
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('portals/index');
  if (!inboxes.value.length) {
    store.dispatch('inboxes/get');
  }
  if (!conversationAttributeDefinitions.value.length) {
    store.dispatch('attributes/get');
  }
});

defineExpose({
  save: saveContentPage,
  canSave: () =>
    Boolean(
      productSheetCanSave() ||
        mediaSheetCanSave() ||
        linksSheetCanSave() ||
        answerTemplatesSheetCanSave() ||
        (knowledgeRetrievalDirty.value &&
          articleScopeSelectionValid.value &&
          !isSavingContentOwnership.value)
    ),
});
</script>

<template>
  <!-- Outer shell: same gap/overflow pattern as Fin's layout__module__right-slot -->
  <div class="fin-ai content-page-shell flex h-full min-h-0 overflow-hidden gap-module p-1 -m-1">

    <!-- ── Main content column ─────────────────────────────────────────── -->
    <div class="content-page-main -order-1 flex-1 overflow-hidden flex flex-col items-stretch p-1 -m-1">
      <div class="content-page-surface flex flex-col h-full flex-grow rounded-module overflow-y-auto overflow-x-hidden bg-base-module shadow-lvl-0">
        <div class="flex flex-col flex-grow flex-shrink-0 h-full w-full">
          <div class="flex flex-col h-full overflow-hidden">

            <!-- ── Top header row ─────────────────────────────────────── -->
            <div class="border-b border-neutral-border">
              <div class="mb-1">
                <div class="fin-page-header-row">
                  <div class="flex items-center">
                    <!-- sidebar-toggle -->
                    <div class="popover__opener mr-2">
                      <button
                        class="btn o__secondary o__icon o__subtle"
                        type="button"
                        aria-label="Toggle sidebar"
                      >
                        <!-- sidebar-open SVG (exact copy from Fin) -->
                        <svg class="interface-icon o__standard" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2 2H14C14.55 2 15 2.45 15 3V13C15 13.55 14.55 14 14 14H2C1.45 14 1 13.55 1 13V3C1 2.45 1.45 2 2 2ZM13.3 3.7H6V12.3H13.3V3.7Z" />
                        </svg>
                      </button>
                    </div>
                    <h1 class="heading o__size-1">Content</h1>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="content-page-header-note">
                      Live knowledge setup
                    </span>
                    <button
                      type="button"
                      class="btn o__secondary"
                      @click="onOpenQuickPreview('Content header')"
                    >
                      Open full preview
                    </button>
                  </div>
                </div>
              </div>

              <!-- ── Search + filters row ──────────────────────────────── -->
              <div class="flex flex-row items-center justify-between py-4 px-6 gap-3">
                <div class="flex flex-row items-center gap-2 flex-1 min-w-0">
                  <div class="relative flex-1 max-w-xs">
                    <span
                      class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
                      style="color: #9ca3af"
                    />
                    <input
                      v-model="searchQuery"
                      type="text"
                      class="search-input reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
                      placeholder="Search..."
                    />
                  </div>
                  <div class="content-page-toolbar-note text-sm fin-muted">
                    Search your current knowledge sources. Internal improvement
                    filters stay inside the collapsed queue below.
                  </div>
                </div>
                <div class="text-sm fin-muted whitespace-nowrap">
                  {{
                    isLoadingContentSources
                      ? 'Loading...'
                      : `${filteredContentSources.length} knowledge sources`
                  }}
                </div>
              </div>
            </div><!-- /border-b header -->

            <!-- ── Scrollable body ────────────────────────────────────── -->
            <div class="content-setup-v2 flex flex-col px-6 pt-6 pb-4 h-full overflow-y-auto">
              <div class="fin-page-hero content-page-hero">
                <div class="fin-page-hero__eyebrow">Customer-facing knowledge</div>
                <div class="fin-page-hero__title">
                  Manage the content the AI can safely use in live replies.
                </div>
                <div class="fin-page-hero__copy">
                  Keep this page focused on what customers will actually feel:
                  live articles, snippets, uploaded documents, synced pages, and
                  a realistic preview on the right.
                </div>
                <div class="fin-page-stats">
                  <div
                    v-for="card in contentSummaryCards"
                    :key="card.label"
                    class="fin-page-stat"
                  >
                    <div class="fin-page-stat__label">{{ card.label }}</div>
                    <div class="fin-page-stat__value">{{ card.value }}</div>
                    <div class="fin-page-stat__note">{{ card.note }}</div>
                  </div>
                </div>
              </div>

              <div
                v-if="contentSeedContext.enabled"
                class="mt-4 rounded-large border border-sky-200 bg-sky-50 px-4 py-4"
              >
                <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <div class="text-xs uppercase tracking-[0.08em] text-sky-700">
                      Content seeded context
                    </div>
                    <div class="mt-1 text-sm font-semibold text-sky-900">
                      {{ contentSeedSummary || 'A repair context was brought into Content.' }}
                    </div>
                    <div
                      v-if="contentSeedContext.scopeWarning"
                      class="mt-1 text-sm text-sky-800"
                    >
                      Scope warning: {{ contentSeedContext.scopeWarning }}
                    </div>
                    <div
                      v-if="contentSeedContext.query"
                      class="mt-1 text-sm text-sky-800"
                    >
                      Sample query: {{ contentSeedContext.query }}
                    </div>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <button
                      type="button"
                      class="btn o__secondary"
                      @click="focusSeededContentTarget"
                    >
                      {{ contentSeedPrimaryActionLabel }}
                    </button>
                    <button
                      v-if="contentSeedContext.query"
                      type="button"
                      class="btn o__secondary"
                      @click="retestSeededContentContext"
                    >
                      Retest sample
                    </button>
                    <button type="button" class="btn o__secondary" @click="clearContentSeed">
                      Clear seeded context
                    </button>
                  </div>
                </div>
              </div>

              <!-- Add content section -->
              <div class="add-content-section mb-12 mt-8">
                <!-- inline style block for container queries — same as Fin -->
                <style>
                  .content-cards__grid { container-type: inline-size; }
                  @container (max-width: 780px) {
                    .content-cards__see-all-collapsible-icon { display: none; }
                  }
                  @container (max-width: 700px) {
                    .content-cards__card { min-width: calc(50% - 8px); }
                    .content-cards__suggestions-count-inline { display: none; }
                    .content-cards__suggestions-count-stacked { display: block; }
                  }
                </style>

                <h3 class="text-h3 font-semibold mb-3">Add content</h3>

                <!-- Cards: flex flex-wrap gap-4 — exactly as Fin -->
                <div class="flex flex-wrap gap-4 content-cards__grid">

                  <!-- Public article -->
                  <div class="flex-1 content-cards__card">
                    <div
                      class="border border-neutral-border rounded-large p-4 flex flex-col cursor-pointer hover:shadow-lvl-1 transition-shadow flex-1"
                      @click="onOpenArticleSheet('public')"
                    >
                      <div class="w-8 h-8 border border-neutral-border rounded-lg bg-neutral-container flex items-center justify-center">
                        <svg class="interface-icon o__standard o__no-margin" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14.5 2C14.5 2 14.48 2 14.47 2C12.1 2.12 9.9 2.85 8 4.01C6.1 2.85 3.89 2.13 1.53 2.01C1.53 2.01 1.51 2.01 1.5 2.01C1.23 2.01 1 2.24 1 2.52V13.25C1 13.52 1.21 13.73 1.48 13.74C3.86 13.86 6.08 14.58 8 15.75C9.92 14.58 12.14 13.85 14.52 13.74C14.79 13.73 15 13.52 15 13.25V2.51C15 2.23 14.77 2 14.5 2ZM13.3 12.14C11.45 12.37 9.65 12.93 8 13.8V6.02L8.89 5.48C10.24 4.65 11.75 4.1 13.3 3.85V12.15V12.14Z" />
                        </svg>
                      </div>
                      <h4 class="font-semibold mt-3">Public article</h4>
                    </div>
                  </div>

                  <!-- Internal article -->
                  <div class="flex-1 content-cards__card">
                    <div
                      class="border border-neutral-border rounded-large p-4 flex flex-col cursor-pointer hover:shadow-lvl-1 transition-shadow flex-1"
                      @click="onOpenArticleSheet('internal')"
                    >
                      <div class="w-8 h-8 border border-neutral-border rounded-lg bg-neutral-container flex items-center justify-center">
                        <svg class="interface-icon o__standard o__no-margin" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                          <path d="M13.3 6H12V4.5C12 2.29 10.21 0.5 8 0.5C5.79 0.5 4 2.29 4 4.5V6H2.7C2.31 6 2 6.31 2 6.7V13.3C2 13.69 2.31 14 2.7 14H13.3C13.69 14 14 13.69 14 13.3V6.7C14 6.31 13.69 6 13.3 6ZM5.5 4.5C5.5 3.12 6.62 2 8 2C9.38 2 10.5 3.12 10.5 4.5V6H5.5V4.5Z" />
                        </svg>
                      </div>
                      <h4 class="font-semibold mt-3">Internal article</h4>
                    </div>
                  </div>

                  <!-- Legacy snippet -->
                  <div class="flex-1 content-cards__card">
                    <div
                      class="border border-neutral-border rounded-large p-4 flex flex-col cursor-pointer hover:shadow-lvl-1 transition-shadow flex-1"
                      @click="openAnswerTemplatesSheet"
                    >
                      <div class="w-8 h-8 border border-neutral-border rounded-lg bg-neutral-container flex items-center justify-center">
                        <svg class="interface-icon o__standard o__no-margin" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                          <path d="M13.5 2H2.5C2.22 2 2 2.22 2 2.5V13.5C2 13.78 2.22 14 2.5 14H10.25L14 10.25V2.5C14 2.22 13.78 2 13.5 2ZM7.99 9.7H3.99V8H7.99V9.7ZM11.99 5.7H3.99V4H11.99V5.7Z" />
                        </svg>
                      </div>
                      <h4 class="font-semibold mt-3">Legacy snippet</h4>
                    </div>
                  </div>

                  <!-- Website sync -->
                  <div class="flex-1 content-cards__card">
                    <div
                      class="border border-neutral-border rounded-large p-4 flex flex-col cursor-pointer hover:shadow-lvl-1 transition-shadow flex-1"
                      @click="openLinksSheet"
                    >
                      <div class="w-8 h-8 border border-neutral-border rounded-lg bg-neutral-container flex items-center justify-center">
                        <svg class="interface-icon o__standard o__no-margin" width="16" height="16" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 1.25C4.287 1.25 1.25 4.287 1.25 8S4.288 14.75 8 14.75c3.713 0 6.75-3.037 6.75-6.75S11.713 1.25 8 1.25zM6.038 12.862a5.242 5.242 0 0 1-3.257-4.553h1.32c.302 0 .548.245.548.548v1.392c0 .145.057.284.16.387l1.069 1.069c.103.103.16.242.16.387v.77zm5.77-1.27A5.223 5.223 0 0 1 8 13.25c-.269 0-.527-.039-.785-.079v-1.426c0-.303.245-.548.548-.548h.133a.548.548 0 0 0 .548-.548V8.86a.548.548 0 0 0-.548-.549h-1.87a.548.548 0 0 1-.549-.548v-.96c0-.275.11-.54.304-.734l1.37-1.37a1.04 1.04 0 0 0 .304-.734V2.805c.182-.019.359-.055.545-.055 1.386 0 2.64.55 3.58 1.43L9.698 6.062a.647.647 0 0 0-.19.458v1.035c0 .172.068.337.19.458l.424.424c.121.122.286.19.458.19h.58c.358 0 .648.29.648.648v2.318z" />
                        </svg>
                      </div>
                      <h4 class="font-semibold mt-3">Website sync</h4>
                    </div>
                  </div>

                  <div class="flex-1 content-cards__card">
                    <div
                      class="border border-neutral-border rounded-large p-4 flex flex-col cursor-pointer hover:shadow-lvl-1 transition-shadow flex-1"
                      @click="scrollRecommendationsIntoView"
                    >
                      <div class="w-8 h-8 border border-neutral-border rounded-lg bg-neutral-container flex items-center justify-center">
                        <span class="i-lucide-lightbulb size-4" />
                      </div>
                      <div class="mt-3 flex items-center justify-between gap-3">
                        <h4 class="font-semibold">Internal improvements</h4>
                        <span class="content-cards__suggestions-count-inline text-sm text-muted">
                          {{ isLoadingRecommendations ? '...' : `${recommendationsCount}` }}
                        </span>
                      </div>
                      <p class="mt-1 text-sm text-muted">
                        Review missed-question clusters in a secondary internal queue.
                      </p>
                      <div class="content-cards__suggestions-count-stacked text-sm text-muted mt-2">
                        {{ isLoadingRecommendations ? 'Loading...' : `${recommendationsCount} clusters` }}
                      </div>
                    </div>
                  </div>

                </div><!-- /content-cards__grid -->
              </div><!-- /add-content-section -->

              <!-- Content sources section -->
              <div ref="retrievalStrategySectionRef" class="flex flex-col">
                <h3 class="text-h3 font-semibold mb-2">Knowledge retrieval strategy</h3>
                <p class="mb-3 text-sm text-muted">
                  Control which Help Center scope the AI reads from, which runtime
                  source types it should emphasize first, and what should happen if
                  no configured evidence is available.
                </p>
                <div class="grid gap-3 md:grid-cols-3 mb-3">
                  <div
                    v-for="card in knowledgeRetrievalCards"
                    :key="card.label"
                    class="rounded-large border border-neutral-border bg-white px-4 py-3"
                  >
                    <div class="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
                      {{ card.label }}
                    </div>
                    <div class="mt-2 text-lg font-semibold text-default">
                      {{ card.value }}
                    </div>
                    <div class="mt-1 text-sm text-muted">
                      {{ card.note }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="retrievalWarnings.length"
                  class="mb-3 rounded-large border border-amber-200 bg-amber-50 px-4 py-3"
                >
                  <div class="text-xs font-semibold uppercase tracking-[0.08em] text-amber-700">
                    Retrieval watchouts
                  </div>
                  <div class="mt-2 grid gap-2">
                    <div
                      v-for="warning in retrievalWarnings"
                      :key="warning"
                      class="text-sm text-amber-900"
                    >
                      {{ warning }}
                    </div>
                  </div>
                </div>
                <div class="mb-3 rounded-large border border-neutral-border bg-white px-4 py-3">
                  <div class="text-sm font-semibold text-default">Knowledge source scope</div>
                  <div class="mt-1 text-sm text-muted">
                    This is the current real runtime scope for each source type.
                  </div>
                  <div class="mt-4 grid gap-3">
                    <div
                      v-for="row in knowledgeSourceStrategyRows"
                      :key="row.key"
                      class="rounded-large border border-neutral-border bg-n-alpha-2 px-4 py-3"
                    >
                      <div class="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                        <div class="min-w-0">
                          <div class="font-semibold text-default">{{ row.title }}</div>
                          <div class="text-sm text-muted mt-1">{{ row.note }}</div>
                        </div>
                        <div class="grid gap-1 text-sm lg:min-w-[260px]">
                          <div><strong>Owner:</strong> {{ row.owner }}</div>
                          <div><strong>Scope:</strong> {{ row.scope }}</div>
                          <div><strong>Status:</strong> {{ row.status }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 rounded-large border border-neutral-border bg-white px-4 py-3">
                  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                      <div class="text-sm font-semibold text-default">
                        Internal improvement snapshot
                      </div>
                      <div class="mt-1 text-sm text-muted">
                        This comes from the real durable content-gap snapshot and the
                        current internal improvement queue.
                      </div>
                    </div>
                    <div class="flex flex-wrap gap-2">
                      <button
                        type="button"
                        class="btn o__secondary"
                        :disabled="isCapturingRecommendationSnapshot"
                        @click="captureRecommendationSnapshot"
                      >
                        {{
                          isCapturingRecommendationSnapshot
                            ? 'Capturing snapshot...'
                            : 'Capture snapshot'
                        }}
                      </button>
                      <button
                        type="button"
                        class="btn o__secondary"
                        @click="scrollRecommendationsIntoView"
                      >
                        Open internal queue
                      </button>
                    </div>
                  </div>
                  <div class="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <div
                      v-for="card in contentOptimizationSnapshotCards"
                      :key="card.key"
                      class="rounded-large border border-neutral-border bg-n-alpha-2 px-4 py-3"
                    >
                      <div class="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">
                        {{ card.label }}
                      </div>
                      <div class="mt-2 text-lg font-semibold text-default">
                        {{ card.value }}
                      </div>
                      <div class="mt-1 text-sm text-muted">
                        {{ card.detail }}
                      </div>
                    </div>
                  </div>
                  <div v-if="topContentOptimizationAlerts.length" class="mt-4 grid gap-3">
                    <div
                      v-for="alert in topContentOptimizationAlerts"
                      :key="alert.key || alert.id || alert.label"
                      class="rounded-large border border-amber-200 bg-amber-50 px-4 py-3"
                    >
                      <div class="text-sm font-semibold text-amber-900">
                        {{ alert.title || alert.label || 'Optimization alert' }}
                      </div>
                      <div class="mt-1 text-sm text-amber-900">
                        {{ alert.message || alert.workflowSummaryLabel || 'Review this internal queue alert.' }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 rounded-large border border-neutral-border bg-n-alpha-2 px-4 py-3">
                  <div class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-default">
                        Article runtime owner
                      </div>
                      <div class="mt-1 text-sm text-default">
                        {{ runtimeOwnershipNote }}
                      </div>
                    </div>
                    <div class="grid gap-2 xl:min-w-[320px]">
                      <label class="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                        Runtime scope
                      </label>
                      <select
                        v-model="draftArticleScopeMode"
                        class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                        :disabled="isSavingContentOwnership"
                      >
                        <option value="inbox_portal">Follow inbox portal at runtime</option>
                        <option value="selected_portal">Pin one Help Center portal</option>
                      </select>
                      <select
                        v-if="draftArticleScopeMode === 'selected_portal'"
                        v-model="draftArticlePortalId"
                        class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                        :disabled="isSavingContentOwnership"
                      >
                        <option value="">Select a connected portal</option>
                        <option
                          v-for="portal in articleScopePortalOptions"
                          :key="portal.id"
                          :value="String(portal.id)"
                        >
                          {{ portal.name }}
                        </option>
                      </select>
                      <div class="text-xs text-muted">
                        {{ articleScopeStatusNote }}
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 rounded-large border border-neutral-border bg-white px-4 py-3">
                  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-default">
                        Channel-specific article scope
                      </div>
                      <div class="mt-1 text-sm text-muted">
                        Override the assistant-wide article scope for specific inboxes
                        when one channel needs a different Help Center portal strategy.
                      </div>
                    </div>
                    <div class="grid gap-3 lg:min-w-[620px]">
                      <div
                        v-if="!inboxBindingRows.length"
                        class="rounded-large border border-dashed border-neutral-border bg-n-alpha-2 px-4 py-3 text-sm text-muted"
                      >
                        Connect one or more inboxes to add per-channel retrieval scope overrides.
                      </div>
                      <div
                        v-for="row in inboxBindingRows"
                        :key="row.inbox_id"
                        class="rounded-large border border-neutral-border bg-n-alpha-2 px-4 py-3"
                      >
                        <div class="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                          <div class="min-w-0">
                            <div class="text-sm font-semibold text-default">
                              {{ row.name }}
                            </div>
                            <div class="mt-1 text-xs text-muted">
                              {{ row.channelType }}
                              <span v-if="row.inboxPortalName">
                                • Inbox portal: {{ row.inboxPortalName }}
                              </span>
                            </div>
                          </div>
                          <div class="grid gap-2 xl:min-w-[360px]">
                            <select
                              :value="row.article_scope_mode"
                              class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                              :disabled="isSavingContentOwnership"
                              @change="
                                updateInboxBindingRow(row.inbox_id, {
                                  article_scope_mode: $event.target.value,
                                  article_portal_id:
                                    $event.target.value === 'selected_portal'
                                      ? row.article_portal_id
                                      : '',
                                })
                              "
                            >
                              <option value="inherit">Inherit assistant-wide scope</option>
                              <option value="inbox_portal">Follow this inbox portal</option>
                              <option value="selected_portal">Pin one connected portal</option>
                            </select>
                            <select
                              v-if="row.article_scope_mode === 'selected_portal'"
                              :value="row.article_portal_id"
                              class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                              :disabled="isSavingContentOwnership"
                              @change="
                                updateInboxBindingRow(row.inbox_id, {
                                  article_portal_id: $event.target.value,
                                })
                              "
                            >
                              <option value="">Select a connected portal</option>
                              <option
                                v-for="portal in articleScopePortalOptions"
                                :key="portal.id"
                                :value="String(portal.id)"
                              >
                                {{ portal.name }}
                              </option>
                            </select>
                            <div class="text-xs text-muted">
                              <template v-if="row.article_scope_mode === 'inherit'">
                                This inbox uses the assistant-wide retrieval scope.
                              </template>
                              <template v-else-if="row.article_scope_mode === 'inbox_portal'">
                                Runtime resolves articles from this inbox's own portal.
                              </template>
                              <template v-else>
                                {{
                                  row.selectedPortalName
                                    ? `Pinned to ${row.selectedPortalName}.`
                                    : 'Choose one connected portal for this inbox override.'
                                }}
                              </template>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 rounded-large border border-neutral-border bg-white px-4 py-3">
                  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-default">
                        Segment-specific article scope
                      </div>
                      <div class="mt-1 text-sm text-muted">
                        Override the assistant-wide article scope for specific reply
                        languages, conversation languages, VIP conversations, or
                        detected attributes before the full segment engine lands.
                      </div>
                    </div>
                    <div class="grid gap-3 lg:min-w-[620px]">
                      <div
                        v-for="row in segmentBindingRows"
                        :key="row.key"
                        class="rounded-large border border-neutral-border bg-n-alpha-2 px-4 py-3"
                      >
                        <div class="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                          <div class="min-w-0">
                            <div class="text-sm font-semibold text-default">
                              {{ row.label }}
                            </div>
                            <div class="mt-1 text-xs text-muted">
                              {{ row.note }}
                            </div>
                          </div>
                          <div class="grid gap-2 xl:min-w-[360px]">
                            <select
                              :value="row.article_scope_mode"
                              class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                              :disabled="isSavingContentOwnership"
                              @change="
                                updateSegmentBindingRow(row.key, {
                                  article_scope_mode: $event.target.value,
                                  article_portal_id:
                                    $event.target.value === 'selected_portal'
                                      ? row.article_portal_id
                                      : '',
                                })
                              "
                            >
                              <option value="inherit">Inherit assistant-wide scope</option>
                              <option value="inbox_portal">Follow live inbox portal</option>
                              <option value="selected_portal">Pin one connected portal</option>
                            </select>
                            <select
                              v-if="row.article_scope_mode === 'selected_portal'"
                              :value="row.article_portal_id"
                              class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                              :disabled="isSavingContentOwnership"
                              @change="
                                updateSegmentBindingRow(row.key, {
                                  article_portal_id: $event.target.value,
                                })
                              "
                            >
                              <option value="">Select a connected portal</option>
                              <option
                                v-for="portal in articleScopePortalOptions"
                                :key="portal.id"
                                :value="String(portal.id)"
                              >
                                {{ portal.name }}
                              </option>
                            </select>
                            <div class="text-xs text-muted">
                              <template v-if="row.article_scope_mode === 'inherit'">
                                This segment uses the assistant-wide retrieval scope.
                              </template>
                              <template v-else-if="row.article_scope_mode === 'inbox_portal'">
                                Runtime resolves articles from the live inbox portal when this segment matches.
                              </template>
                              <template v-else>
                                {{
                                  row.selectedPortalName
                                    ? `Pinned to ${row.selectedPortalName} when this segment matches.`
                                    : 'Choose one connected portal for this segment override.'
                                }}
                              </template>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="rounded-large border border-dashed border-neutral-border bg-white px-4 py-3">
                        <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div class="min-w-0">
                            <div class="text-sm font-semibold text-default">
                              Detected-attribute article scope
                            </div>
                            <div class="mt-1 text-xs text-muted">
                              Use saved conversation attributes as presets, then match a
                              persisted detected attribute like
                              <code>loan_stage</code> or <code>product_name</code>
                              before switching Help Center scope.
                            </div>
                          </div>
                          <div class="flex items-center gap-2">
                            <span class="text-xs text-muted">
                              {{ detectedAttributeBindingRows.length }}/{{ MAX_DETECTED_ATTRIBUTE_SEGMENT_BINDINGS }}
                            </span>
                            <button
                              type="button"
                              class="inline-flex items-center justify-center rounded-lg border border-neutral-border px-3 py-2 text-sm font-medium text-default hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-60"
                              :disabled="
                                isSavingContentOwnership ||
                                detectedAttributeBindingRows.length >=
                                  MAX_DETECTED_ATTRIBUTE_SEGMENT_BINDINGS
                              "
                              @click="addDetectedAttributeBindingRow"
                            >
                              Add attribute override
                            </button>
                          </div>
                        </div>
                        <div
                          v-if="detectedAttributeBindingRows.length"
                          class="mt-3 grid gap-3"
                        >
                          <div
                            v-for="row in detectedAttributeBindingRows"
                            :key="row.key"
                            class="rounded-large border border-neutral-border bg-n-alpha-2 px-4 py-3"
                          >
                            <div class="grid gap-3 xl:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto]">
                              <label class="grid gap-2 text-sm text-default">
                                <span class="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                                  Saved attribute
                                </span>
                                <select
                                  :value="row.presetSelectValue"
                                  class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                                  :disabled="isSavingContentOwnership"
                                  @change="
                                    updateDetectedAttributePresetSelection(
                                      row,
                                      $event.target.value
                                    )
                                  "
                                >
                                  <option value="">Select one saved attribute</option>
                                  <option
                                    v-for="option in retrievalDetectedAttributePresetOptions"
                                    :key="option.key"
                                    :value="option.key"
                                  >
                                    {{ option.label }}
                                  </option>
                                  <option value="__custom__">Custom attribute key</option>
                                </select>
                              </label>
                              <label
                                v-if="
                                  row.presetSelectValue === '__custom__' ||
                                  !retrievalDetectedAttributePresetOptions.length
                                "
                                class="grid gap-2 text-sm text-default"
                              >
                                <span class="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                                  Custom key
                                </span>
                                <input
                                  :value="row.attribute_key"
                                  type="text"
                                  class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                                  :disabled="isSavingContentOwnership"
                                  placeholder="loan_stage"
                                  @input="
                                    updateDetectedAttributeBindingRow(row.key, {
                                      attribute_key: $event.target.value,
                                    })
                                  "
                                />
                              </label>
                              <label class="grid gap-2 text-sm text-default">
                                <span class="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                                  Match rule
                                </span>
                                <select
                                  :value="row.match_type"
                                  class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                                  :disabled="isSavingContentOwnership"
                                  @change="
                                    updateDetectedAttributeBindingRow(row.key, {
                                      match_type: $event.target.value,
                                      match_value:
                                        $event.target.value === 'present'
                                          ? ''
                                          : row.match_value,
                                    })
                                  "
                                >
                                  <option
                                    v-for="option in DETECTED_ATTRIBUTE_MATCH_TYPE_OPTIONS"
                                    :key="option.value"
                                    :value="option.value"
                                  >
                                    {{ option.label }}
                                  </option>
                                </select>
                              </label>
                              <label class="grid gap-2 text-sm text-default">
                                <span class="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                                  Match value
                                </span>
                                <input
                                  v-if="row.match_type !== 'present'"
                                  :value="row.match_value"
                                  type="text"
                                  :list="
                                    row.detectedAttributeDefinition?.values?.length
                                      ? `retrieval-attribute-values-${row.key}`
                                      : null
                                  "
                                  class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                                  :disabled="isSavingContentOwnership"
                                  placeholder="late_payment"
                                  @input="
                                    updateDetectedAttributeBindingRow(row.key, {
                                      match_value: $event.target.value,
                                    })
                                  "
                                />
                                <div
                                  v-else
                                  class="rounded-lg border border-neutral-border bg-n-alpha-2 px-3 py-2 text-sm text-muted"
                                >
                                  Any non-empty detected value
                                </div>
                                <datalist
                                  v-if="row.detectedAttributeDefinition?.values?.length"
                                  :id="`retrieval-attribute-values-${row.key}`"
                                >
                                  <option
                                    v-for="value in row.detectedAttributeDefinition.values"
                                    :key="`${row.key}-value-${value}`"
                                    :value="value"
                                  />
                                </datalist>
                              </label>
                              <div class="grid gap-2">
                                <span class="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                                  Scope override
                                </span>
                                <select
                                  :value="row.article_scope_mode"
                                  class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                                  :disabled="isSavingContentOwnership"
                                  @change="
                                    updateDetectedAttributeBindingRow(row.key, {
                                      article_scope_mode: $event.target.value,
                                      article_portal_id:
                                        $event.target.value === 'selected_portal'
                                          ? row.article_portal_id
                                          : '',
                                    })
                                  "
                                >
                                  <option value="inherit">Inherit assistant-wide scope</option>
                                  <option value="inbox_portal">Follow live inbox portal</option>
                                  <option value="selected_portal">Pin one connected portal</option>
                                </select>
                                <select
                                  v-if="row.article_scope_mode === 'selected_portal'"
                                  :value="row.article_portal_id"
                                  class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                                  :disabled="isSavingContentOwnership"
                                  @change="
                                    updateDetectedAttributeBindingRow(row.key, {
                                      article_portal_id: $event.target.value,
                                    })
                                  "
                                >
                                  <option value="">Select a connected portal</option>
                                  <option
                                    v-for="portal in articleScopePortalOptions"
                                    :key="portal.id"
                                    :value="String(portal.id)"
                                  >
                                    {{ portal.name }}
                                  </option>
                                </select>
                              </div>
                              <div class="flex items-start justify-end">
                                <button
                                  type="button"
                                  class="inline-flex items-center justify-center rounded-lg border border-transparent px-3 py-2 text-sm font-medium text-muted hover:border-neutral-border hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                                  :disabled="isSavingContentOwnership"
                                  @click="removeDetectedAttributeBindingRow(row.key)"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                            <div class="mt-2 text-xs text-muted">
                              <div v-if="row.detectedAttributeDefinition" class="mb-1">
                                <span class="font-medium text-default">
                                  {{ row.detectedAttributeDefinition.label }}
                                </span>
                                <span v-if="row.detectedAttributeDefinition.description">
                                  • {{ row.detectedAttributeDefinition.description }}
                                </span>
                                <span v-else-if="row.detectedAttributeDefinition.regexCue">
                                  • Cue: {{ row.detectedAttributeDefinition.regexCue }}
                                </span>
                              </div>
                              <template v-if="row.article_scope_mode === 'inherit'">
                                This detected attribute rule keeps the assistant-wide retrieval scope.
                              </template>
                              <template v-else-if="row.article_scope_mode === 'inbox_portal'">
                                Matching this attribute rule makes article scope follow the live inbox portal.
                              </template>
                              <template v-else>
                                {{
                                  row.selectedPortalName
                                    ? `Pinned to ${row.selectedPortalName} when this detected attribute rule matches.`
                                    : 'Choose one connected portal for this attribute override.'
                                }}
                              </template>
                            </div>
                          </div>
                        </div>
                        <div v-else class="mt-3 text-xs text-muted">
                          No detected-attribute overrides yet. Add one when a persisted
                          conversation attribute should change article scope.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 rounded-large border border-neutral-border bg-white px-4 py-3">
                  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-default">
                        Retrieval depth
                      </div>
                      <div class="mt-1 text-sm text-muted">
                        Control how much Help Center evidence the runtime can pack into
                        each answer before it falls back to the rest of the AI context.
                      </div>
                    </div>
                    <div class="grid gap-3 lg:min-w-[420px] lg:grid-cols-2">
                      <label class="grid gap-2 text-sm text-default">
                        <span class="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                          Sources per answer
                        </span>
                        <select
                          v-model="draftMaxSources"
                          class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                          :disabled="isSavingContentOwnership"
                        >
                          <option
                            v-for="option in KNOWLEDGE_SOURCE_LIMIT_OPTIONS"
                            :key="option.value"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                      </label>
                      <label class="grid gap-2 text-sm text-default">
                        <span class="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                          Context budget
                        </span>
                        <select
                          v-model="draftContextBudget"
                          class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                          :disabled="isSavingContentOwnership"
                        >
                          <option
                            v-for="option in CONTEXT_BUDGET_OPTIONS"
                            :key="option.value"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                      </label>
                    </div>
                  </div>
                  <div class="mt-3 grid gap-2 lg:grid-cols-3">
                    <div
                      v-for="option in CONTEXT_BUDGET_OPTIONS"
                      :key="option.value"
                      class="rounded-large border border-neutral-border bg-n-alpha-2 px-3 py-2"
                      :class="draftContextBudget === option.value ? 'border-woot-500 bg-woot-50' : ''"
                    >
                      <div class="text-sm font-semibold text-default">{{ option.label }}</div>
                      <div class="mt-1 text-xs text-muted">{{ option.note }}</div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 rounded-large border border-neutral-border bg-white px-4 py-3">
                  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-default">
                        Confidence floor
                      </div>
                      <div class="mt-1 text-sm text-muted">
                        Set the minimum article-match confidence required before retrieved
                        Help Center content is allowed into runtime context.
                      </div>
                    </div>
                    <div class="grid gap-3 lg:min-w-[420px]">
                      <select
                        v-model="draftMinConfidenceScore"
                        class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                        :disabled="isSavingContentOwnership"
                      >
                        <option
                          v-for="option in RETRIEVAL_CONFIDENCE_OPTIONS"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                      <div class="grid gap-2 lg:grid-cols-3">
                        <div
                          v-for="option in RETRIEVAL_CONFIDENCE_OPTIONS"
                          :key="option.value"
                          class="rounded-large border border-neutral-border bg-n-alpha-2 px-3 py-2"
                          :class="
                            normalizeMinConfidenceScore(draftMinConfidenceScore) ===
                            normalizeMinConfidenceScore(option.value)
                              ? 'border-woot-500 bg-woot-50'
                              : ''
                          "
                        >
                          <div class="text-sm font-semibold text-default">{{ option.label }}</div>
                          <div class="mt-1 text-xs text-muted">{{ option.note }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 rounded-large border border-neutral-border bg-white px-4 py-3">
                  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-default">
                        Search balance
                      </div>
                      <div class="mt-1 text-sm text-muted">
                        Decide whether runtime article retrieval should favor exact
                        keyword matches first or let semantic recall lead.
                      </div>
                    </div>
                    <div class="grid gap-3 lg:min-w-[420px]">
                      <select
                        v-model="draftSearchBalance"
                        class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                        :disabled="isSavingContentOwnership"
                      >
                        <option
                          v-for="option in SEARCH_BALANCE_OPTIONS"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                      <div class="grid gap-2">
                        <div
                          v-for="option in SEARCH_BALANCE_OPTIONS"
                          :key="option.value"
                          class="rounded-large border border-neutral-border bg-n-alpha-2 px-3 py-2"
                          :class="draftSearchBalance === option.value ? 'border-woot-500 bg-woot-50' : ''"
                        >
                          <div class="text-sm font-semibold text-default">{{ option.label }}</div>
                          <div class="mt-1 text-xs text-muted">{{ option.note }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 rounded-large border border-neutral-border bg-white px-4 py-3">
                  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-default">
                        Source priority
                      </div>
                      <div class="mt-1 text-sm text-muted">
                        This controls the order the runtime prompt uses when article
                        knowledge, snippets, links, and uploaded documents are all
                        available at the same time.
                      </div>
                    </div>
                    <div class="grid gap-2 lg:min-w-[520px]">
                      <div
                        v-for="item in orderedSourcePriorityItems"
                        :key="item.key"
                        class="rounded-large border border-neutral-border bg-n-alpha-2 px-4 py-3"
                      >
                        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div class="min-w-0">
                            <div class="text-xs font-semibold uppercase tracking-[0.08em] text-muted">
                              Priority {{ item.rank }}
                            </div>
                            <div class="mt-1 text-sm font-semibold text-default">
                              {{ item.label }}
                            </div>
                            <div class="mt-1 text-xs text-muted">
                              {{ item.note }}
                            </div>
                          </div>
                          <div class="flex items-center gap-2">
                            <button
                              type="button"
                              class="rounded-lg border border-neutral-border bg-white px-3 py-2 text-xs font-medium text-default disabled:cursor-not-allowed disabled:opacity-50"
                              :disabled="!item.canMoveUp || isSavingContentOwnership"
                              @click="moveSourcePriorityItem(item.key, -1)"
                            >
                              Move up
                            </button>
                            <button
                              type="button"
                              class="rounded-lg border border-neutral-border bg-white px-3 py-2 text-xs font-medium text-default disabled:cursor-not-allowed disabled:opacity-50"
                              :disabled="!item.canMoveDown || isSavingContentOwnership"
                              @click="moveSourcePriorityItem(item.key, 1)"
                            >
                              Move down
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 rounded-large border border-neutral-border bg-white px-4 py-3">
                  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-default">
                        Citation strategy
                      </div>
                      <div class="mt-1 text-sm text-muted">
                        Keep citation behavior with retrieval so source-backed answers
                        can stay transparent without forcing every reply to look the same.
                      </div>
                    </div>
                    <div class="grid gap-3 lg:min-w-[420px]">
                      <select
                        v-model="draftCitationMode"
                        class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                        :disabled="isSavingContentOwnership"
                      >
                        <option
                          v-for="option in RETRIEVAL_CITATION_MODE_OPTIONS"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                      <div class="grid gap-2">
                        <div
                          v-for="option in RETRIEVAL_CITATION_MODE_OPTIONS"
                          :key="option.value"
                          class="rounded-large border border-neutral-border bg-n-alpha-2 px-3 py-2"
                          :class="draftCitationMode === option.value ? 'border-woot-500 bg-woot-50' : ''"
                        >
                          <div class="text-sm font-semibold text-default">{{ option.label }}</div>
                          <div class="mt-1 text-xs text-muted">{{ option.note }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 rounded-large border border-neutral-border bg-white px-4 py-3">
                  <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div class="min-w-0">
                      <div class="text-sm font-semibold text-default">
                        No-evidence fallback
                      </div>
                      <div class="mt-1 text-sm text-muted">
                        Choose the runtime behavior when no configured retrieval
                        evidence is available for the customer's question.
                      </div>
                    </div>
                    <div class="grid gap-3 lg:min-w-[420px]">
                      <select
                        v-model="draftNoEvidenceBehavior"
                        class="w-full rounded-lg border border-neutral-border bg-white px-3 py-2 text-sm text-default"
                        :disabled="isSavingContentOwnership"
                      >
                        <option
                          v-for="option in NO_EVIDENCE_BEHAVIOR_OPTIONS"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                      <div class="grid gap-2">
                        <div
                          v-for="option in NO_EVIDENCE_BEHAVIOR_OPTIONS"
                          :key="option.value"
                          class="rounded-large border border-neutral-border bg-n-alpha-2 px-3 py-2"
                          :class="draftNoEvidenceBehavior === option.value ? 'border-woot-500 bg-woot-50' : ''"
                        >
                          <div class="text-sm font-semibold text-default">{{ option.label }}</div>
                          <div class="mt-1 text-xs text-muted">{{ option.note }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="mb-3 flex items-center justify-between gap-3">
                  <h4 class="text-base font-semibold text-default">Live content sources</h4>
                  <div class="text-sm text-muted">
                    {{ filteredContentSources.length }} source types
                  </div>
                </div>
                <div>
                  <div class="all-content-viewport overflow-auto all-content-viewport__full-height">
                    <div class="flex flex-col all-content-scroll-container h-full">
                      <div class="flex flex-col ml-2 mr-1">
                        <div class="all-content-table o__hide-selection-columns">
                          <table class="ds-new__table">
                            <thead class="ds-new__table__head">
                              <tr class="ds-new__table__row">
                                <th class="ds-new__table__column-header o__draggable"></th>
                                <th class="ds-new__table__column-header o__checkbox"></th>
                                <th class="ds-new__table__column-header o__active-sort o__sortable" tabindex="0" scope="col">
                                  <span class="ds-new__table__column__label">
                                    Title
                                    <span class="ds-new__table__column__sort">
                                      <svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg" class="ds-new__table__column__sort-icon o__asc o__active-sort-direction">
                                        <path d="M4 2.10299L1.4751 4.5L0.5 3.375L4 0L7.5 3.375L6.5249 4.5L4 2.10299Z" />
                                      </svg>
                                      <svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg" class="ds-new__table__column__sort-icon o__desc">
                                        <path d="M4 2.89701L6.5249 0.5L7.5 1.625L4 5L0.5 1.625L1.4751 0.5L4 2.89701Z" />
                                      </svg>
                                    </span>
                                  </span>
                                </th>
                                <th class="ds-new__table__column-header" scope="col">
                                  <span class="ds-new__table__column__label">Status</span>
                                </th>
                                <th class="ds-new__table__column-header" scope="col">
                                  <span class="ds-new__table__column__label">AI Agent</span>
                                </th>
                                <th class="ds-new__table__column-header" scope="col">
                                  <span class="ds-new__table__column__label"></span>
                                </th>
                              </tr>
                            </thead>
                            <tbody class="ds-new__table__body">
                              <tr v-if="!filteredContentSources.length" class="ds-new__table__row">
                                <td class="ds-new__table__cell o__text" colspan="6">
                                  <div class="ds-new__table__cell-shim justify-center py-6">
                                    <span class="text-muted">No content sources found.</span>
                                  </div>
                                </td>
                              </tr>
                              <tr
                                v-for="source in filteredContentSources"
                                :key="source.key"
                                class="ds-new__table__row"
                              >
                                <!-- drag handle cell -->
                                <td class="ds-new__table__cell o__text">
                                  <div class="ds-new__table__cell-shim">
                                    <span class="draggable-icon"><div class="min-w-4 inline-flex"></div></span>
                                  </div>
                                </td>
                                <!-- checkbox cell -->
                                <td class="ds-new__table__cell o__checkbox">
                                  <div class="ds-new__table__cell-shim">
                                    <div class="flex h-full justify-start">
                                      <div class="hidden-checkbox inline-flex"></div>
                                    </div>
                                  </div>
                                </td>
                                <!-- Title cell -->
                                <td class="ds-new__table__cell o__text">
                                  <div class="ds-new__table__cell-shim">
                                    <div class="flex group flex-row gap-2 items-baseline cursor-pointer">
                                      <div class="overflow-hidden flex-grow">
                                        <div class="u__one-truncated-line no-underline block pr-1 hover:underline hover:text-accent">
                                          <div class="flex flex-row items-center truncate">
                                            <div class="flex items-center justify-center mr-2">
                                              <span :class="[source.icon, 'size-4 text-muted']" />
                                            </div>
                                            <span class="u__one-truncated-line">
                                              {{ source.title }}
                                              <span class="text-muted">{{ source.subtitle }}</span>
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <!-- Status cell -->
                                <td class="ds-new__table__cell o__text">
                                  <div class="ds-new__table__cell-shim">
                                    <div class="flex items-center gap-2 whitespace-nowrap">
                                      <div
                                        v-if="source.statusDot"
                                        class="w-4 h-4 rounded-full shrink-0 border-4 bg-success-fill-emphasis border-success-container"
                                      ></div>
                                      <div
                                        v-else
                                        class="w-4 h-4 rounded-full shrink-0 border-4 bg-neutral-400 border-neutral-200"
                                      ></div>
                                      <span class="text-sm text-default leading-5">{{ source.statusText }}</span>
                                    </div>
                                  </div>
                                </td>
                                <!-- AI Agent cell -->
                                <td class="ds-new__table__cell o__text">
                                  <div class="ds-new__table__cell-shim">
                                    <span class="flex items-center gap-2">
                                      <svg
                                        v-if="source.aiEnabled"
                                        class="interface-icon o__standard o__palette-text-success"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path d="M5.76003 12.95L2.15003 9.34C1.82003 9.01 1.82003 8.47 2.15003 8.14C2.48003 7.81 3.02002 7.81 3.35002 8.14L5.76003 10.55L12.65 3.66C12.98 3.33 13.52 3.33 13.85 3.66C14.18 3.99 14.18 4.53 13.85 4.86L5.76003 12.95Z" />
                                      </svg>
                                      <span class="text-sm text-default">{{ source.aiCount }}</span>
                                    </span>
                                  </div>
                                </td>
                                <!-- Spacer cell -->
                                <td class="ds-new__table__cell o__text">
                                  <div class="ds-new__table__cell-shim justify-end">
                                    <button
                                      v-if="source.manageTarget"
                                      type="button"
                                      class="fin-link text-sm"
                                      @click="handleManageContentSource(source)"
                                    >
                                      Manage
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div><!-- /content sources section -->

              <details ref="recommendationsSectionRef" class="fin-advanced-details mt-8">
                <summary>Internal improvement queue</summary>
                <div class="fin-advanced-details__body">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h3 class="text-h3 font-semibold mb-2">Internal improvements</h3>
                    <p class="text-sm text-muted">
                      This is an internal queue for repeated misses and article
                      opportunities. It is intentionally collapsed by default so
                      the main page stays focused on live customer-facing content.
                    </p>
                    <p
                      v-if="recommendationQueueSummary"
                      class="mt-2 text-xs uppercase tracking-[0.08em] text-slate-400"
                    >
                      Queue mix: {{ recommendationQueueSummary }}
                    </p>
                    <p class="mt-2 text-sm text-slate-500">
                      Active lane: {{ activeRecommendationPresetLabel }} · Sort:
                      {{
                        recommendationSortOptions.find(option => option.id === recommendationSort)?.label ||
                        recommendationSort
                      }}
                    </p>
                    <div class="mt-3 flex items-center gap-3">
                      <div class="text-sm text-slate-500">
                        {{ recommendationSnapshotLabel }}
                      </div>
                      <button
                        type="button"
                        class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                        :disabled="isCapturingRecommendationSnapshot"
                        @click="captureRecommendationSnapshot"
                      >
                        {{
                          isCapturingRecommendationSnapshot
                            ? 'Capturing snapshot...'
                            : 'Capture snapshot'
                        }}
                      </button>
                    </div>
                    <div
                      v-if="activeRecommendationAlert"
                      class="mt-3 rounded-large border border-sky-200 bg-sky-50 px-4 py-4"
                    >
                      <div class="flex items-start justify-between gap-3">
                        <div>
                          <div class="text-xs uppercase tracking-[0.08em] text-sky-700">
                            Alert drill-down active
                          </div>
                          <div class="mt-1 text-sm font-semibold text-sky-900">
                            {{ activeRecommendationAlert.drilldownContext?.title || activeRecommendationAlert.title }}
                          </div>
                          <div class="mt-1 text-sm text-sky-800">
                            {{
                              activeRecommendationAlert.drilldownContext?.summary ||
                              activeRecommendationAlert.message
                            }}
                          </div>
                          <div class="mt-2 flex flex-wrap gap-2 text-xs text-sky-700">
                            <span class="rounded-full border border-sky-300 px-2 py-1">
                              {{ recommendationAlertThresholdLabel(activeRecommendationAlert) }}
                            </span>
                            <span class="rounded-full border border-sky-300 px-2 py-1">
                              {{ recommendationAlertCapacityBandLabel(activeRecommendationAlert) }}
                            </span>
                            <span
                              v-if="activeRecommendationAlert.thresholdProfile?.label"
                              class="rounded-full border border-sky-300 px-2 py-1"
                            >
                              {{ activeRecommendationAlert.thresholdProfile.label }}
                            </span>
                            <span
                              class="rounded-full border border-sky-300 px-2 py-1"
                            >
                              {{
                                recommendationThresholdProfileSourceLabel(
                                  activeRecommendationAlert.thresholdProfile
                                )
                              }}
                            </span>
                            <span
                              v-if="
                                activeRecommendationAlert.workflowSessionState &&
                                activeRecommendationAlert.workflowSessionState !== 'none'
                              "
                              class="rounded-full border border-sky-300 px-2 py-1"
                            >
                              {{
                                recommendationAlertWorkflowStateLabel(
                                  activeRecommendationAlert
                                )
                              }}
                            </span>
                            <span
                              v-if="
                                activeRecommendationAlert.workflowDueStatus &&
                                activeRecommendationAlert.workflowDueStatus !== 'none'
                              "
                              class="rounded-full border border-sky-300 px-2 py-1"
                            >
                              {{
                                recommendationAlertDueStatusLabel(
                                  activeRecommendationAlert
                                )
                              }}
                            </span>
                            <span
                              v-if="activeRecommendationAlert.workflowNeedsFollowup"
                              class="rounded-full border border-sky-300 px-2 py-1"
                            >
                              Needs follow-up
                            </span>
                            <span
                              v-for="item in activeRecommendationAlert.drilldownContext?.applied_filters || []"
                              :key="`alert-filter-${item}`"
                              class="rounded-full border border-sky-300 px-2 py-1"
                            >
                              {{ item }}
                            </span>
                          </div>
                          <div
                            v-if="activeRecommendationAlert.drilldownContext?.reason"
                            class="mt-2 text-xs text-sky-700"
                          >
                            {{ activeRecommendationAlert.drilldownContext.reason }}
                          </div>
                          <div
                            v-if="activeRecommendationAlert.thresholdProfile?.rationale"
                            class="mt-2 text-xs text-sky-700"
                          >
                            {{ activeRecommendationAlert.thresholdProfile.rationale }}
                          </div>
                          <div
                            v-if="activeRecommendationAlert.workflowSummaryLabel"
                            class="mt-2 text-xs text-sky-700"
                          >
                            {{ activeRecommendationAlert.workflowSummaryLabel }}
                          </div>
                          <div
                            v-if="activeRecommendationAlert.workflowReopenReason"
                            class="mt-2 text-xs text-sky-700"
                          >
                            {{ activeRecommendationAlert.workflowReopenReason }}
                          </div>
                          <div
                            v-if="activeRecommendationAlert.workflowOwnerLabel"
                            class="mt-2 text-xs text-sky-700"
                          >
                            Owner: {{ activeRecommendationAlert.workflowOwnerLabel }}
                          </div>
                          <div
                            v-if="activeRecommendationAlert.workflowDueAt"
                            class="mt-2 text-xs text-sky-700"
                          >
                            Due: {{ formatRecommendationDate(activeRecommendationAlert.workflowDueAt) }}
                          </div>
                          <div
                            v-if="activeRecommendationAlert.workflowResolutionOutcome"
                            class="mt-2 text-xs text-sky-700"
                          >
                            Resolution outcome:
                            {{ activeRecommendationAlert.workflowResolutionOutcome }}
                          </div>
                          <div
                            v-if="activeRecommendationAlert.workflowHandoffReason"
                            class="mt-2 text-xs text-sky-700"
                          >
                            Handoff reason:
                            {{ activeRecommendationAlert.workflowHandoffReason }}
                          </div>
                          <div
                            v-if="activeRecommendationAlert.workflowNextCheckpoint"
                            class="mt-2 text-xs text-sky-700"
                          >
                            Next checkpoint:
                            {{ activeRecommendationAlert.workflowNextCheckpoint }}
                          </div>
                          <div
                            v-if="activeRecommendationAlert.workflowNote"
                            class="mt-2 text-xs text-sky-700"
                          >
                            {{ activeRecommendationAlert.workflowNote }}
                          </div>
                          <div
                            v-if="activeRecommendationAlert.workflowLastEventLabel"
                            class="mt-2 text-xs text-sky-700"
                          >
                            Last activity:
                            {{ activeRecommendationAlert.workflowLastEventLabel }}
                            <span v-if="activeRecommendationAlert.workflowLastActorName">
                              · {{ activeRecommendationAlert.workflowLastActorName }}
                            </span>
                            <span v-if="activeRecommendationAlert.workflowLastEventAt">
                              ·
                              {{
                                formatRecommendationDate(
                                  activeRecommendationAlert.workflowLastEventAt
                                )
                              }}
                            </span>
                          </div>
                          <div
                            v-if="activeRecommendationAlert.thresholdProfile?.override_fields?.length"
                            class="mt-2 text-xs text-sky-700"
                          >
                            Override fields:
                            {{ activeRecommendationAlert.thresholdProfile.override_fields.join(', ') }}
                          </div>
                          <div
                            v-if="
                              recommendationAlertWorkflowDrafts[
                                activeRecommendationAlert.key
                              ]
                            "
                            class="mt-3 rounded-large border border-sky-200 bg-white px-3 py-3"
                          >
                            <div class="text-xs uppercase tracking-[0.08em] text-sky-700">
                              Workflow details
                            </div>
                            <div class="mt-3 grid gap-2 md:grid-cols-2">
                              <label class="block">
                                <span class="text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                                  Owner
                                </span>
                                <input
                                  v-model="
                                    recommendationAlertWorkflowDrafts[
                                      activeRecommendationAlert.key
                                    ].ownerLabel
                                  "
                                  type="text"
                                  class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900"
                                  placeholder="Assign a workflow owner"
                                />
                              </label>
                              <label class="block">
                                <span class="text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                                  Due date
                                </span>
                                <input
                                  v-model="
                                    recommendationAlertWorkflowDrafts[
                                      activeRecommendationAlert.key
                                    ].dueDate
                                  "
                                  type="date"
                                  class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900"
                                />
                              </label>
                            </div>
                            <div class="mt-2 grid gap-2 md:grid-cols-2">
                              <label class="block">
                                <span class="text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                                  Resolution outcome
                                </span>
                                <input
                                  v-model="
                                    recommendationAlertWorkflowDrafts[
                                      activeRecommendationAlert.key
                                    ].resolutionOutcome
                                  "
                                  type="text"
                                  class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900"
                                  placeholder="Published article, scope fixed, waiting on policy..."
                                />
                              </label>
                              <label class="block">
                                <span class="text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                                  Handoff reason
                                </span>
                                <input
                                  v-model="
                                    recommendationAlertWorkflowDrafts[
                                      activeRecommendationAlert.key
                                    ].handoffReason
                                  "
                                  type="text"
                                  class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900"
                                  placeholder="Why this moved to another owner"
                                />
                              </label>
                            </div>
                            <label class="mt-2 block">
                              <span class="text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                                Next checkpoint
                              </span>
                              <input
                                v-model="
                                  recommendationAlertWorkflowDrafts[
                                    activeRecommendationAlert.key
                                  ].nextCheckpoint
                                "
                                type="text"
                                class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900"
                                placeholder="What follow-up should happen next"
                              />
                            </label>
                            <label class="mt-2 block">
                              <span class="text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                                Operator note
                              </span>
                              <textarea
                                v-model="
                                  recommendationAlertWorkflowDrafts[
                                    activeRecommendationAlert.key
                                  ].note
                                "
                                rows="3"
                                class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900"
                                placeholder="Capture the current owner plan or follow-up note."
                              />
                            </label>
                          </div>
                          <div
                            v-if="activeRecommendationAlert.recommendedNextSteps?.length"
                            class="mt-3 rounded-large border border-sky-200 bg-white px-3 py-3"
                          >
                            <div class="text-xs uppercase tracking-[0.08em] text-sky-700">
                              Recommended next steps
                            </div>
                            <div
                              v-for="step in activeRecommendationAlert.recommendedNextSteps"
                              :key="`alert-step-${step}`"
                              class="mt-2 text-sm text-sky-900"
                            >
                              {{ step }}
                            </div>
                          </div>
                          <div
                            v-if="activeRecommendationAlert.resolutionChecklist?.length"
                            class="mt-3 rounded-large border border-sky-200 bg-white px-3 py-3"
                          >
                            <div class="text-xs uppercase tracking-[0.08em] text-sky-700">
                              Resolution checklist
                            </div>
                            <div
                              v-for="item in activeRecommendationAlert.resolutionChecklist"
                              :key="`alert-check-${item}`"
                              class="mt-2 text-sm text-sky-900"
                            >
                              {{ item }}
                            </div>
                          </div>
                        </div>
                        <div class="flex flex-col gap-2">
                          <button
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700"
                            @click="openRecommendationWorkflowAudit(activeRecommendationAlert)"
                          >
                            Open audit trail
                          </button>
                          <button
                            v-if="
                              activeRecommendationAlert.workflowStatus &&
                              activeRecommendationAlert.workflowStatus !== 'none'
                            "
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="
                              savingRecommendationAlertWorkflowKey ===
                                activeRecommendationAlert.key ||
                              !recommendationAlertWorkflowMetadataDirty(
                                activeRecommendationAlert
                              )
                            "
                            @click="
                              saveRecommendationAlertWorkflowDetails(
                                activeRecommendationAlert
                              )
                            "
                          >
                            Save details
                          </button>
                          <button
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="
                              savingRecommendationAlertWorkflowKey ===
                              activeRecommendationAlert.key
                            "
                            @click="
                              saveRecommendationAlertWorkflow(
                                activeRecommendationAlert,
                                'resolved'
                              )
                            "
                          >
                            Mark resolved
                          </button>
                          <button
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="
                              savingRecommendationAlertWorkflowKey ===
                              activeRecommendationAlert.key
                            "
                            @click="
                              saveRecommendationAlertWorkflow(
                                activeRecommendationAlert,
                                'snoozed'
                              )
                            "
                          >
                            Snooze 3 days
                          </button>
                          <button
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="
                              savingRecommendationAlertWorkflowKey ===
                              activeRecommendationAlert.key
                            "
                            @click="
                              saveRecommendationAlertWorkflow(
                                activeRecommendationAlert,
                                'handoff'
                              )
                            "
                          >
                            Mark handed off
                          </button>
                          <button
                            v-if="
                              activeRecommendationAlert.workflowStatus &&
                              activeRecommendationAlert.workflowStatus !== 'none'
                            "
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="
                              savingRecommendationAlertWorkflowKey ===
                              activeRecommendationAlert.key
                            "
                            @click="
                              clearRecommendationAlertWorkflow(
                                activeRecommendationAlert
                              )
                            "
                          >
                            Clear workflow
                          </button>
                          <button
                            v-if="activeRecommendationAlert.recommendedActionLabel"
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700"
                            @click="applyRecommendationAlert(activeRecommendationAlert)"
                          >
                            {{ activeRecommendationAlert.recommendedActionLabel }}
                          </button>
                          <button
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700"
                            @click="clearRecommendationAlert"
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    </div>
                    <div class="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                      <div
                        v-for="card in recommendationSnapshotCards"
                        :key="`snapshot-${card.key}`"
                        class="rounded-large border border-dashed border-slate-200 bg-white px-3 py-3"
                      >
                        <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                          {{ card.label }}
                        </div>
                        <div class="mt-1 text-sm font-semibold text-slate-900">
                          {{ card.value }}
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="recommendationTrajectoryCards.some(card => card.value)"
                      class="mt-3 rounded-large border border-neutral-border bg-base-module px-4 py-4"
                    >
                      <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                        Backlog trajectory
                      </div>
                      <div class="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                        <div
                          v-for="card in recommendationTrajectoryCards"
                          :key="`trajectory-${card.key}`"
                          class="rounded-large border border-slate-200 bg-white px-3 py-3"
                        >
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            {{ card.label }}
                          </div>
                          <div class="mt-1 text-sm font-semibold text-slate-900">
                            {{ card.value }}
                          </div>
                          <div class="mt-1 text-xs text-slate-500">
                            {{ card.detail }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="recommendationThresholdProfiles.length"
                      class="mt-3 rounded-large border border-neutral-border bg-base-module px-4 py-4"
                    >
                      <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                        Threshold tuning
                      </div>
                      <div class="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                        <div
                          v-for="profile in recommendationThresholdProfiles"
                          :key="`threshold-profile-${profile.key}`"
                          class="rounded-large border border-slate-200 bg-white px-3 py-3"
                        >
                          <div class="text-sm font-medium text-slate-900">
                            {{ profile.targetLabel || profile.label }}
                          </div>
                          <div class="mt-1 text-xs uppercase tracking-[0.08em] text-slate-400">
                            {{ recommendationAlertThresholdLabel(profile) }} ·
                            {{ recommendationAlertCapacityBandLabel(profile) }}
                          </div>
                          <div class="mt-1 text-sm text-slate-600">
                            Threshold {{ profile.thresholdValue }} · Current
                            {{ profile.currentCount }} ({{
                              formatRecommendationSnapshotDelta(profile.currentDelta)
                            }})
                          </div>
                          <div class="mt-1 text-xs text-slate-500">
                            {{ recommendationThresholdProfileSourceLabel(profile) }}
                            <span v-if="profile.overrideFields?.length">
                              · Override: {{ profile.overrideFields.join(', ') }}
                            </span>
                          </div>
                          <div class="mt-2 text-xs text-slate-500">
                            {{ profile.rationale }}
                          </div>
                          <div
                            v-if="recommendationThresholdOverrideDrafts[profile.key]"
                            class="mt-3 grid gap-2"
                          >
                            <label class="block">
                              <span class="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">
                                Label override
                              </span>
                              <input
                                v-model="recommendationThresholdOverrideDrafts[profile.key].label"
                                type="text"
                                class="mt-1 w-full rounded-large border border-slate-200 px-3 py-2 text-sm text-slate-900"
                                :placeholder="profile.label || 'Use default label'"
                              />
                            </label>
                            <div class="grid gap-2 md:grid-cols-2">
                              <label class="block">
                                <span class="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">
                                  Threshold
                                </span>
                                <input
                                  v-model="recommendationThresholdOverrideDrafts[profile.key].thresholdValue"
                                  type="number"
                                  min="1"
                                  step="1"
                                  class="mt-1 w-full rounded-large border border-slate-200 px-3 py-2 text-sm text-slate-900"
                                  :placeholder="String(profile.thresholdValue || '')"
                                />
                              </label>
                              <label class="block">
                                <span class="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">
                                  Watch delta
                                </span>
                                <input
                                  v-model="recommendationThresholdOverrideDrafts[profile.key].watchDelta"
                                  type="number"
                                  min="1"
                                  step="1"
                                  class="mt-1 w-full rounded-large border border-slate-200 px-3 py-2 text-sm text-slate-900"
                                  :placeholder="String(profile.watchDelta || '')"
                                />
                              </label>
                            </div>
                            <label class="block">
                              <span class="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-400">
                                Rationale override
                              </span>
                              <textarea
                                v-model="recommendationThresholdOverrideDrafts[profile.key].rationale"
                                rows="3"
                                class="mt-1 w-full rounded-large border border-slate-200 px-3 py-2 text-sm text-slate-900"
                                :placeholder="profile.rationale || 'Explain why this assistant needs a different threshold.'"
                              />
                            </label>
                            <div class="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                class="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                                :disabled="
                                  savingRecommendationThresholdProfileKey === profile.key ||
                                  !recommendationThresholdOverrideDirty(profile)
                                "
                                @click="saveRecommendationThresholdOverride(profile)"
                              >
                                {{
                                  savingRecommendationThresholdProfileKey === profile.key
                                    ? 'Saving...'
                                    : 'Save override'
                                }}
                              </button>
                              <button
                                type="button"
                                class="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
                                :disabled="
                                  savingRecommendationThresholdProfileKey === profile.key ||
                                  (!recommendationThresholdOverrideDirty(profile) &&
                                    !hasPersistedRecommendationThresholdOverride(profile))
                                "
                                @click="resetRecommendationThresholdOverride(profile)"
                              >
                                {{ recommendationThresholdResetLabel(profile) }}
                              </button>
                              <span
                                v-if="recommendationThresholdOverrideDirty(profile)"
                                class="text-[11px] text-amber-600"
                              >
                                Unsaved override change
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="recommendationAlertWorkflowSummary.total_alerts"
                      class="mt-3 rounded-large border border-neutral-border bg-base-module px-4 py-4"
                    >
                      <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                        Alert workflow sessions
                      </div>
                      <div class="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                        <div
                          v-for="card in recommendationAlertWorkflowCards"
                          :key="`alert-workflow-${card.key}`"
                          class="rounded-large border border-slate-200 bg-white px-3 py-3"
                        >
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            {{ card.label }}
                          </div>
                          <div class="mt-1 text-sm font-semibold text-slate-900">
                            {{ card.value }}
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="
                          recommendationAlertWorkflowResolutionOutcomeCards.length ||
                          recommendationAlertWorkflowHandoffReasonCards.length
                        "
                        class="mt-3 grid gap-2 md:grid-cols-2"
                      >
                        <div
                          v-if="recommendationAlertWorkflowResolutionOutcomeCards.length"
                          class="rounded-large border border-slate-200 bg-white px-3 py-3"
                        >
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            Resolution outcomes
                          </div>
                          <div class="mt-2 flex flex-wrap gap-2">
                            <span
                              v-for="item in recommendationAlertWorkflowResolutionOutcomeCards"
                              :key="`resolution-outcome-${item.id}`"
                              class="rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-600"
                            >
                              {{ item.label }} · {{ item.count }}
                            </span>
                          </div>
                        </div>
                        <div
                          v-if="recommendationAlertWorkflowHandoffReasonCards.length"
                          class="rounded-large border border-slate-200 bg-white px-3 py-3"
                        >
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            Handoff reasons
                          </div>
                          <div class="mt-2 flex flex-wrap gap-2">
                            <span
                              v-for="item in recommendationAlertWorkflowHandoffReasonCards"
                              :key="`handoff-reason-${item.id}`"
                              class="rounded-full border border-slate-200 px-2 py-1 text-xs text-slate-600"
                            >
                              {{ item.label }} · {{ item.count }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="recommendationAlertWorkflowActivityTrendCards.length"
                        class="mt-3 rounded-large border border-slate-200 bg-white px-3 py-3"
                      >
                        <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                          Change intent trends
                        </div>
                        <div class="mt-1 text-sm text-slate-600">
                          Dominant:
                          {{
                            recommendationAlertWorkflowActivityHighlightRollups
                              .dominant_highlight_label || 'None'
                          }}
                          <span
                            v-if="
                              recommendationAlertWorkflowActivityHighlightRollups
                                .dominant_highlight_count
                            "
                          >
                            ·
                            {{
                              recommendationAlertWorkflowActivityHighlightRollups
                                .dominant_highlight_count
                            }}
                            events across
                            {{
                              recommendationAlertWorkflowActivityHighlightRollups
                                .dominant_session_count || 0
                            }}
                            sessions
                          </span>
                        </div>
                        <div class="mt-3 grid gap-2 md:grid-cols-3">
                          <button
                            v-for="item in recommendationAlertWorkflowActivityTrendCards.slice(0, 3)"
                            :key="`workflow-highlight-trend-${item.id}`"
                            type="button"
                            class="rounded-large border px-3 py-3 text-left"
                            :class="
                              recommendationAlertWorkflowActivityHighlightFilter ===
                              item.recommendedFilterId
                                ? 'border-slate-900 bg-slate-50'
                                : 'border-slate-200'
                            "
                            @click="
                              applyRecommendationAlertWorkflowActivityHighlightFilter(
                                item.recommendedFilterId
                              )
                            "
                          >
                            <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                              {{ item.label }}
                            </div>
                            <div class="mt-1 text-sm font-semibold text-slate-900">
                              {{ item.count }} events
                            </div>
                            <div class="mt-1 text-xs text-slate-500">
                              {{ item.sessionCount }} sessions
                              <span v-if="item.latestChangedAt">
                                · Latest
                                {{ formatRecommendationDate(item.latestChangedAt) }}
                              </span>
                            </div>
                            <div
                              v-if="item.latestWorkflowLabel || item.latestWorkflowKey"
                              class="mt-1 text-xs text-slate-500"
                            >
                              Last seen on:
                              {{ item.latestWorkflowLabel || item.latestWorkflowKey }}
                            </div>
                          </button>
                        </div>
                      </div>
                      <div
                        v-if="recommendationAlertWorkflowActivityWindowTrendCards.length"
                        class="mt-3 rounded-large border border-slate-200 bg-white px-3 py-3"
                      >
                        <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                          Change intent momentum
                        </div>
                        <div class="mt-1 text-sm text-slate-600">
                          Recent window:
                          {{
                            recommendationAlertWorkflowActivityHighlightWindow
                              .recent_window_event_count || 0
                          }}
                          events
                          <span>
                            · Previous:
                            {{
                              recommendationAlertWorkflowActivityHighlightWindow
                                .previous_window_event_count || 0
                            }}
                          </span>
                          <span
                            v-if="
                              recommendationAlertWorkflowActivityHighlightWindow
                                .dominant_rising_highlight_label
                            "
                          >
                            · Rising:
                            {{
                              recommendationAlertWorkflowActivityHighlightWindow
                                .dominant_rising_highlight_label
                            }}
                          </span>
                          <span
                            v-if="
                              recommendationAlertWorkflowActivityHighlightWindow
                                .dominant_cooling_highlight_label
                            "
                          >
                            · Cooling:
                            {{
                              recommendationAlertWorkflowActivityHighlightWindow
                                .dominant_cooling_highlight_label
                            }}
                          </span>
                        </div>
                        <div class="mt-3 grid gap-2 md:grid-cols-3">
                          <button
                            v-for="item in recommendationAlertWorkflowActivityWindowTrendCards.slice(0, 3)"
                            :key="`workflow-highlight-window-${item.id}`"
                            type="button"
                            class="rounded-large border px-3 py-3 text-left"
                            :class="
                              recommendationAlertWorkflowActivityHighlightFilter ===
                              item.recommendedFilterId
                                ? 'border-slate-900 bg-slate-50'
                                : 'border-slate-200'
                            "
                            @click="
                              applyRecommendationAlertWorkflowActivityHighlightFilter(
                                item.recommendedFilterId
                              )
                            "
                          >
                            <div class="flex items-center justify-between gap-2">
                              <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                                {{ item.label }}
                              </div>
                              <span
                                class="rounded-full border px-2 py-1 text-[11px]"
                                :class="
                                  item.trendDirection === 'rising'
                                    ? 'border-emerald-200 text-emerald-700'
                                    : item.trendDirection === 'cooling'
                                      ? 'border-amber-200 text-amber-700'
                                      : 'border-slate-200 text-slate-500'
                                "
                              >
                                {{ item.trendDirection || 'steady' }}
                              </span>
                            </div>
                            <div class="mt-1 text-sm font-semibold text-slate-900">
                              {{ formatRecommendationSnapshotDelta(item.delta) }}
                            </div>
                            <div class="mt-1 text-xs text-slate-500">
                              Recent {{ item.recentCount }} · Previous {{ item.previousCount }}
                            </div>
                            <div class="mt-1 text-xs text-slate-500">
                              Sessions {{ item.recentSessionCount }} / {{ item.previousSessionCount }}
                            </div>
                            <div
                              v-if="item.latestWorkflowLabel || item.latestWorkflowKey"
                              class="mt-1 text-xs text-slate-500"
                            >
                              Last seen on:
                              {{ item.latestWorkflowLabel || item.latestWorkflowKey }}
                            </div>
                          </button>
                        </div>
                      </div>
                      <div
                        v-if="recommendationAlertWorkflowActivityTimeline.length"
                        class="mt-3 grid gap-2 md:grid-cols-2"
                      >
                        <div class="rounded-large border border-slate-200 bg-white px-3 py-3">
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            Activity counts
                          </div>
                          <div class="mt-3 grid gap-2 sm:grid-cols-3">
                            <div
                              v-for="card in recommendationAlertWorkflowActivityCards"
                              :key="`workflow-activity-${card.key}`"
                              class="rounded-large border border-slate-200 px-3 py-3"
                            >
                              <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                                {{ card.label }}
                              </div>
                              <div class="mt-1 text-sm font-semibold text-slate-900">
                                {{ card.value }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div class="rounded-large border border-slate-200 bg-white px-3 py-3">
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            Recent activity
                          </div>
                          <div
                            v-if="recommendationAlertWorkflowActivityHighlightCards.length"
                            class="mt-2 flex flex-wrap gap-2"
                          >
                            <button
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                recommendationAlertWorkflowActivityHighlightFilter === 'all'
                                  ? 'border-slate-900 text-slate-900'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="recommendationAlertWorkflowActivityHighlightFilter = 'all'"
                            >
                              All
                            </button>
                            <button
                              v-for="item in recommendationAlertWorkflowActivityHighlightCards"
                              :key="`workflow-activity-filter-${item.id}`"
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                recommendationAlertWorkflowActivityHighlightFilter === item.id
                                  ? 'border-slate-900 text-slate-900'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="recommendationAlertWorkflowActivityHighlightFilter = item.id"
                            >
                              {{ item.label }} · {{ item.count }}
                            </button>
                          </div>
                          <div
                            v-for="item in recommendationFilteredAlertWorkflowActivityTimeline.slice(0, 5)"
                            :key="`workflow-activity-row-${item.workflowKey}-${item.changedAt}`"
                            class="mt-2 text-sm text-slate-700"
                          >
                            <div class="font-medium text-slate-900">
                              {{ item.workflowLabel || item.workflowKey }}
                            </div>
                            <div class="text-xs text-slate-500">
                              {{ item.changeSummaryLabel || item.changeType }}
                              <span v-if="item.actorName"> · {{ item.actorName }}</span>
                              <span v-if="item.changedAt">
                                · {{ formatRecommendationDate(item.changedAt) }}
                              </span>
                            </div>
                            <div
                              v-if="item.changeHighlights.length"
                              class="mt-1 flex flex-wrap gap-1"
                            >
                              <span
                                v-for="highlight in item.changeHighlights"
                                :key="`workflow-activity-highlight-${item.workflowKey}-${item.changedAt}-${highlight.id}`"
                                class="rounded-full border border-slate-200 px-2 py-1 text-[11px] text-slate-500"
                              >
                                {{ highlight.label }}
                              </span>
                            </div>
                          </div>
                          <div
                            v-if="!recommendationFilteredAlertWorkflowActivityTimeline.length"
                            class="mt-2 rounded-large border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500"
                          >
                            No activity events match the current highlight filter.
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="activeRecommendationAlertWorkflowActivityHighlightContext"
                        class="mt-3 rounded-large border border-sky-200 bg-sky-50 px-3 py-3"
                      >
                        <div class="flex items-start justify-between gap-3">
                          <div>
                            <div class="text-xs uppercase tracking-[0.08em] text-sky-700">
                              Active change intent
                            </div>
                            <div class="mt-1 text-sm font-semibold text-sky-900">
                              {{
                                activeRecommendationAlertWorkflowActivityHighlightContext
                                  .drilldownContext?.title ||
                                activeRecommendationAlertWorkflowActivityHighlightContext
                                  .recommendedFilterLabel
                              }}
                            </div>
                            <div class="mt-1 text-xs text-sky-700">
                              {{
                                activeRecommendationAlertWorkflowActivityHighlightContext
                                  .drilldownContext?.summary ||
                                'Recent activity is filtered to the selected change intent.'
                              }}
                            </div>
                            <div class="mt-1 text-xs text-sky-700">
                              Showing
                              {{ recommendationFilteredAlertWorkflowActivityTimeline.length }}
                              matching recent activity events.
                            </div>
                          </div>
                          <button
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700"
                            @click="applyRecommendationAlertWorkflowActivityHighlightFilter('all')"
                          >
                            Clear intent filter
                          </button>
                        </div>
                      </div>
                      <div
                        v-if="activeRecommendationWorkflowAudit"
                        class="mt-3 rounded-large border border-slate-200 bg-white px-4 py-4"
                      >
                        <div class="flex items-start justify-between gap-3">
                          <div>
                            <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                              Session audit trail
                            </div>
                            <div class="mt-1 text-sm font-semibold text-slate-900">
                              {{
                                activeRecommendationWorkflowAudit.workflowLabel ||
                                activeRecommendationWorkflowAudit.workflowKey
                              }}
                            </div>
                            <div class="mt-1 text-xs text-slate-500">
                              {{ activeRecommendationWorkflowAudit.totalEvents }} recorded events
                              <span v-if="activeRecommendationWorkflowAudit.latestChangedAt">
                                · Latest
                                {{
                                  formatRecommendationDate(
                                    activeRecommendationWorkflowAudit.latestChangedAt
                                  )
                                }}
                              </span>
                              <span
                                v-if="
                                  recommendationWorkflowAuditHighlightFilter !== 'all' &&
                                  recommendationFilteredActiveWorkflowAuditEvents.length !==
                                    activeRecommendationWorkflowAudit.events.length
                                "
                              >
                                · Showing
                                {{ recommendationFilteredActiveWorkflowAuditEvents.length }}
                              </span>
                            </div>
                            <div
                              v-if="activeRecommendationWorkflowAudit.latestChangeHighlights.length"
                              class="mt-2 flex flex-wrap gap-1"
                            >
                              <span
                                v-for="highlight in activeRecommendationWorkflowAudit.latestChangeHighlights"
                                :key="`workflow-session-latest-highlight-${activeRecommendationWorkflowAudit.workflowKey}-${highlight.id}`"
                                class="rounded-full border border-slate-200 px-2 py-1 text-[11px] text-slate-500"
                              >
                                {{ highlight.label }}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                            @click="closeRecommendationWorkflowAudit"
                          >
                            Close audit
                          </button>
                        </div>
                        <div
                          v-if="activeRecommendationWorkflowAudit.events.length"
                          class="mt-3 grid gap-2"
                        >
                          <div
                            v-if="activeRecommendationWorkflowAuditHighlightCards.length"
                            class="flex flex-wrap gap-2"
                          >
                            <button
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                recommendationWorkflowAuditHighlightFilter === 'all'
                                  ? 'border-slate-900 text-slate-900'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="recommendationWorkflowAuditHighlightFilter = 'all'"
                            >
                              All
                            </button>
                            <button
                              v-for="item in activeRecommendationWorkflowAuditHighlightCards"
                              :key="`workflow-session-filter-${activeRecommendationWorkflowAudit.workflowKey}-${item.id}`"
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                recommendationWorkflowAuditHighlightFilter === item.id
                                  ? 'border-slate-900 text-slate-900'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="recommendationWorkflowAuditHighlightFilter = item.id"
                            >
                              {{ item.label }} · {{ item.count }}
                            </button>
                          </div>
                          <div
                            v-for="item in recommendationFilteredActiveWorkflowAuditEvents"
                            :key="`workflow-session-audit-${activeRecommendationWorkflowAudit.workflowKey}-${item.changedAt}-${item.changeType}`"
                            class="rounded-large border border-slate-200 px-3 py-3"
                          >
                            <div class="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                              <span class="rounded-full border border-slate-200 px-2 py-1">
                                {{ item.changeSummaryLabel || item.changeType || 'Workflow change' }}
                              </span>
                              <span v-if="item.actorName">
                                {{ item.actorName }}
                              </span>
                              <span v-if="item.changedAt">
                                {{ formatRecommendationDate(item.changedAt) }}
                              </span>
                            </div>
                            <div
                              v-if="item.previousStatus || item.nextStatus"
                              class="mt-2 text-sm text-slate-700"
                            >
                              {{ item.previousStatus || 'none' }} -> {{ item.nextStatus || 'none' }}
                            </div>
                            <div
                              v-if="item.changeHighlights.length"
                              class="mt-2 flex flex-wrap gap-1"
                            >
                              <span
                                v-for="highlight in item.changeHighlights"
                                :key="`workflow-session-highlight-${item.changedAt}-${highlight.id}`"
                                class="rounded-full border border-slate-200 px-2 py-1 text-[11px] text-slate-500"
                              >
                                {{ highlight.label }}
                              </span>
                            </div>
                            <div
                              v-if="item.fieldChanges.length"
                              class="mt-2 rounded-large border border-slate-200 bg-slate-50 px-3 py-3"
                            >
                              <div class="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-500">
                                Field changes
                              </div>
                              <div class="mt-2 grid gap-2">
                                <div
                                  v-for="change in item.fieldChanges"
                                  :key="`workflow-session-audit-field-${item.changedAt}-${change.field}`"
                                  class="rounded-large border border-slate-200 bg-white px-3 py-2"
                                >
                                  <div class="text-xs font-medium text-slate-700">
                                    {{ change.label || change.field }}
                                  </div>
                                  <div class="mt-1 text-xs text-slate-500">
                                    {{ change.previousDisplay || 'None' }} -> {{ change.nextDisplay || 'None' }}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            v-if="!recommendationFilteredActiveWorkflowAuditEvents.length"
                            class="rounded-large border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500"
                          >
                            No audit events match the current highlight filter.
                          </div>
                        </div>
                        <div
                          v-else
                          class="mt-3 rounded-large border border-dashed border-slate-200 px-3 py-4 text-sm text-slate-500"
                        >
                          No durable workflow activity is recorded for this session yet.
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="recommendationAlertWorkflowLedger.length"
                      class="mt-3 rounded-large border border-neutral-border bg-base-module px-4 py-4"
                    >
                      <div class="flex items-start justify-between gap-3">
                        <div>
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            Workflow session ledger
                          </div>
                          <div class="mt-1 text-sm text-slate-500">
                            Showing {{ recommendationFilteredAlertWorkflowLedger.length }}
                            of {{ recommendationAlertWorkflowLedger.length }} sessions.
                          </div>
                        </div>
                        <button
                          type="button"
                          class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                          @click="resetRecommendationAlertWorkflowFilters"
                        >
                          Reset ledger filters
                        </button>
                      </div>
                      <div
                        v-if="selectedRecommendationAlertWorkflowSessions.length"
                        class="mt-3 rounded-large border border-sky-200 bg-sky-50 px-3 py-3"
                      >
                        <div class="flex flex-wrap items-center gap-2">
                          <div class="text-sm text-sky-900">
                            {{ selectedRecommendationAlertWorkflowSessions.length }}
                            sessions selected
                          </div>
                          <button
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="isApplyingRecommendationAlertWorkflowBatch"
                            @click="applyRecommendationAlertWorkflowBatch('resolved')"
                          >
                            Batch resolve
                          </button>
                          <button
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="isApplyingRecommendationAlertWorkflowBatch"
                            @click="applyRecommendationAlertWorkflowBatch('snoozed')"
                          >
                            Batch snooze
                          </button>
                          <button
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="isApplyingRecommendationAlertWorkflowBatch"
                            @click="applyRecommendationAlertWorkflowBatch('handoff')"
                          >
                            Batch handoff
                          </button>
                          <button
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                            :disabled="isApplyingRecommendationAlertWorkflowBatch"
                            @click="applyRecommendationAlertWorkflowBatch('clear')"
                          >
                            Clear selected
                          </button>
                          <button
                            type="button"
                            class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700"
                            @click="clearSelectedRecommendationAlertWorkflows"
                          >
                            Clear selection
                          </button>
                        </div>
                        <div class="mt-3 rounded-large border border-sky-200 bg-white px-3 py-3">
                          <div class="text-xs uppercase tracking-[0.08em] text-sky-700">
                            Batch metadata
                          </div>
                          <div class="mt-1 text-xs text-sky-700">
                            Enable a field to apply it across the current selection. Leaving an
                            enabled field blank clears that value for the selected sessions.
                          </div>
                          <div
                            v-if="
                              recommendationSuggestedAlertWorkflowBatchPreset &&
                              activeRecommendationSuggestedAlertWorkflowBatchPreset
                            "
                            class="mt-3 rounded-large border border-emerald-200 bg-emerald-50 px-3 py-3"
                          >
                            <div class="flex items-start justify-between gap-3">
                              <div>
                                <div class="flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-emerald-700">
                                  <span>Suggested preset</span>
                                  <span class="rounded-full border border-emerald-300 px-2 py-0.5">
                                    {{
                                      String(
                                        recommendationSuggestedAlertWorkflowBatchPreset.confidence ||
                                          'medium'
                                      ).toUpperCase()
                                    }}
                                  </span>
                                </div>
                                <div class="mt-1 text-sm font-semibold text-emerald-900">
                                  {{ activeRecommendationSuggestedAlertWorkflowBatchPreset.label }}
                                </div>
                                <div class="mt-1 text-xs text-emerald-700">
                                  {{ recommendationSuggestedAlertWorkflowBatchPreset.reason }}
                                </div>
                              </div>
                              <button
                                type="button"
                                class="rounded-full border border-emerald-300 px-3 py-1 text-xs text-emerald-700"
                                @click="
                                  applyRecommendationAlertWorkflowBatchPreset(
                                    activeRecommendationSuggestedAlertWorkflowBatchPreset.id
                                  )
                                "
                              >
                                Use suggested
                              </button>
                            </div>
                            <div
                              v-if="recommendationSuggestedAlertWorkflowBatchPresetCards.length"
                              class="mt-3 grid gap-2 md:grid-cols-4"
                            >
                              <div
                                v-for="card in recommendationSuggestedAlertWorkflowBatchPresetCards"
                                :key="`workflow-batch-preset-summary-${card.key}`"
                                class="rounded-large border border-emerald-200 bg-white px-3 py-3"
                              >
                                <div class="text-[11px] uppercase tracking-[0.08em] text-emerald-700">
                                  {{ card.label }}
                                </div>
                                <div class="mt-1 text-sm font-semibold text-slate-900">
                                  {{ card.value }}
                                </div>
                                <div class="mt-1 text-[11px] text-slate-500">
                                  {{ card.detail }}
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="recommendationAlternativeAlertWorkflowBatchPresets.length"
                              class="mt-3 rounded-large border border-emerald-200 bg-white px-3 py-3"
                            >
                              <div class="text-[11px] font-medium uppercase tracking-[0.08em] text-emerald-700">
                                Why not others
                              </div>
                              <div class="mt-1 text-xs text-slate-500">
                                Other presets were evaluated too, but their signal coverage was weaker
                                for the current selection.
                              </div>
                              <div class="mt-3 grid gap-2 md:grid-cols-2">
                                <div
                                  v-for="preset in recommendationAlternativeAlertWorkflowBatchPresets"
                                  :key="`workflow-batch-preset-why-not-${preset.presetId}`"
                                  class="rounded-large border px-3 py-3"
                                  :class="
                                    preset.isPreviewing
                                      ? 'border-emerald-300 bg-emerald-50'
                                      : 'border-slate-200 bg-slate-50'
                                  "
                                >
                                  <div class="flex items-center justify-between gap-2">
                                    <div class="text-sm font-semibold text-slate-900">
                                      {{ preset.presetLabel }}
                                    </div>
                                    <div class="rounded-full border border-slate-200 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-slate-500">
                                      {{ String(preset.confidence || 'low').toUpperCase() }}
                                    </div>
                                  </div>
                                  <div class="mt-1 text-[11px] text-slate-500">
                                    {{ preset.matchingCount }} / {{ preset.totalCount }}
                                    {{ preset.supportingLabel.toLowerCase() }}
                                  </div>
                                  <div class="mt-2 text-xs text-slate-600">
                                    {{ preset.whyNotReason }}
                                  </div>
                                  <div class="mt-2 text-[11px] text-slate-500">
                                    {{ preset.deltaSummary }}
                                  </div>
                                  <div class="mt-3 flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      class="rounded-full border px-3 py-1 text-xs"
                                      :class="
                                        preset.isPreviewing
                                          ? 'border-emerald-300 text-emerald-700'
                                          : 'border-slate-200 text-slate-600'
                                      "
                                      @click="
                                        previewRecommendationAlertWorkflowBatchPresetComparison(
                                          preset.presetId
                                        )
                                      "
                                    >
                                      {{ preset.isPreviewing ? 'Previewing' : 'Preview differences' }}
                                    </button>
                                    <button
                                      type="button"
                                      class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                                      @click="
                                        applyRecommendationAlertWorkflowBatchPreset(preset.presetId)
                                      "
                                    >
                                      Load instead
                                    </button>
                                  </div>
                                </div>
                              </div>
                              <div
                                v-if="activeRecommendationAlternativeAlertWorkflowBatchPreset"
                                class="mt-3 rounded-large border border-emerald-200 bg-emerald-50 px-3 py-3"
                              >
                                <div class="text-[11px] font-medium uppercase tracking-[0.08em] text-emerald-700">
                                  Alternative preview
                                </div>
                                <div class="mt-1 text-sm font-semibold text-emerald-900">
                                  {{
                                    activeRecommendationAlternativeAlertWorkflowBatchPreset.presetLabel
                                  }}
                                </div>
                                <div class="mt-1 text-xs text-emerald-700">
                                  {{
                                    activeRecommendationAlternativeAlertWorkflowBatchPreset.deltaSummary
                                  }}
                                </div>
                                <div class="mt-3 grid gap-2 md:grid-cols-2">
                                  <div
                                    v-for="field in activeRecommendationAlternativeAlertWorkflowBatchPreset.differingFields"
                                    :key="`workflow-batch-preset-diff-${activeRecommendationAlternativeAlertWorkflowBatchPreset.presetId}-${field.key}`"
                                    class="rounded-large border border-emerald-200 bg-white px-3 py-3"
                                  >
                                    <div class="flex items-center justify-between gap-2">
                                      <div class="text-xs font-medium uppercase tracking-[0.08em] text-emerald-700">
                                        {{ field.label }}
                                      </div>
                                      <div class="rounded-full border border-emerald-200 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-emerald-700">
                                        {{ field.changeKind }}
                                      </div>
                                    </div>
                                    <div class="mt-2 text-[11px] text-slate-500">
                                      Suggested: {{ field.suggestedDisplay }}
                                    </div>
                                    <div class="mt-1 text-xs text-slate-700">
                                      Alternative: {{ field.alternativeDisplay }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="closestRecommendationAlertWorkflowBatchPresetDrift"
                              class="mt-3 rounded-large border px-3 py-3"
                              :class="
                                closestRecommendationAlertWorkflowBatchPresetDrift.isExactMatch
                                  ? 'border-sky-200 bg-sky-50'
                                  : 'border-amber-200 bg-amber-50'
                              "
                            >
                              <div class="flex items-start justify-between gap-3">
                                <div>
                                  <div
                                    class="text-[11px] font-medium uppercase tracking-[0.08em]"
                                    :class="
                                      closestRecommendationAlertWorkflowBatchPresetDrift.isExactMatch
                                        ? 'text-sky-700'
                                        : 'text-amber-700'
                                    "
                                  >
                                    {{
                                      closestRecommendationAlertWorkflowBatchPresetDrift.isExactMatch
                                        ? 'Current draft matches a preset'
                                        : 'Current draft drift'
                                    }}
                                  </div>
                                  <div
                                    class="mt-1 text-sm font-semibold"
                                    :class="
                                      closestRecommendationAlertWorkflowBatchPresetDrift.isExactMatch
                                        ? 'text-sky-900'
                                        : 'text-amber-900'
                                    "
                                  >
                                    {{
                                      closestRecommendationAlertWorkflowBatchPresetDrift.presetLabel
                                    }}
                                  </div>
                                  <div
                                    class="mt-1 text-xs"
                                    :class="
                                      closestRecommendationAlertWorkflowBatchPresetDrift.isExactMatch
                                        ? 'text-sky-700'
                                        : 'text-amber-700'
                                    "
                                  >
                                    {{
                                      closestRecommendationAlertWorkflowBatchPresetDrift.driftSummary
                                    }}
                                  </div>
                                </div>
                                <button
                                  v-if="
                                    !closestRecommendationAlertWorkflowBatchPresetDrift.isExactMatch ||
                                    !closestRecommendationAlertWorkflowBatchPresetDrift.isLoadedPreset
                                  "
                                  type="button"
                                  class="rounded-full border px-3 py-1 text-xs"
                                  :class="
                                    closestRecommendationAlertWorkflowBatchPresetDrift.isExactMatch
                                      ? 'border-sky-300 text-sky-700'
                                      : 'border-amber-300 text-amber-700'
                                  "
                                  @click="
                                    applyRecommendationAlertWorkflowBatchPreset(
                                      closestRecommendationAlertWorkflowBatchPresetDrift.presetId
                                    )
                                  "
                                >
                                  {{
                                    closestRecommendationAlertWorkflowBatchPresetDrift.isExactMatch
                                      ? 'Load matching preset'
                                      : 'Restore closest preset'
                                  }}
                                </button>
                              </div>
                              <div
                                v-if="recommendationAlertWorkflowBatchPresetDriftCards.length"
                                class="mt-3 grid gap-2 md:grid-cols-3"
                              >
                                <div
                                  v-for="card in recommendationAlertWorkflowBatchPresetDriftCards"
                                  :key="`workflow-batch-preset-drift-${card.key}`"
                                  class="rounded-large border border-white/70 bg-white px-3 py-3"
                                >
                                  <div class="text-[11px] uppercase tracking-[0.08em] text-slate-500">
                                    {{ card.label }}
                                  </div>
                                  <div class="mt-1 text-sm font-semibold text-slate-900">
                                    {{ card.value }}
                                  </div>
                                  <div class="mt-1 text-[11px] text-slate-500">
                                    {{ card.detail }}
                                  </div>
                                </div>
                              </div>
                              <div
                                v-if="recommendationAlertWorkflowBatchPresetDriftRepairs.length"
                                class="mt-3 rounded-large border border-white/70 bg-white px-3 py-3"
                              >
                                <div class="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-600">
                                  Recommended fixes
                                </div>
                                <div class="mt-2 flex flex-wrap gap-2">
                                  <button
                                    v-for="repair in recommendationAlertWorkflowBatchPresetDriftRepairs"
                                    :key="`workflow-batch-preset-repair-${repair.key}`"
                                    type="button"
                                    class="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700"
                                    @click="
                                      applyRecommendationAlertWorkflowBatchPresetField(
                                        closestRecommendationAlertWorkflowBatchPresetDrift.presetId,
                                        repair.key
                                      )
                                    "
                                  >
                                    {{ repair.actionLabel }}
                                  </button>
                                </div>
                                <div class="mt-3 grid gap-2 md:grid-cols-3">
                                  <div
                                    v-for="repair in recommendationAlertWorkflowBatchPresetDriftRepairs"
                                    :key="`workflow-batch-preset-repair-reason-${repair.key}`"
                                    class="rounded-large border border-slate-200 bg-slate-50 px-3 py-3"
                                  >
                                    <div class="text-xs font-medium text-slate-900">
                                      {{ repair.label }}
                                    </div>
                                    <div class="mt-1 text-[11px] text-slate-500">
                                      {{ repair.reason }}
                                    </div>
                                    <div class="mt-2 text-[11px] text-slate-500">
                                      Preset: {{ repair.presetDisplay }}
                                    </div>
                                    <div class="mt-1 text-[11px] text-slate-500">
                                      Current: {{ repair.currentDisplay }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div
                                v-if="
                                  closestRecommendationAlertWorkflowBatchPresetDrift.differingFields
                                    .length
                                "
                                class="mt-3 grid gap-2 md:grid-cols-2"
                              >
                                <div
                                  v-for="field in closestRecommendationAlertWorkflowBatchPresetDrift.differingFields"
                                  :key="`workflow-batch-preset-drift-current-${field.key}`"
                                  class="rounded-large border border-white/70 bg-white px-3 py-3"
                                >
                                  <div class="flex items-center justify-between gap-2">
                                    <div class="text-xs font-medium uppercase tracking-[0.08em] text-slate-600">
                                      {{ field.label }}
                                    </div>
                                    <div class="rounded-full border border-slate-200 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-slate-500">
                                      {{ field.driftKind }}
                                    </div>
                                  </div>
                                  <div class="mt-2 text-[11px] text-slate-500">
                                    Preset: {{ field.presetDisplay }}
                                  </div>
                                  <div class="mt-1 text-xs text-slate-700">
                                    Current draft: {{ field.currentDisplay }}
                                  </div>
                                  <button
                                    type="button"
                                    class="mt-3 rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                                    @click="
                                      applyRecommendationAlertWorkflowBatchPresetField(
                                        closestRecommendationAlertWorkflowBatchPresetDrift.presetId,
                                        field.key
                                      )
                                    "
                                  >
                                    Use preset value
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            v-if="recommendationAlertWorkflowBatchMetadataPresetDefinitions.length"
                            class="mt-3 rounded-large border border-sky-200 bg-sky-50 px-3 py-3"
                          >
                            <div class="flex items-start justify-between gap-3">
                              <div>
                                <div class="text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                                  Quick presets
                                </div>
                                <div class="mt-1 text-xs text-sky-700">
                                  Load a common batch workflow template, then tweak fields if needed.
                                </div>
                              </div>
                              <button
                                type="button"
                                class="rounded-full border border-sky-300 px-3 py-1 text-xs text-sky-700"
                                @click="clearRecommendationAlertWorkflowBatchPreset"
                              >
                                Reset preset
                              </button>
                            </div>
                            <div class="mt-3 flex flex-wrap gap-2">
                              <button
                                v-for="preset in recommendationAlertWorkflowBatchMetadataPresetDefinitions"
                                :key="`workflow-batch-preset-${preset.id}`"
                                type="button"
                                class="rounded-large border px-3 py-2 text-left"
                                :class="
                                  activeRecommendationAlertWorkflowBatchPresetId === preset.id
                                    ? 'border-sky-400 bg-white text-sky-900'
                                    : activeRecommendationSuggestedAlertWorkflowBatchPreset?.id ===
                                        preset.id
                                      ? 'border-emerald-300 bg-white text-emerald-900'
                                    : 'border-sky-200 bg-white text-sky-700'
                                "
                                @click="applyRecommendationAlertWorkflowBatchPreset(preset.id)"
                              >
                                <div class="flex items-center gap-2 text-xs font-medium">
                                  <span>{{ preset.label }}</span>
                                  <span
                                    v-if="
                                      activeRecommendationSuggestedAlertWorkflowBatchPreset?.id ===
                                      preset.id
                                    "
                                    class="rounded-full border border-emerald-300 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-emerald-700"
                                  >
                                    Suggested
                                  </span>
                                  <span
                                    v-if="activeRecommendationAlertWorkflowBatchPresetId === preset.id"
                                    class="rounded-full border border-sky-300 px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-sky-700"
                                  >
                                    Loaded
                                  </span>
                                </div>
                                <div class="mt-1 text-[11px] text-slate-500">
                                  {{ preset.description }}
                                </div>
                              </button>
                            </div>
                            <div
                              v-if="activeRecommendationAlertWorkflowBatchPresetId"
                              class="mt-2 text-xs text-sky-700"
                            >
                              Preset loaded. You can still edit any field before saving.
                            </div>
                          </div>
                          <div class="mt-3 grid gap-2 md:grid-cols-2">
                            <label class="block">
                              <span class="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                                <input
                                  v-model="
                                    recommendationAlertWorkflowBatchMetadataDraft.applyOwnerLabel
                                  "
                                  type="checkbox"
                                  class="rounded border-sky-300 text-sky-600"
                                />
                                Owner
                              </span>
                              <input
                                v-model="recommendationAlertWorkflowBatchMetadataDraft.ownerLabel"
                                type="text"
                                class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900 disabled:bg-slate-50 disabled:text-slate-400"
                                :disabled="
                                  !recommendationAlertWorkflowBatchMetadataDraft.applyOwnerLabel
                                "
                                placeholder="Batch assign workflow owner"
                              />
                            </label>
                            <label class="block">
                              <span class="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                                <input
                                  v-model="recommendationAlertWorkflowBatchMetadataDraft.applyDueDate"
                                  type="checkbox"
                                  class="rounded border-sky-300 text-sky-600"
                                />
                                Due date
                              </span>
                              <input
                                v-model="recommendationAlertWorkflowBatchMetadataDraft.dueDate"
                                type="date"
                                class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900 disabled:bg-slate-50 disabled:text-slate-400"
                                :disabled="
                                  !recommendationAlertWorkflowBatchMetadataDraft.applyDueDate
                                "
                              />
                            </label>
                          </div>
                          <div class="mt-2 grid gap-2 md:grid-cols-2">
                            <label class="block">
                              <span class="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                                <input
                                  v-model="
                                    recommendationAlertWorkflowBatchMetadataDraft.applyResolutionOutcome
                                  "
                                  type="checkbox"
                                  class="rounded border-sky-300 text-sky-600"
                                />
                                Resolution outcome
                              </span>
                              <input
                                v-model="
                                  recommendationAlertWorkflowBatchMetadataDraft.resolutionOutcome
                                "
                                type="text"
                                class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900 disabled:bg-slate-50 disabled:text-slate-400"
                                :disabled="
                                  !recommendationAlertWorkflowBatchMetadataDraft.applyResolutionOutcome
                                "
                                placeholder="Published article, scope fixed..."
                              />
                            </label>
                            <label class="block">
                              <span class="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                                <input
                                  v-model="
                                    recommendationAlertWorkflowBatchMetadataDraft.applyHandoffReason
                                  "
                                  type="checkbox"
                                  class="rounded border-sky-300 text-sky-600"
                                />
                                Handoff reason
                              </span>
                              <input
                                v-model="
                                  recommendationAlertWorkflowBatchMetadataDraft.handoffReason
                                "
                                type="text"
                                class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900 disabled:bg-slate-50 disabled:text-slate-400"
                                :disabled="
                                  !recommendationAlertWorkflowBatchMetadataDraft.applyHandoffReason
                                "
                                placeholder="Why this batch moved to another owner"
                              />
                            </label>
                          </div>
                          <label class="mt-2 block">
                            <span class="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                              <input
                                v-model="
                                  recommendationAlertWorkflowBatchMetadataDraft.applyNextCheckpoint
                                "
                                type="checkbox"
                                class="rounded border-sky-300 text-sky-600"
                              />
                              Next checkpoint
                            </span>
                            <input
                              v-model="recommendationAlertWorkflowBatchMetadataDraft.nextCheckpoint"
                              type="text"
                              class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900 disabled:bg-slate-50 disabled:text-slate-400"
                              :disabled="
                                !recommendationAlertWorkflowBatchMetadataDraft.applyNextCheckpoint
                              "
                              placeholder="What follow-up should happen next"
                            />
                          </label>
                          <label class="mt-2 block">
                            <span class="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-sky-700">
                              <input
                                v-model="recommendationAlertWorkflowBatchMetadataDraft.applyNote"
                                type="checkbox"
                                class="rounded border-sky-300 text-sky-600"
                              />
                              Operator note
                            </span>
                            <textarea
                              v-model="recommendationAlertWorkflowBatchMetadataDraft.note"
                              rows="3"
                              class="mt-1 w-full rounded-large border border-sky-200 px-3 py-2 text-sm text-slate-900 disabled:bg-slate-50 disabled:text-slate-400"
                              :disabled="
                                !recommendationAlertWorkflowBatchMetadataDraft.applyNote
                              "
                              placeholder="Capture the same follow-up note across the selection."
                            />
                          </label>
                          <div
                            v-if="recommendationAlertWorkflowBatchMetadataPreflight"
                            class="mt-3 rounded-large border px-3 py-3"
                            :class="
                              recommendationAlertWorkflowBatchMetadataPreflight.readiness === 'risk'
                                ? 'border-amber-200 bg-amber-50'
                                : recommendationAlertWorkflowBatchMetadataPreflight.readiness ===
                                      'caution'
                                  ? 'border-sky-200 bg-sky-50'
                                  : 'border-emerald-200 bg-emerald-50'
                            "
                          >
                            <div class="flex items-start justify-between gap-3">
                              <div>
                                <div
                                  class="text-[11px] font-medium uppercase tracking-[0.08em]"
                                  :class="
                                    recommendationAlertWorkflowBatchMetadataPreflight.readiness ===
                                    'risk'
                                      ? 'text-amber-700'
                                      : recommendationAlertWorkflowBatchMetadataPreflight
                                            .readiness === 'caution'
                                        ? 'text-sky-700'
                                        : 'text-emerald-700'
                                  "
                                >
                                  Batch save preflight
                                </div>
                                <div
                                  class="mt-1 text-sm font-semibold"
                                  :class="
                                    recommendationAlertWorkflowBatchMetadataPreflight.readiness ===
                                    'risk'
                                      ? 'text-amber-900'
                                      : recommendationAlertWorkflowBatchMetadataPreflight
                                            .readiness === 'caution'
                                        ? 'text-sky-900'
                                        : 'text-emerald-900'
                                  "
                                >
                                  {{ recommendationAlertWorkflowBatchMetadataPreflight.title }}
                                </div>
                                <div
                                  class="mt-1 text-xs"
                                  :class="
                                    recommendationAlertWorkflowBatchMetadataPreflight.readiness ===
                                    'risk'
                                      ? 'text-amber-700'
                                      : recommendationAlertWorkflowBatchMetadataPreflight
                                            .readiness === 'caution'
                                        ? 'text-sky-700'
                                        : 'text-emerald-700'
                                  "
                                >
                                  {{ recommendationAlertWorkflowBatchMetadataPreflight.summary }}
                                </div>
                              </div>
                              <button
                                v-if="
                                  recommendationAlertWorkflowBatchMetadataSaveGuard.requiresConfirmation
                                "
                                type="button"
                                class="rounded-full border px-3 py-1 text-xs"
                                :class="
                                  recommendationAlertWorkflowBatchMetadataSaveGuard.isAcknowledged
                                    ? 'border-amber-300 bg-white text-amber-700'
                                    : 'border-amber-300 text-amber-700'
                                "
                                @click="
                                  recommendationAlertWorkflowBatchMetadataSaveGuard.isAcknowledged
                                    ? clearRecommendationAlertWorkflowBatchMetadataRiskAcknowledgement()
                                    : acknowledgeRecommendationAlertWorkflowBatchMetadataRisk()
                                "
                              >
                                {{
                                  recommendationAlertWorkflowBatchMetadataSaveGuard.isAcknowledged
                                    ? 'Risk acknowledged'
                                    : 'Acknowledge risk'
                                }}
                              </button>
                            </div>
                            <div
                              v-if="recommendationAlertWorkflowBatchMetadataPreflightCards.length"
                              class="mt-3 grid gap-2 md:grid-cols-3"
                            >
                              <div
                                v-for="card in recommendationAlertWorkflowBatchMetadataPreflightCards"
                                :key="`workflow-batch-preflight-${card.key}`"
                                class="rounded-large border border-white/70 bg-white px-3 py-3"
                              >
                                <div class="text-[11px] uppercase tracking-[0.08em] text-slate-500">
                                  {{ card.label }}
                                </div>
                                <div class="mt-1 text-sm font-semibold text-slate-900">
                                  {{ card.value }}
                                </div>
                                <div class="mt-1 text-[11px] text-slate-500">
                                  {{ card.detail }}
                                </div>
                              </div>
                            </div>
                            <div
                              v-if="
                                recommendationAlertWorkflowBatchMetadataPreflight.highImpactFields
                                  .length
                              "
                              class="mt-3 rounded-large border border-white/70 bg-white px-3 py-3"
                            >
                              <div class="text-[11px] font-medium uppercase tracking-[0.08em] text-slate-600">
                                High-impact gaps
                              </div>
                              <div class="mt-2 flex flex-wrap gap-2">
                                <button
                                  v-for="field in recommendationAlertWorkflowBatchMetadataPreflight.highImpactFields"
                                  :key="`workflow-batch-preflight-high-impact-${field.key}`"
                                  type="button"
                                  class="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700"
                                  @click="
                                    applyRecommendationAlertWorkflowBatchPresetField(
                                      closestRecommendationAlertWorkflowBatchPresetDrift.presetId,
                                      field.key
                                    )
                                  "
                                >
                                  Fix {{ field.label.toLowerCase() }}
                                </button>
                              </div>
                            </div>
                            <div
                              v-if="
                                recommendationAlertWorkflowBatchMetadataPreflight.recommendedRepairs
                                  .length
                              "
                              class="mt-3 text-xs text-slate-600"
                            >
                              Suggested before save:
                              {{
                                recommendationAlertWorkflowBatchMetadataPreflight.recommendedRepairs
                                  .map(item => item.label)
                                  .join(', ')
                              }}
                            </div>
                          </div>
                          <div class="mt-3 flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              class="rounded-full border px-3 py-1 text-xs disabled:cursor-not-allowed disabled:opacity-60"
                              :class="
                                recommendationAlertWorkflowBatchMetadataSaveGuard.requiresConfirmation
                                  ? 'border-amber-300 text-amber-700'
                                  : 'border-sky-300 text-sky-700'
                              "
                              :disabled="
                                isApplyingRecommendationAlertWorkflowBatch ||
                                !recommendationAlertWorkflowBatchMetadataDirty
                              "
                              @click="applyRecommendationAlertWorkflowBatchMetadata"
                            >
                              {{ recommendationAlertWorkflowBatchMetadataSaveGuard.buttonLabel }}
                            </button>
                            <div
                              class="text-xs"
                              :class="
                                recommendationAlertWorkflowBatchMetadataPreflight?.readiness ===
                                'risk'
                                  ? 'text-amber-700'
                                  : 'text-sky-700'
                              "
                            >
                              {{
                                recommendationAlertWorkflowBatchMetadataSaveGuard.helperText ||
                                  (recommendationAlertWorkflowBatchMetadataDirty
                                    ? 'Ready to apply metadata changes to the current selection.'
                                    : 'No batch metadata changes selected yet.')
                              }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="mt-3 grid gap-2 md:grid-cols-2">
                        <div class="rounded-large border border-slate-200 bg-white px-3 py-3">
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            Status
                          </div>
                          <div class="mt-2 flex flex-wrap gap-2">
                            <button
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                recommendationAlertWorkflowAllFilteredSelected
                                  ? 'border-slate-900 text-slate-900'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="
                                recommendationAlertWorkflowAllFilteredSelected
                                  ? clearSelectedRecommendationAlertWorkflows()
                                  : selectAllFilteredRecommendationAlertWorkflows()
                              "
                            >
                              {{
                                recommendationAlertWorkflowAllFilteredSelected
                                  ? 'Clear visible'
                                  : 'Select visible'
                              }}
                            </button>
                            <button
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                recommendationAlertWorkflowStatusFilter === 'all'
                                  ? 'border-slate-900 text-slate-900'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="recommendationAlertWorkflowStatusFilter = 'all'"
                            >
                              All
                            </button>
                            <button
                              v-for="item in recommendationAlertWorkflowStatusCards"
                              :key="`workflow-status-${item.id}`"
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                recommendationAlertWorkflowStatusFilter === item.id
                                  ? 'border-slate-900 text-slate-900'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="recommendationAlertWorkflowStatusFilter = item.id"
                            >
                              {{ item.label }} · {{ item.count }}
                            </button>
                          </div>
                        </div>
                        <div class="rounded-large border border-slate-200 bg-white px-3 py-3">
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            Due
                          </div>
                          <div class="mt-2 flex flex-wrap gap-2">
                            <button
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                recommendationAlertWorkflowDueFilter === 'all'
                                  ? 'border-slate-900 text-slate-900'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="recommendationAlertWorkflowDueFilter = 'all'"
                            >
                              All
                            </button>
                            <button
                              v-for="item in recommendationAlertWorkflowDueCards"
                              :key="`workflow-due-${item.id}`"
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                recommendationAlertWorkflowDueFilter === item.id
                                  ? 'border-slate-900 text-slate-900'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="recommendationAlertWorkflowDueFilter = item.id"
                            >
                              {{ item.label }} · {{ item.count }}
                            </button>
                          </div>
                        </div>
                        <div class="rounded-large border border-slate-200 bg-white px-3 py-3">
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            Follow-up
                          </div>
                          <div class="mt-2 flex flex-wrap gap-2">
                            <button
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                recommendationAlertWorkflowFollowupFilter === 'all'
                                  ? 'border-slate-900 text-slate-900'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="recommendationAlertWorkflowFollowupFilter = 'all'"
                            >
                              All
                            </button>
                            <button
                              v-for="item in recommendationAlertWorkflowFollowupCards"
                              :key="`workflow-followup-${item.id}`"
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                recommendationAlertWorkflowFollowupFilter === item.id
                                  ? 'border-slate-900 text-slate-900'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="recommendationAlertWorkflowFollowupFilter = item.id"
                            >
                              {{ item.label }} · {{ item.count }}
                            </button>
                          </div>
                        </div>
                        <div class="rounded-large border border-slate-200 bg-white px-3 py-3">
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            Sort
                          </div>
                          <select
                            v-model="recommendationAlertWorkflowSort"
                            class="mt-2 w-full rounded-large border border-slate-200 px-3 py-2 text-sm text-slate-900"
                          >
                            <option
                              v-for="option in recommendationAlertWorkflowSortOptions"
                              :key="`workflow-sort-${option.id}`"
                              :value="option.id"
                            >
                              {{ option.label }}
                            </option>
                          </select>
                        </div>
                      </div>
                      <div
                        v-if="recommendationAlertWorkflowResultCards.length"
                        class="mt-3 rounded-large border border-slate-200 bg-white px-3 py-3"
                      >
                        <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                          Result
                        </div>
                        <div class="mt-2 flex flex-wrap gap-2">
                          <button
                            type="button"
                            class="rounded-full border px-2 py-1 text-xs"
                            :class="
                              recommendationAlertWorkflowResultFilter === 'all'
                                ? 'border-slate-900 text-slate-900'
                                : 'border-slate-200 text-slate-500'
                            "
                            @click="recommendationAlertWorkflowResultFilter = 'all'"
                          >
                            All
                          </button>
                          <button
                            v-for="item in recommendationAlertWorkflowResultCards"
                            :key="`workflow-result-${item.id}`"
                            type="button"
                            class="rounded-full border px-2 py-1 text-xs"
                            :class="
                              recommendationAlertWorkflowResultFilter === item.id
                                ? 'border-slate-900 text-slate-900'
                                : 'border-slate-200 text-slate-500'
                            "
                            @click="recommendationAlertWorkflowResultFilter = item.id"
                          >
                            {{ item.label }} · {{ item.count }}
                          </button>
                        </div>
                      </div>
                      <div class="mt-3 grid gap-2 md:grid-cols-2">
                        <div
                          v-for="session in recommendationFilteredAlertWorkflowLedger"
                          :key="`workflow-ledger-${session.key}`"
                          class="rounded-large border bg-white px-3 py-3"
                          :class="
                            selectedRecommendationAlertWorkflowKeys.includes(session.key)
                              ? 'border-sky-300'
                              : 'border-slate-200'
                          "
                        >
                          <div class="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <button
                              type="button"
                              class="rounded-full border px-2 py-1 text-xs"
                              :class="
                                selectedRecommendationAlertWorkflowKeys.includes(session.key)
                                  ? 'border-sky-300 text-sky-700'
                                  : 'border-slate-200 text-slate-500'
                              "
                              @click="
                                toggleRecommendationAlertWorkflowSelection(session.key)
                              "
                            >
                              {{
                                selectedRecommendationAlertWorkflowKeys.includes(session.key)
                                  ? 'Selected'
                                  : 'Select'
                              }}
                            </button>
                            <span class="rounded-full border border-slate-200 px-2 py-1">
                              {{ recommendationAlertWorkflowStateLabel(session) }}
                            </span>
                            <span
                              v-if="
                                session.workflowDueStatus &&
                                session.workflowDueStatus !== 'none'
                              "
                              class="rounded-full border border-slate-200 px-2 py-1"
                            >
                              {{ recommendationAlertDueStatusLabel(session) }}
                            </span>
                            <span
                              v-if="session.workflowNeedsFollowup"
                              class="rounded-full border border-slate-200 px-2 py-1"
                            >
                              Needs follow-up
                            </span>
                            <span
                              v-if="session.isSuppressed"
                              class="rounded-full border border-slate-200 px-2 py-1"
                            >
                              Suppressed
                            </span>
                          </div>
                          <div class="mt-2 text-sm font-semibold text-slate-900">
                            {{ session.title }}
                          </div>
                          <div
                            v-if="session.workflowSummaryLabel"
                            class="mt-1 text-xs text-slate-500"
                          >
                            {{ session.workflowSummaryLabel }}
                          </div>
                          <div
                            v-if="session.workflowOwnerLabel"
                            class="mt-2 text-xs text-slate-500"
                          >
                            Owner: {{ session.workflowOwnerLabel }}
                          </div>
                          <div
                            v-if="session.workflowDueAt"
                            class="mt-1 text-xs text-slate-500"
                          >
                            Due: {{ formatRecommendationDate(session.workflowDueAt) }}
                          </div>
                          <div
                            v-if="session.workflowResolutionOutcome"
                            class="mt-1 text-xs text-slate-500"
                          >
                            Resolution outcome: {{ session.workflowResolutionOutcome }}
                          </div>
                          <div
                            v-if="session.workflowHandoffReason"
                            class="mt-1 text-xs text-slate-500"
                          >
                            Handoff reason: {{ session.workflowHandoffReason }}
                          </div>
                          <div
                            v-if="session.workflowNextCheckpoint"
                            class="mt-1 text-xs text-slate-500"
                          >
                            Next checkpoint: {{ session.workflowNextCheckpoint }}
                          </div>
                          <div
                            v-if="session.workflowResultLabel"
                            class="mt-1 text-xs text-slate-500"
                          >
                            {{ session.workflowResultLabel }}
                          </div>
                          <div
                            v-if="session.workflowLastEventLabel"
                            class="mt-1 text-xs text-slate-500"
                          >
                            Last activity:
                            {{ session.workflowLastEventLabel }}
                            <span v-if="session.workflowLastActorName">
                              · {{ session.workflowLastActorName }}
                            </span>
                            <span v-if="session.workflowLastEventAt">
                              · {{ formatRecommendationDate(session.workflowLastEventAt) }}
                            </span>
                          </div>
                          <div
                            v-if="session.workflowNote"
                            class="mt-1 text-xs text-slate-500"
                          >
                            {{ session.workflowNote }}
                          </div>
                          <div class="mt-3 flex flex-wrap gap-2">
                            <button
                              type="button"
                              class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                              @click="applyRecommendationAlert(session)"
                            >
                              Open session
                            </button>
                            <button
                              type="button"
                              class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                              @click="openRecommendationWorkflowAudit(session)"
                            >
                              Audit trail
                            </button>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="!recommendationFilteredAlertWorkflowLedger.length"
                        class="mt-3 rounded-large border border-dashed border-slate-200 bg-white px-3 py-4 text-sm text-slate-500"
                      >
                        No workflow sessions match the current ledger filters.
                      </div>
                    </div>
                    <div
                      v-if="
                        recommendationVisibleLanePressureAlerts.length ||
                        recommendationVisibleOwnerCapacityAlerts.length
                      "
                      class="mt-3 rounded-large border border-neutral-border bg-base-module px-4 py-4"
                    >
                      <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                        Queue alerts
                      </div>
                      <div class="mt-3 grid gap-2 md:grid-cols-2">
                        <div
                          v-for="alert in recommendationVisibleLanePressureAlerts"
                          :key="`lane-alert-${alert.category}-${alert.id}`"
                          class="rounded-large border px-3 py-3"
                          :class="recommendationAlertClass(alert)"
                        >
                          <div class="text-xs uppercase tracking-[0.08em]">
                            Lane pressure
                          </div>
                          <div class="mt-1 text-sm font-semibold">
                            {{ alert.title }}
                          </div>
                          <div class="mt-1 text-xs uppercase tracking-[0.08em]">
                            {{ recommendationAlertThresholdLabel(alert) }}
                          </div>
                          <div class="mt-1 text-xs uppercase tracking-[0.08em]">
                            {{ recommendationAlertCapacityBandLabel(alert) }}
                          </div>
                          <div
                            v-if="alert.workflowSessionState && alert.workflowSessionState !== 'none'"
                            class="mt-1 text-xs uppercase tracking-[0.08em]"
                          >
                            {{ recommendationAlertWorkflowStateLabel(alert) }}
                          </div>
                          <div class="mt-1 text-sm">
                            {{ alert.message }}
                          </div>
                          <button
                            v-if="alert.recommendedActionLabel"
                            type="button"
                            class="mt-3 rounded-full border border-current px-3 py-1 text-xs"
                            @click="applyRecommendationAlert(alert)"
                          >
                            {{ alert.recommendedActionLabel }}
                          </button>
                        </div>
                        <div
                          v-for="alert in recommendationVisibleOwnerCapacityAlerts"
                          :key="`owner-alert-${alert.category}-${alert.id}`"
                          class="rounded-large border px-3 py-3"
                          :class="recommendationAlertClass(alert)"
                        >
                          <div class="text-xs uppercase tracking-[0.08em]">
                            Owner capacity
                          </div>
                          <div class="mt-1 text-sm font-semibold">
                            {{ alert.title }}
                          </div>
                          <div class="mt-1 text-xs uppercase tracking-[0.08em]">
                            {{ recommendationAlertThresholdLabel(alert) }}
                          </div>
                          <div class="mt-1 text-xs uppercase tracking-[0.08em]">
                            {{ recommendationAlertCapacityBandLabel(alert) }}
                          </div>
                          <div
                            v-if="alert.workflowSessionState && alert.workflowSessionState !== 'none'"
                            class="mt-1 text-xs uppercase tracking-[0.08em]"
                          >
                            {{ recommendationAlertWorkflowStateLabel(alert) }}
                          </div>
                          <div class="mt-1 text-sm">
                            {{ alert.message }}
                          </div>
                          <button
                            v-if="alert.recommendedActionLabel"
                            type="button"
                            class="mt-3 rounded-full border border-current px-3 py-1 text-xs"
                            @click="applyRecommendationAlert(alert)"
                          >
                            {{ alert.recommendedActionLabel }}
                          </button>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="recommendationSnapshotHistoryPoints.length"
                      class="mt-3 rounded-large border border-neutral-border bg-base-module px-4 py-4"
                    >
                      <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                        Snapshot history
                      </div>
                      <div class="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                        <div
                          v-for="point in recommendationSnapshotHistoryPoints"
                          :key="point.capturedAt"
                          class="rounded-large border border-slate-200 bg-white px-3 py-3 text-sm text-slate-600"
                        >
                          <div class="font-medium text-slate-900">
                            {{ formatRecommendationDate(point.capturedAt) || point.capturedAt }}
                          </div>
                          <div class="mt-1 text-xs uppercase tracking-[0.08em] text-slate-400">
                            {{ recommendationSnapshotModeLabel(point.captureMode) }}
                          </div>
                          <div class="mt-2">Clusters: {{ point.totalClusters }}</div>
                          <div>Write now: {{ point.writeNowCount }}</div>
                          <div>Active now: {{ point.activeNowCount }}</div>
                          <div>Strong families: {{ point.strongFamilyCount }}</div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="recommendationPresetTrendCards.length"
                      class="mt-3 rounded-large border border-neutral-border bg-base-module px-4 py-4"
                    >
                      <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                        Preset lane trends
                      </div>
                      <div class="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                        <div
                          v-for="card in recommendationPresetTrendCards"
                          :key="`preset-trend-${card.id}`"
                          class="rounded-large border border-slate-200 bg-white px-3 py-3"
                        >
                          <div class="text-sm font-medium text-slate-900">
                            {{ card.label }}
                          </div>
                          <div class="mt-1 text-sm text-slate-600">
                            {{ recommendationSnapshotTrendSummary(card) }}
                          </div>
                          <div class="mt-3 flex items-end gap-2">
                            <div
                              v-for="point in card.history"
                              :key="`${card.id}-${point.capturedAt}`"
                              class="flex flex-col items-center gap-1"
                            >
                              <div
                                class="rounded bg-slate-200"
                                :title="`${formatRecommendationDate(point.capturedAt) || point.capturedAt}: ${point.count}`"
                                :style="{
                                  width: recommendationSnapshotTrendBucketWidth(card, point.count),
                                  height: recommendationSnapshotTrendBucketHeight(card, point.count),
                                }"
                              ></div>
                              <div class="text-[10px] text-slate-400">
                                {{ formatRecommendationDate(point.capturedAt) }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="recommendationReadinessTrendCards.length"
                      class="mt-3 rounded-large border border-neutral-border bg-base-module px-4 py-4"
                    >
                      <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                        Readiness trends
                      </div>
                      <div class="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                        <div
                          v-for="card in recommendationReadinessTrendCards"
                          :key="`readiness-trend-${card.id}`"
                          class="rounded-large border border-slate-200 bg-white px-3 py-3"
                        >
                          <div class="text-sm font-medium text-slate-900">
                            {{ card.label }}
                          </div>
                          <div class="mt-1 text-sm text-slate-600">
                            {{ recommendationSnapshotTrendSummary(card) }}
                          </div>
                          <div class="mt-3 flex items-end gap-2">
                            <div
                              v-for="point in card.history"
                              :key="`${card.id}-${point.capturedAt}`"
                              class="flex flex-col items-center gap-1"
                            >
                              <div
                                class="rounded bg-amber-200"
                                :title="`${formatRecommendationDate(point.capturedAt) || point.capturedAt}: ${point.count}`"
                                :style="{
                                  width: recommendationSnapshotTrendBucketWidth(card, point.count),
                                  height: recommendationSnapshotTrendBucketHeight(card, point.count),
                                }"
                              ></div>
                              <div class="text-[10px] text-slate-400">
                                {{ formatRecommendationDate(point.capturedAt) }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="recommendationOwnerTrendCards.length"
                      class="mt-3 rounded-large border border-neutral-border bg-base-module px-4 py-4"
                    >
                      <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                        Owner workload trends
                      </div>
                      <div class="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                        <div
                          v-for="card in recommendationOwnerTrendCards"
                          :key="`owner-trend-${card.id}`"
                          class="rounded-large border border-slate-200 bg-white px-3 py-3"
                        >
                          <div class="text-sm font-medium text-slate-900">
                            {{ card.label }}
                          </div>
                          <div class="mt-1 text-sm text-slate-600">
                            {{ recommendationSnapshotTrendSummary(card) }}
                          </div>
                          <div class="mt-3 flex items-end gap-2">
                            <div
                              v-for="point in card.history"
                              :key="`${card.id}-${point.capturedAt}`"
                              class="flex flex-col items-center gap-1"
                            >
                              <div
                                class="rounded bg-emerald-200"
                                :title="`${formatRecommendationDate(point.capturedAt) || point.capturedAt}: ${point.count}`"
                                :style="{
                                  width: recommendationSnapshotTrendBucketWidth(card, point.count),
                                  height: recommendationSnapshotTrendBucketHeight(card, point.count),
                                }"
                              ></div>
                              <div class="text-[10px] text-slate-400">
                                {{ formatRecommendationDate(point.capturedAt) }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                      <div
                        v-for="card in recommendationSummaryCards"
                        :key="card.key"
                        class="rounded-large border bg-base-module px-3 py-3 transition"
                        :class="
                          card.presetId && recommendationPresetFilter === card.presetId
                            ? 'border-slate-900 ring-1 ring-slate-900'
                            : 'border-neutral-border'
                        "
                      >
                        <button
                          type="button"
                          class="block w-full text-left"
                          :disabled="!card.presetId"
                          @click="card.presetId ? applyRecommendationPreset(card.presetId) : null"
                        >
                          <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                            {{ card.label }}
                          </div>
                          <div class="mt-1 text-lg font-semibold text-slate-900">
                            {{ card.value }}
                          </div>
                          <div
                            v-if="card.presetId"
                            class="mt-2 text-xs text-slate-500"
                          >
                            Open lane
                          </div>
                        </button>
                      </div>
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <button
                        v-for="preset in recommendationPresetOptions"
                        :key="`preset-${preset.id}`"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="
                          recommendationPresetFilter === preset.id
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        "
                        @click="applyRecommendationPreset(preset.id)"
                      >
                        {{ preset.label }}
                        <span v-if="typeof preset.count === 'number'">({{ preset.count }})</span>
                      </button>
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <button
                        v-for="filter in recommendationPriorityFilters"
                        :key="`priority-${filter.id}`"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="
                          recommendationPriorityFilter === filter.id
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        "
                        @click="recommendationPriorityFilter = filter.id"
                      >
                        {{ filter.label }}
                      </button>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        v-for="option in recommendationSortOptions"
                        :key="`sort-${option.id}`"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="
                          recommendationSort === option.id
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        "
                        @click="recommendationSort = option.id"
                      >
                        Sort: {{ option.label }}
                      </button>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        v-for="filter in recommendationWorkTypeFilters"
                        :key="`work-${filter.id}`"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="
                          recommendationWorkTypeFilter === filter.id
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        "
                        @click="recommendationWorkTypeFilter = filter.id"
                      >
                        {{ filter.label }}
                      </button>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        v-for="filter in recommendationDraftStateFilters"
                        :key="`draft-${filter.id}`"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="
                          recommendationDraftStateFilter === filter.id
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        "
                        @click="recommendationDraftStateFilter = filter.id"
                      >
                        {{ filter.label }}
                      </button>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        v-for="filter in recommendationFamilyFilters"
                        :key="`family-filter-${filter.id}`"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="
                          recommendationFamilyFilter === filter.id
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        "
                        @click="recommendationFamilyFilter = filter.id"
                      >
                        {{ filter.label }}
                        <span v-if="typeof filter.count === 'number'">({{ filter.count }})</span>
                      </button>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        v-for="filter in recommendationFamilyConfidenceFilters"
                        :key="`family-confidence-filter-${filter.id}`"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="
                          recommendationFamilyConfidenceFilter === filter.id
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        "
                        @click="recommendationFamilyConfidenceFilter = filter.id"
                      >
                        {{ filter.label }}
                        <span v-if="typeof filter.count === 'number'">({{ filter.count }})</span>
                      </button>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        v-for="filter in recommendationReadinessFilters"
                        :key="`readiness-filter-${filter.id}`"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="
                          recommendationReadinessFilter === filter.id
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        "
                        @click="recommendationReadinessFilter = filter.id"
                      >
                        {{ filter.label }}
                        <span v-if="typeof filter.count === 'number'">({{ filter.count }})</span>
                      </button>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        v-for="filter in recommendationRoutingFilters"
                        :key="`routing-filter-${filter.id}`"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="
                          recommendationRoutingFilter === filter.id
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        "
                        @click="recommendationRoutingFilter = filter.id"
                      >
                        {{ filter.label }}
                        <span v-if="typeof filter.count === 'number'">({{ filter.count }})</span>
                      </button>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        v-for="filter in recommendationTrendFilters"
                        :key="`trend-filter-${filter.id}`"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="
                          recommendationTrendFilter === filter.id
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        "
                        @click="recommendationTrendFilter = filter.id"
                      >
                        {{ filter.label }}
                        <span v-if="typeof filter.count === 'number'">({{ filter.count }})</span>
                      </button>
                    </div>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <button
                        v-for="filter in recommendationFreshnessFilters"
                        :key="`freshness-filter-${filter.id}`"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs"
                        :class="
                          recommendationFreshnessFilter === filter.id
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-600'
                        "
                        @click="recommendationFreshnessFilter = filter.id"
                      >
                        {{ filter.label }}
                        <span v-if="typeof filter.count === 'number'">({{ filter.count }})</span>
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  v-if="isLoadingRecommendations"
                  class="mt-4 rounded-large border border-neutral-border bg-base-module px-5 py-5 text-sm text-muted"
                >
                  Loading recommendations...
                </div>

                <div
                  v-else-if="!gapRecommendations.length"
                  class="mt-4 rounded-large border border-neutral-border bg-base-module px-5 py-5"
                >
                  <div class="font-semibold">No open content-gap recommendations</div>
                  <div class="text-sm text-muted mt-1">
                    New runtime gaps will appear here after the assistant misses a
                    topic and the backlog recorder captures it.
                  </div>
                </div>

                <div
                  v-if="
                    recommendationFamilyCards.length ||
                    recommendationFamilyConfidenceCards.length ||
                    recommendationReadinessCards.length ||
                    recommendationTrendCards.length ||
                    recommendationFreshnessCards.length ||
                    recommendationRoutingCards.length
                  "
                  class="mt-4 grid gap-3 xl:grid-cols-2"
                >
                  <div
                    v-if="recommendationFamilyCards.length"
                    class="rounded-large border border-neutral-border bg-base-module px-4 py-4"
                  >
                    <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                      Family coverage
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <div
                        v-for="card in recommendationFamilyCards.slice(0, 4)"
                        :key="`family-${card.id}`"
                        class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                      >
                        {{ card.label }} · {{ card.count }} ·
                        {{ recommendationMetaCardTrendLabel(card) }}
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="recommendationFamilyConfidenceCards.length"
                    class="rounded-large border border-neutral-border bg-base-module px-4 py-4"
                  >
                    <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                      Family confidence
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <div
                        v-for="card in recommendationFamilyConfidenceCards.slice(0, 3)"
                        :key="`family-confidence-${card.id}`"
                        class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                      >
                        {{ card.label }} · {{ card.count }} ·
                        {{ recommendationMetaCardTrendLabel(card) }}
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="recommendationReadinessCards.length"
                    class="rounded-large border border-neutral-border bg-base-module px-4 py-4"
                  >
                    <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                      Readiness mix
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <div
                        v-for="card in recommendationReadinessCards.slice(0, 4)"
                        :key="`readiness-${card.id}`"
                        class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                      >
                        {{ card.label }} · {{ card.count }} ·
                        {{ recommendationMetaCardTrendLabel(card) }}
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="recommendationRoutingCards.length"
                    class="rounded-large border border-neutral-border bg-base-module px-4 py-4"
                  >
                    <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                      Routing mix
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <div
                        v-for="card in recommendationRoutingCards.slice(0, 4)"
                        :key="`routing-${card.id}`"
                        class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                      >
                        {{ card.label }} · {{ card.count }} ·
                        {{ recommendationMetaCardTrendLabel(card) }}
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="recommendationTrendCards.length"
                    class="rounded-large border border-neutral-border bg-base-module px-4 py-4"
                  >
                    <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                      Trend mix
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <div
                        v-for="card in recommendationTrendCards.slice(0, 5)"
                        :key="`trend-${card.id}`"
                        class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                      >
                        {{ card.label }} · {{ card.count }} ·
                        {{ recommendationMetaCardTrendLabel(card) }}
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="recommendationFreshnessCards.length"
                    class="rounded-large border border-neutral-border bg-base-module px-4 py-4"
                  >
                    <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                      Freshness mix
                    </div>
                    <div class="mt-3 flex flex-wrap gap-2">
                      <div
                        v-for="card in recommendationFreshnessCards.slice(0, 4)"
                        :key="`freshness-${card.id}`"
                        class="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600"
                      >
                        {{ card.label }} · {{ card.count }} ·
                        {{ recommendationMetaCardTrendLabel(card) }}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-4 grid gap-4">
                  <div
                    v-for="section in recommendationLaneSections"
                    :key="`lane-${section.id}`"
                    class="rounded-large border border-neutral-border bg-base-module px-5 py-4"
                  >
                    <div class="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div class="flex flex-wrap items-center gap-2">
                          <div class="font-semibold text-slate-900">
                            {{ section.label }}
                          </div>
                          <span class="rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600">
                            {{ section.count }} clusters
                          </span>
                          <span class="rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600">
                            {{ recommendationTrendDirectionLabel(section.trendDirection) }}
                          </span>
                        </div>
                        <div class="mt-1 text-sm text-slate-500">
                          {{ recommendationMetaCardTrendLabel(section) }} ·
                          {{ section.totalHits }} total hits in this lane
                        </div>
                      </div>
                      <div
                        v-if="Array.isArray(section.weeklyBuckets) && section.weeklyBuckets.length"
                        class="flex min-w-[220px] items-end gap-1"
                      >
                        <div
                          v-for="bucket in section.weeklyBuckets"
                          :key="`${section.id}-${bucket.label}`"
                          class="flex flex-1 flex-col items-center gap-1"
                        >
                          <div class="h-10 w-full rounded bg-slate-100">
                            <div
                              class="h-full rounded bg-slate-300"
                              :style="{ width: recommendationMetaCardBucketWidth(section, bucket.count) }"
                            />
                          </div>
                          <div class="text-[10px] text-slate-400">{{ bucket.label }}</div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-if="!section.rows.length"
                      class="mt-4 rounded-large border border-dashed border-slate-200 px-4 py-4 text-sm text-slate-500"
                    >
                      No visible recommendations in this lane for the current slice.
                    </div>

                    <div v-else class="mt-4 grid gap-3">
                      <div
                        v-for="row in section.rows"
                        :key="`${section.id}-${row.cluster_key || row.topic}-${row.reason}`"
                        class="rounded-large border border-slate-200 bg-white px-5 py-4"
                      >
                        <div class="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                          <div class="min-w-0">
                            <div class="flex flex-wrap items-center gap-2">
                              <div class="font-semibold text-slate-900">
                                {{ row.topic || row.query_preview || 'Unknown topic' }}
                              </div>
                              <span
                                v-if="recommendationPriorityLabel(row)"
                                class="rounded-full border px-2 py-0.5 text-xs font-medium"
                                :class="recommendationPriorityClass(row)"
                              >
                                {{ recommendationPriorityLabel(row) }}
                              </span>
                            </div>
                            <div class="mt-1 flex items-center gap-2 flex-wrap text-sm text-slate-500">
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                {{ humanizeContentGapReason(row.reason) }}
                              </span>
                              <span>{{ row.total_count }} hits</span>
                              <span v-if="row.recent_7d_count || row.previous_7d_count">
                                {{ contentGapTrendLabel(row) }}
                              </span>
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                {{ recommendationTrendDirectionLabel(row.trend_direction) }}
                              </span>
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                {{ recommendationFreshnessLabel(row) }}
                              </span>
                              <span v-if="recommendationPersistenceLabel(row)">
                                {{ recommendationPersistenceLabel(row) }}
                              </span>
                              <span v-if="row.created_article_count">
                                {{ row.created_article_count }} draft linked
                              </span>
                              <span v-if="row.query_preview">latest: {{ row.query_preview }}</span>
                            </div>
                            <div
                              v-if="Array.isArray(row.sample_queries) && row.sample_queries.length"
                              class="mt-2 text-sm text-slate-500"
                            >
                              Covers: {{ row.sample_queries.join(' • ') }}
                            </div>
                            <div class="mt-2 text-sm text-slate-500">
                              Variants: {{ row.query_variant_count || row.sample_queries?.length || 0 }}
                              <span v-if="row.draft_state === 'draft_linked'">· Draft linked</span>
                              <span v-else-if="row.draft_state === 'published_linked'">· Published linked</span>
                              <span v-else>· No draft yet</span>
                              <span>· This week {{ row.current_week_count || 0 }}</span>
                              <span>· Last seen {{ row.days_since_last_seen || 0 }}d ago</span>
                            </div>
                            <div class="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                Work: {{ humanizeRecommendationWorkType(row) }}
                              </span>
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                Stage: {{ humanizeRecommendationDeliveryStage(row) }}
                              </span>
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                Family: {{ humanizeRecommendationFamily(row) }}
                              </span>
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                {{ recommendationFamilyConfidenceLabel(row) }}
                              </span>
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                Readiness: {{ recommendationReadinessLabel(row) }}
                              </span>
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                Backlog: {{ recommendationBacklogStatusLabel(row) }}
                              </span>
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                Route: {{ recommendationRoutingTargetLabel(row) }}
                              </span>
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                Effort: {{ recommendationDeliveryEffortLabel(row) }}
                              </span>
                              <span class="rounded-full border border-slate-200 px-2 py-0.5">
                                Urgency: {{ recommendationUrgencyLabel(row) }}
                              </span>
                              <span v-if="recommendationOwnerHint(row)">
                                Owner: {{ recommendationOwnerHint(row) }}
                              </span>
                            </div>
                            <div
                              v-if="recommendationNextAction(row)"
                              class="mt-2 text-sm text-slate-600"
                            >
                              Next step: {{ recommendationNextAction(row) }}
                            </div>
                            <div
                              v-if="recommendationPriorityRationale(row)"
                              class="mt-1 text-sm text-slate-500"
                            >
                              Why now: {{ recommendationPriorityRationale(row) }}
                            </div>
                            <div
                              v-if="row.family_signal_summary || row.routing_reason"
                              class="mt-1 text-sm text-slate-500"
                            >
                              Family signals:
                              {{ row.family_signal_summary || 'general fallback' }}.
                              <span v-if="row.routing_reason">
                                Route note: {{ row.routing_reason }}
                              </span>
                            </div>
                            <div
                              v-if="recommendationWeeklyBuckets(row).length"
                              class="mt-3 flex flex-col gap-2"
                            >
                              <div class="text-xs uppercase tracking-[0.08em] text-slate-400">
                                {{ recommendationTrendTitle(row) }}
                              </div>
                              <div class="flex flex-col gap-1">
                                <div
                                  v-for="bucket in recommendationWeeklyBuckets(row)"
                                  :key="`${row.cluster_key || row.topic}-${bucket.label}`"
                                  class="flex items-center gap-2 text-xs text-slate-500"
                                >
                                  <span class="w-10 shrink-0">{{ bucket.label }}</span>
                                  <div class="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
                                    <div
                                      class="h-full rounded-full bg-slate-400"
                                      :style="{ width: contentGapWeeklyBucketWidth(row, bucket.count) }"
                                    />
                                  </div>
                                  <span class="w-5 shrink-0 text-right">{{ bucket.count }}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div class="flex items-center gap-2">
                            <button
                              v-if="recommendationSampleQuery(row)"
                              type="button"
                              class="btn o__secondary"
                              @click="openRecommendationInTest(row)"
                            >
                              Retest sample
                            </button>
                            <button
                              type="button"
                              class="btn o__secondary"
                              :disabled="creatingGapDraftTagId === row.sample_tag_id || row.created_article_count > 0"
                              @click="createGapArticleDraft(row)"
                            >
                              {{
                                row.created_article_count > 0
                                  ? 'Draft already created'
                                  : creatingGapDraftTagId === row.sample_tag_id
                                  ? 'Creating draft...'
                                  : 'Create draft article'
                              }}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
              </details>

              <div class="mt-8">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <h3 class="text-h3 font-semibold mb-2">Product basics</h3>
                    <p class="text-sm text-muted">
                      Keep AI-facing product records in this workspace instead of
                      routing through the old product shell.
                    </p>
                  </div>
                  <button type="button" class="btn o__secondary" @click="openCreateProductDialog">
                    <span class="i-lucide-plus size-4" />
                    Add product
                  </button>
                </div>

                <div
                  v-if="!productCount"
                  class="mt-4 rounded-large border border-neutral-border bg-base-module px-5 py-5"
                >
                  <div class="font-semibold">No products yet</div>
                  <div class="text-sm text-muted mt-1">
                    Create a product record so AI can use the right basics for
                    product-backed answers.
                  </div>
                </div>

                <div v-else class="mt-4 grid gap-3">
                  <button
                    v-for="product in products"
                    :key="product.id"
                    type="button"
                    class="content-product-card"
                    @click="openProductSheet(product)"
                  >
                    <div class="min-w-0">
                      <div class="flex items-center gap-2 flex-wrap">
                        <span class="font-semibold text-slate-900">
                          {{ getProductName(product) }}
                        </span>
                        <span
                          v-if="getProductSeries(product)"
                          class="rounded-full border border-slate-200 px-2 py-0.5 text-xs text-slate-600"
                        >
                          {{ getProductSeries(product) }}
                        </span>
                      </div>
                      <div class="mt-1 text-sm text-slate-500 line-clamp-2">
                        {{ getProductSummary(product) }}
                      </div>
                    </div>
                    <span class="fin-link text-sm shrink-0">Edit basics</span>
                  </button>
                </div>
              </div>

            </div><!-- /content-setup-v2 -->
          </div>
        </div>
      </div>
    </div><!-- /main column -->

    <!-- ── Right Preview panel ─────────────────────────────────────────── -->
    <div
      id="unified-messenger-preview-layout"
      class="content-page-preview flex flex-col items-center justify-center bg-base-module shadow-lvl-0 rounded-module max-h-screen pt-4"
    >
      <!-- Preview header -->
      <div class="w-full flex items-center pb-4 px-6 flex-none">
        <div>
          <h1 class="text-h1 font-semibold">Preview</h1>
        </div>
        <div class="flex flex-grow justify-end">
          <div class="flex gap-2 items-center">
            <label class="content-page-preview__view-select">
              <select
                :value="activePreviewTab"
                aria-label="Preview mode"
                @change="selectPreviewTab($event.target.value)"
              >
                <option value="customer">Customer view</option>
                <option value="event_log">Event log</option>
              </select>
              <svg class="interface-icon o__standard" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.79491 10.1807C7.18432 10.6065 7.81568 10.6064 8.20509 10.1807L11.2942 6.80291C11.7339 6.32211 11.4225 5.5 10.8006 5.5L4.1994 5.5C3.57755 5.5 3.26612 6.32211 3.70584 6.80291L6.79491 10.1807Z" />
              </svg>
            </label>
            <button
              type="button"
              class="btn o__secondary o__icon"
              aria-label="Settings"
              @click="onPreviewSettingsClick"
            >
              <svg class="interface-icon o__standard" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M14.9 8.49001L14.11 8.06C13.24 7.58 12.98 6.45001 13.56 5.63L14.08 4.89999C14.21 4.70999 14.21 4.46 14.06 4.28L13.15 3.14C13.01 2.96 12.76 2.9 12.55 2.98L11.72 3.32001C10.8 3.70001 9.74997 3.20001 9.47997 2.24001L9.22997 1.38C9.16997 1.16 8.95997 1.00999 8.72997 1.00999H7.26997C7.03997 1.00999 6.83997 1.16 6.76997 1.38L6.51997 2.24001C6.23997 3.20001 5.19997 3.70001 4.27997 3.32001L3.44997 2.98C3.23997 2.89 2.98997 2.95 2.84997 3.14L1.93997 4.28C1.79997 4.46 1.78997 4.70999 1.91997 4.89999L2.43997 5.63C3.01997 6.44 2.75997 7.58 1.88997 8.06L1.09997 8.49001C0.89997 8.60001 0.78997 8.83 0.84997 9.06L1.17997 10.49C1.22997 10.71 1.42997 10.88 1.65997 10.89L2.55997 10.94C3.54997 11 4.27997 11.9 4.10997 12.89L3.95997 13.78C3.91997 14.01 4.03997 14.23 4.24997 14.33L5.56997 14.96C5.77997 15.06 6.02997 15.01 6.17997 14.84L6.77997 14.17C7.43997 13.43 8.60997 13.43 9.26997 14.17L9.86997 14.84C10.02 15.01 10.27 15.06 10.48 14.96L11.8 14.33C12.01 14.23 12.12 14 12.09 13.78L11.94 12.89C11.77 11.91 12.5 11 13.49 10.94L14.39 10.89C14.62 10.88 14.81 10.71 14.87 10.49L15.2 9.06C15.25 8.84 15.15 8.60001 14.95 8.49001H14.9ZM7.99997 10.85C6.51997 10.85 5.31997 9.65 5.31997 8.17C5.31997 6.69 6.51997 5.49001 7.99997 5.49001C9.47997 5.49001 10.68 6.69 10.68 8.17C10.68 9.65 9.47997 10.85 7.99997 10.85Z" />
              </svg>
            </button>
            <button
              type="button"
              class="btn o__secondary o__icon"
              aria-label="Close preview"
              @click="onPreviewCloseClick"
            >
              <svg class="interface-icon o__standard" width="16" height="16" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.25 3.95L12.05 2.75L8 6.8L3.95 2.75L2.75 3.95L6.8 8L2.75 12.05L3.95 13.25L8 9.2L12.05 13.25L13.25 12.05L9.2 8L13.25 3.95Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Main preview body -->
      <div class="w-full flex flex-col flex-1 min-h-0 border-t border-neutral-border">
        <div
          v-if="activePreviewTab === 'customer'"
          class="content-page-preview__customer"
        >
          <div class="content-page-preview__device">
            <div class="content-page-preview__device-header">
              <button type="button" class="content-page-preview__device-icon">
                <span class="i-lucide-chevron-left size-4" />
              </button>
              <div class="content-page-preview__device-agent">
                <div class="content-page-preview__device-avatar">
                  {{ previewAssistantInitial }}
                </div>
                <div class="content-page-preview__device-agent-copy">
                  <div class="content-page-preview__device-agent-name">
                    {{ previewAssistantName }}
                  </div>
                  <div class="content-page-preview__device-agent-meta">
                    <span class="content-page-preview__status-dot" />
                    {{ previewStatusLine }}
                  </div>
                </div>
              </div>
              <div class="content-page-preview__device-actions">
                <button type="button" class="content-page-preview__device-icon">
                  <span class="i-lucide-minus size-4" />
                </button>
                <button
                  type="button"
                  class="content-page-preview__device-icon"
                  @click="onPreviewCloseClick"
                >
                  <span class="i-lucide-x size-4" />
                </button>
              </div>
            </div>

            <div class="content-page-preview__device-body">
              <div class="content-page-preview__preview-stage">
                <div class="content-page-preview__thread">
                  <div class="content-page-preview__message-row is-assistant">
                    <div class="content-page-preview__message-avatar">
                      {{ previewAssistantInitial }}
                    </div>
                    <div
                      class="content-page-preview__bubble content-page-preview__bubble--assistant"
                    >
                      {{ previewHeroMessage }}
                    </div>
                  </div>

                  <div class="content-page-preview__quick-actions">
                    <button
                      type="button"
                      class="content-page-preview__quick-action"
                      @click="onOpenQuickPreview('Preview CTA')"
                    >
                      Chat now
                    </button>
                    <button
                      type="button"
                      class="content-page-preview__quick-action"
                      @click="selectPreviewTab('event_log')"
                    >
                      Just browsing
                    </button>
                  </div>

                  <div class="content-page-preview__day-divider">
                    <span>Today</span>
                  </div>

                  <div class="content-page-preview__message-row is-customer">
                    <div
                      class="content-page-preview__bubble content-page-preview__bubble--customer"
                    >
                      {{ previewCustomerQuestion }}
                    </div>
                  </div>

                  <div class="content-page-preview__message-row is-assistant">
                    <div class="content-page-preview__message-avatar is-muted">
                      {{ previewAssistantInitial }}
                    </div>
                    <div class="content-page-preview__message-stack">
                      <div
                        class="content-page-preview__bubble content-page-preview__bubble--assistant content-page-preview__bubble--secondary"
                      >
                        {{ previewAssistantFollowup }}
                      </div>
                      <div class="content-page-preview__announcement">
                        {{ runtimeOwnershipNote }}
                      </div>
                    </div>
                  </div>

                  <div class="content-page-preview__typing-row">
                    <div class="content-page-preview__message-avatar is-muted">
                      {{ previewAssistantInitial }}
                    </div>
                    <div class="content-page-preview__typing-bubble">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="content-page-preview__composer">
              <button type="button" class="content-page-preview__composer-icon">
                <span class="i-lucide-paperclip size-4" />
              </button>
              <div class="content-page-preview__composer-placeholder">
                Type your message
              </div>
              <div class="content-page-preview__composer-trailing">
                <span class="i-lucide-smile size-4" />
                <button type="button" class="content-page-preview__composer-send">
                  <span class="i-lucide-send-horizontal size-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="content-page-preview__event-log">
          <div
            v-for="item in previewEvents"
            :key="item.id"
            class="content-page-preview__event-log-item"
            :class="`is-${item.type}`"
          >
            <div class="content-page-preview__event-log-time">{{ item.time }}</div>
            <div class="content-page-preview__event-log-title">{{ item.title }}</div>
            <div class="content-page-preview__event-log-detail">{{ item.detail }}</div>
          </div>
          <div v-if="!previewEvents.length" class="content-page-preview__event-log-empty">
            No preview events yet.
          </div>
        </div>
        <span class="u__absolute"></span>
      </div>
    </div><!-- /preview panel -->

  </div>

  <teleport to="body">
    <div
      v-if="isProductDialogOpen"
      class="content-modal-overlay"
      @click.self="closeCreateProductDialog"
    >
      <div class="content-modal">
        <div class="content-modal__header">
          <div>
            <div class="text-base font-semibold text-slate-900">Add product</div>
            <div class="text-sm text-slate-500 mt-1">
              Create a product record directly from Train &gt; Content.
            </div>
          </div>
          <button
            type="button"
            class="content-modal__close"
            :disabled="isCreatingProduct"
            @click="closeCreateProductDialog"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="content-modal__body">
          <label class="content-modal__field">
            <span class="content-modal__label">Product name</span>
            <input v-model="newProductDraft.name" type="text" class="content-modal__input" />
          </label>
          <label class="content-modal__field">
            <span class="content-modal__label">Series</span>
            <input v-model="newProductDraft.series" type="text" class="content-modal__input" />
          </label>
          <label class="content-modal__field">
            <span class="content-modal__label">Description</span>
            <textarea
              v-model="newProductDraft.description"
              rows="4"
              class="content-modal__textarea"
            />
          </label>
        </div>

        <div class="content-modal__footer">
          <button
            type="button"
            class="btn o__secondary"
            :disabled="isCreatingProduct"
            @click="closeCreateProductDialog"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn o__primary text-white"
            :disabled="isCreatingProduct"
            @click="createProduct"
          >
            {{ isCreatingProduct ? 'Creating...' : 'Create product' }}
          </button>
        </div>
      </div>
    </div>
  </teleport>

  <teleport to="body">
    <div
      v-if="isProductSheetOpen && selectedProduct"
      class="content-sheet-overlay"
      @click.self="closeProductSheet"
    >
      <aside class="content-sheet">
        <div class="content-sheet__header">
          <div>
            <div class="text-base font-semibold text-slate-900">
              {{ getProductName(selectedProduct) }}
            </div>
            <div class="text-sm text-slate-500 mt-1">
              Edit the product basics used by AI runtime and preview.
            </div>
          </div>
          <button type="button" class="content-sheet__close" @click="closeProductSheet">
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="content-sheet__body">
          <ProductInformationTab
            ref="productInformationRef"
            :key="selectedProduct.id"
            :assistant-id="assistantId"
            :product="selectedProduct"
          />
        </div>
      </aside>
    </div>
  </teleport>

  <teleport to="body">
    <div
      v-if="isMediaSheetOpen"
      class="content-sheet-overlay"
      @click.self="closeMediaSheet"
    >
      <aside class="content-sheet content-sheet--wide">
        <div class="content-sheet__header">
          <div>
            <div class="text-base font-semibold text-slate-900">Uploaded documents</div>
            <div class="text-sm text-slate-500 mt-1">
              Manage AI media assets directly inside Train &gt; Content.
            </div>
          </div>
          <button type="button" class="content-sheet__close" @click="closeMediaSheet">
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="content-sheet__body">
          <ProductMediaTab
            ref="productMediaRef"
            :assistant-id="assistantId"
            :product="{}"
          />
        </div>
      </aside>
    </div>
  </teleport>

  <teleport to="body">
    <div
      v-if="isLinksSheetOpen"
      class="content-sheet-overlay"
      @click.self="closeLinksSheet"
    >
      <aside class="content-sheet">
        <div class="content-sheet__header">
          <div>
            <div class="text-base font-semibold text-slate-900">Website sync</div>
            <div class="text-sm text-slate-500 mt-1">
              Manage fixed signup and promo links plus additional synced URLs inside
              Train &gt; Content.
            </div>
          </div>
          <button type="button" class="content-sheet__close" @click="closeLinksSheet">
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="content-sheet__body">
          <ProductLinksTab
            ref="productLinksRef"
            :assistant-id="assistantId"
            :product="{}"
          />
        </div>
      </aside>
    </div>
  </teleport>

  <teleport to="body">
    <div
      v-if="isAnswerTemplatesSheetOpen"
      class="content-sheet-overlay"
      @click.self="closeAnswerTemplatesSheet"
    >
      <aside class="content-sheet content-sheet--wide">
        <div class="content-sheet__header">
          <div>
            <div class="text-base font-semibold text-slate-900">Legacy snippets</div>
            <div class="text-sm text-slate-500 mt-1">
              Maintain compatibility-only answer templates inside Train &gt; Content
              while canonical knowledge continues moving to articles.
            </div>
          </div>
          <button
            type="button"
            class="content-sheet__close"
            @click="closeAnswerTemplatesSheet"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <div class="content-sheet__body">
          <ProductAnswerTemplateTab
            ref="productAnswerTemplatesRef"
            :assistant-id="assistantId"
            :product="{}"
          />
        </div>
      </aside>
    </div>
  </teleport>

  <CreateArticleSheet
    :show="isArticleSheetOpen"
    :article-type="activeArticleType"
    :is-saving="isSavingArticle"
    :saving-action="savingArticleAction"
    :portal-slug="defaultPortalSlug"
    @close="onCloseArticleSheet"
    @save-draft="onSaveArticleDraft"
    @publish="onPublishArticle"
  />
</template>

<style scoped>
/* ── Page shell ──────────────────────────────────────────────────────────── */
.content-page-shell {
  box-sizing: content-box;
  gap: 20px;
}

.content-page-main {
  min-width: 0;
}

.content-page-surface {
  border: 1px solid var(--fin-neutral-border);
  background: #ffffff;
}

.content-page-hero {
  margin-bottom: 4px;
}

.content-page-header-note {
  display: inline-flex;
  align-items: center;
  border-radius: 9999px;
  padding: 6px 10px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
}

.content-page-toolbar-note {
  min-width: 0;
}

.content-page-shell .heading.o__size-1 {
  font-size: 17px;
  line-height: 22px;
}

.content-page-shell .text-h3 {
  font-size: 13px;
  line-height: 18px;
}

.content-page-shell .btn {
  font-size: 13px;
  line-height: 16px;
}

.content-cards__card {
  flex: 1 1 152px;
  min-width: 152px;
  max-width: 176px;
}

.content-cards__card h4 {
  font-size: 13px;
  line-height: 18px;
}

/* ── Search input (/settings/vip exact styles) ───────────────────────────── */
.search-input {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.search-input:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
  background: #ffffff !important;
}
.search-input::placeholder {
  color: #9ca3af !important;
}

/* ── Content cards ───────────────────────────────────────────────────────── */
/* container queries injected inline via <style> in the template (as Fin does) */

/* ── ds-new__table (Fin's content sources table) ─────────────────────────── */
.ds-new__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  color: #111827;
}

.ds-new__table__head .ds-new__table__row th {
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  padding: 8px 12px;
  border-bottom: 1px solid var(--fin-neutral-border);
  white-space: nowrap;
}

.ds-new__table__column-header.o__draggable,
.ds-new__table__column-header.o__checkbox {
  width: 32px;
  padding: 0;
}

.ds-new__table__column__label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.ds-new__table__column__sort {
  display: inline-flex;
  flex-direction: column;
  gap: 1px;
  margin-left: 4px;
}

.ds-new__table__column__sort-icon {
  opacity: 0.4;
}

.ds-new__table__column__sort-icon.o__active-sort-direction {
  opacity: 1;
}

.ds-new__table__body .ds-new__table__row td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--fin-neutral-border);
  vertical-align: middle;
}

.ds-new__table__body .ds-new__table__row:last-child td {
  border-bottom: none;
}

.ds-new__table__cell-shim {
  display: flex;
  align-items: center;
}

.u__one-truncated-line {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.text-muted {
  color: #6b7280;
}

.text-accent:hover {
  color: #2563eb;
}

.text-default {
  color: #111827;
}

.o__palette-text-success {
  color: #16a34a;
}

/* Status dot variants */
.bg-success-fill-emphasis { background: #22c55e; }
.border-success-container  { border-color: #bbf7d0; }

/* gap-module ~ 8px (matching Fin) */
.gap-module { gap: 8px; }

/* ── tab-control ──────────────────────────────────────────────────────────── */
.tab-control__tab {
  height: 36px;
  padding: 0 14px;
  border: 0;
  background: transparent;
  color: #6b7280;
  border-bottom: 2px solid transparent;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.tab-control__tab.o__selected {
  color: #111827;
  border-bottom-color: #111827;
}

.tab-control__tab__contents {
  display: flex;
  align-items: center;
  height: 100%;
}

.tab-control__tab__label {
  line-height: 1;
}

/* ── Right Preview panel ─────────────────────────────────────────────────── */
.content-page-preview {
  width: 440px;
  min-width: 440px;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--fin-neutral-border);
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
}

#unified-messenger-preview-layout {
  width: 440px;
}

.content-page-preview .text-h1,
.content-page-preview h1 {
  font-size: 15px;
  line-height: 20px;
}

.content-page-preview .text,
.content-page-preview strong {
  font-size: 13px;
  line-height: 18px;
}

.content-page-preview__view-select {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.content-page-preview__view-select select {
  appearance: none;
  border: 1px solid var(--fin-neutral-border);
  border-radius: 9999px;
  background: #ffffff;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  padding: 7px 30px 7px 12px;
  cursor: pointer;
}

.content-page-preview__view-select svg {
  position: absolute;
  right: 10px;
  color: #6b7280;
  pointer-events: none;
}

.content-page-preview__customer {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0 0 18px;
}

.content-page-preview__device {
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.content-page-preview__device-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid #eef2f7;
  background: #ffffff;
}

.content-page-preview__device-agent {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.content-page-preview__device-avatar {
  width: 30px;
  height: 30px;
  border-radius: 9999px;
  background: linear-gradient(135deg, #fb923c, #f97316 45%, #f59e0b 100%);
  color: #ffffff;
  font-size: 12px;
  line-height: 30px;
  font-weight: 700;
  text-align: center;
  box-shadow: 0 8px 18px rgba(249, 115, 22, 0.2);
}

.content-page-preview__device-agent-copy {
  min-width: 0;
}

.content-page-preview__device-agent-name {
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  color: #111827;
}

.content-page-preview__device-agent-meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  line-height: 15px;
  color: #6b7280;
}

.content-page-preview__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: #22c55e;
  flex: none;
}

.content-page-preview__device-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.content-page-preview__device-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.content-page-preview__device-icon:hover {
  background: #f3f4f6;
  color: #111827;
}

.content-page-preview__device-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background: #f8fafc;
}

.content-page-preview__preview-stage {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 100%;
  padding: 16px 16px 20px;
}

.content-page-preview__thread {
  display: flex;
  flex-direction: column;
  gap: 11px;
  padding: 4px 0 0;
}

.content-page-preview__message-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.content-page-preview__message-row.is-customer {
  justify-content: flex-end;
}

.content-page-preview__message-avatar {
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  background: linear-gradient(135deg, #fb923c, #f59e0b);
  color: #ffffff;
  font-size: 10px;
  line-height: 24px;
  font-weight: 700;
  text-align: center;
  flex: none;
}

.content-page-preview__message-avatar.is-muted {
  background: linear-gradient(135deg, #93c5fd, #60a5fa);
}

.content-page-preview__message-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  max-width: calc(100% - 32px);
}

.content-page-preview__bubble {
  max-width: 286px;
  border-radius: 18px;
  padding: 10px 13px;
  font-size: 12px;
  line-height: 18px;
  white-space: pre-line;
}

.content-page-preview__bubble--assistant {
  background: #ffffff;
  border: 1px solid #eceff3;
  color: #111827;
  border-top-left-radius: 8px;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
}

.content-page-preview__bubble--secondary {
  background: #ffffff;
  border-color: #eceff3;
}

.content-page-preview__bubble--customer {
  background: #dbeafe;
  color: #1e3a8a;
  border: 1px solid #bfdbfe;
  border-bottom-right-radius: 8px;
  box-shadow: 0 10px 20px rgba(37, 99, 235, 0.12);
}

.content-page-preview__day-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2px 0;
}

.content-page-preview__day-divider span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 20px;
  padding: 0 8px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid #edf2f7;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 600;
}

.content-page-preview__quick-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.content-page-preview__quick-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  padding: 0 13px;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  background: #ffffff;
  color: #111827;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.content-page-preview__quick-action:hover {
  border-color: #cbd5e1;
  background: #fff7ed;
}

.content-page-preview__typing-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.content-page-preview__typing-bubble {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 32px;
  padding: 0 11px;
  border-radius: 15px;
  background: #ffffff;
  border: 1px solid #eceff3;
  border-top-left-radius: 8px;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.04);
}

.content-page-preview__typing-bubble span {
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: #cbd5e1;
}

.content-page-preview__announcement {
  padding: 9px 11px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 11px;
  line-height: 16px;
}

.content-page-preview__composer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px 13px;
  border-top: 1px solid #eef2f7;
  background: #ffffff;
}

.content-page-preview__composer-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
}

.content-page-preview__composer-placeholder {
  flex: 1;
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  padding: 9px 14px;
  font-size: 11px;
  line-height: 16px;
  color: #6b7280;
  background: #ffffff;
}

.content-page-preview__composer-trailing {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #6b7280;
}

.content-page-preview__composer-send {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 9999px;
  background: linear-gradient(180deg, #2563eb, #1d4ed8);
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 8px 16px rgba(37, 99, 235, 0.22);
}

.content-page-preview__event-log {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 24px 16px;
  overflow-y: auto;
}

.content-page-preview__event-log-item {
  border: 1px solid var(--fin-neutral-border);
  border-radius: 10px;
  padding: 10px 12px;
  background: #ffffff;
}

.content-page-preview__event-log-item.is-success {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.content-page-preview__event-log-item.is-error {
  border-color: #fecaca;
  background: #fef2f2;
}

.content-page-preview__event-log-time {
  font-size: 11px;
  line-height: 16px;
  color: #6b7280;
  margin-bottom: 2px;
}

.content-page-preview__event-log-title {
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: #111827;
}

.content-page-preview__event-log-detail {
  font-size: 12px;
  line-height: 18px;
  color: #4b5563;
}

.content-page-preview__event-log-empty {
  font-size: 12px;
  line-height: 18px;
  color: #6b7280;
  text-align: center;
  padding: 24px 0;
}

.content-page-shell h1,
.content-page-shell h2,
.content-page-shell h3,
.content-page-shell h4,
.content-page-shell th,
.content-page-shell td,
.content-page-shell .heading,
.content-page-shell .font-semibold {
  color: #111827;
}

:global(body.dark) .content-page-shell,
:global(body.theme-mix) .content-page-shell,
:global(body.dark) #unified-messenger-preview-layout,
:global(body.theme-mix) #unified-messenger-preview-layout {
  color: #111827 !important;
}

:global(body.dark) .content-page-shell .bg-base-module,
:global(body.theme-mix) .content-page-shell .bg-base-module,
:global(body.dark) #unified-messenger-preview-layout.bg-base-module,
:global(body.theme-mix) #unified-messenger-preview-layout.bg-base-module {
  background: #ffffff !important;
}

:global(body.dark) .content-page-shell :is(h1, h2, h3, h4, .heading, .font-semibold, .text-default, .tab-control__tab__label, .ds-new__table__column__label),
:global(body.theme-mix) .content-page-shell :is(h1, h2, h3, h4, .heading, .font-semibold, .text-default, .tab-control__tab__label, .ds-new__table__column__label),
:global(body.dark) #unified-messenger-preview-layout :is(h1, h2, h3, h4, .tab-control__tab__label, .text, strong),
:global(body.theme-mix) #unified-messenger-preview-layout :is(h1, h2, h3, h4, .tab-control__tab__label, .text, strong) {
  color: #111827 !important;
}

:global(body.dark) .content-page-shell .text-muted,
:global(body.theme-mix) .content-page-shell .text-muted {
  color: #6b7280 !important;
}

.content-product-card {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #ffffff;
  text-align: left;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.content-product-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.content-modal-overlay,
.content-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 70;
  background: rgba(15, 23, 42, 0.32);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.content-modal {
  width: min(560px, calc(100vw - 32px));
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.2);
  overflow: hidden;
}

.content-modal__header,
.content-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
}

.content-modal__header {
  border-bottom: 1px solid #e5e7eb;
}

.content-modal__footer {
  border-top: 1px solid #e5e7eb;
}

.content-modal__body {
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.content-modal__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.content-modal__label {
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: #374151;
}

.content-modal__input,
.content-modal__textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 14px;
  line-height: 20px;
  color: #111827;
  background: #ffffff;
}

.content-modal__textarea {
  resize: vertical;
}

.content-modal__close,
.content-sheet__close {
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  background: #ffffff;
  color: #6b7280;
}

.content-sheet {
  width: min(920px, calc(100vw - 32px));
  max-height: calc(100vh - 40px);
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.content-sheet__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
}

.content-sheet__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px;
  background: #f8fafc;
}

.content-sheet--wide {
  width: min(1180px, calc(100vw - 32px));
}

/* interface-icon base */
.interface-icon {
  display: inline-block;
  flex-shrink: 0;
  fill: currentColor;
}
</style>
